import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import HomePage from './components/HomePage';

const modelViewStateCache = new Map();
let modelViewerElementPromise = null;

function ensureModelViewerElementLoaded() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.customElements?.get('model-viewer')) return Promise.resolve();
  if (!modelViewerElementPromise) {
    modelViewerElementPromise = import('@google/model-viewer').catch((error) => {
      modelViewerElementPromise = null;
      throw error;
    });
  }
  return modelViewerElementPromise;
}

// Handles image load errors by replacing with a placeholder SVG
function handleImgError(e) {
  e.currentTarget.onerror = null;
  e.currentTarget.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><rect width="100%" height="100%" fill="#eee"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="18" fill="#999">Image unavailable</text></svg>';
}

// Resolve media URLs so relative /uploads paths work behind /shopapi proxies
function resolveMediaUrl(u) {
  if (!u) return '';
  let s = String(u).trim();
  // Strip surrounding quotes and normalize slashes
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1).trim();
  }
  s = s.replace(/\\/g, '/');
  // Allow absolute URLs (http/https), data URIs, and blob URLs to pass through
  if (/^https?:\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:')) return s;
  if (s.startsWith('/shopapi/uploads/')) return s;
  if (s.startsWith('/uploads/')) return `/shopapi${s}`;
  if (s.startsWith('uploads/')) return `/shopapi/${s}`;
  // If looks like a filename or relative path, assume it's under uploads
  if (!s.startsWith('/')) return `/shopapi/uploads/${s}`;
  return s;
}

function getMediaKind(src) {
  const s = String(src || '').toLowerCase();
  if (s.match(/\.(mp4|webm|mov|mkv)(\?.*)?$/)) return 'video';
  if (s.match(/\.(glb|gltf|obj|stl|usdz)(\?.*)?$/)) return 'model';
  if (s.match(/\.(jpg|jpeg|png|gif|webp|avif|svg|ico)(\?.*)?$/)) return 'image';
  return 'unknown';
}

function isModelPreviewable(src) {
  const s = String(src || '').toLowerCase();
  return s.match(/\.(glb|gltf|usdz|stl)(\?.*)?$/);
}

const isImageMedia = (src) => getMediaKind(src) === 'image';

function collectProductMediaSources(product) {
  if (!product) return [];
  try {
    const sources = [];
    if (product.img) sources.push(product.img);
    if (product.images) {
      const parsed = Array.isArray(product.images) ? product.images : JSON.parse(product.images || '[]');
      if (Array.isArray(parsed)) sources.push(...parsed);
    }
    return sources
      .map((entry) => String(entry || '').trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

function getPerformanceModeOverride() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = String(window.localStorage.getItem('shopstarter_performance_mode') || '').trim().toLowerCase();
    if (raw === 'lite' || raw === 'full') return raw;
    return null;
  } catch {
    return null;
  }
}

function isLikelyMobileDevice() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  try {
    const ua = String(navigator.userAgent || '').toLowerCase();
    const uaMobile = /android|iphone|ipad|ipod|mobile|iemobile|opera mini/.test(ua);
    const coarsePointer = !!window.matchMedia?.('(pointer: coarse)')?.matches;
    const hasTouch = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 0;
    const screenWidth = window.screen?.width || window.innerWidth || 0;
    const screenHeight = window.screen?.height || window.innerHeight || 0;
    const shortestSide = Math.min(screenWidth, screenHeight);
    const phoneLikeViewport = shortestSide > 0 && shortestSide <= 1024;
    return uaMobile || ((coarsePointer || hasTouch) && phoneLikeViewport);
  } catch {
    return false;
  }
}

function detectPerformanceLiteMode() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  try {
    const override = getPerformanceModeOverride();
    if (override === 'lite') return true;
    if (override === 'full') return false;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const saveData = !!connection?.saveData;
    const effectiveType = String(connection?.effectiveType || '').toLowerCase();
    const weakNetwork = effectiveType.includes('slow-2g') || effectiveType.includes('2g');
    const constrainedNetwork = weakNetwork || effectiveType.includes('3g');
    if (saveData || weakNetwork) return true;

    const isMobile = isLikelyMobileDevice();
    if (!isMobile) return constrainedNetwork;

    const lowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4;
    const veryLowCpu = typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4;
    if (lowMemory || veryLowCpu) return true;
    return constrainedNetwork;
  } catch {
    return false;
  }
}

function VideoPlayer({ src, className = '', frameClassName = '', controlsMode = 'full', immersive = false }) {
  const videoRef = React.useRef(null);
  const bgRef = React.useRef(null);
  const lastTimeRef = React.useRef(0);
  const lastWasPlayingRef = React.useRef(false);
  const clickTimerRef = React.useRef(null);
  const [isArmed, setIsArmed] = React.useState(false);
  const [playing, setPlaying] = React.useState(false);
  const [muted, setMuted] = React.useState(true);
  const [duration, setDuration] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  const activeSrc = isArmed ? src : undefined;

  React.useEffect(() => {
    setIsArmed(false);
    setPlaying(false);
    setDuration(0);
    setProgress(0);
    lastTimeRef.current = 0;
    lastWasPlayingRef.current = false;
  }, [src]);

  const armSource = React.useCallback((autoplay = false) => {
    if (isArmed) return;
    lastWasPlayingRef.current = !!autoplay;
    setIsArmed(true);
  }, [isArmed]);

  const togglePlay = () => {
    if (!isArmed) {
      armSource(true);
      return;
    }
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
      try { bgRef.current?.play?.().catch(() => {}); } catch {}
    } else {
      el.pause();
      try { bgRef.current?.pause?.(); } catch {}
    }
  };
  const toggleMute = () => {
    if (!isArmed) {
      setMuted((prev) => !prev);
      return;
    }
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
    if (bgRef.current) bgRef.current.muted = true;
  };
  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    setProgress(el.currentTime || 0);
    lastTimeRef.current = el.currentTime || 0;
    if (bgRef.current) {
      try { bgRef.current.currentTime = el.currentTime || 0; } catch {}
    }
  };
  const handleLoaded = () => {
    const el = videoRef.current;
    if (!el) return;
    setDuration(el.duration || 0);
    el.muted = muted;
    setMuted(el.muted);
    if (lastTimeRef.current > 0 && Math.abs(el.currentTime - lastTimeRef.current) > 0.2) {
      try { el.currentTime = lastTimeRef.current; } catch {}
    }
    if (lastWasPlayingRef.current) {
      el.play().catch(() => {});
    }
    if (bgRef.current) {
      bgRef.current.muted = true;
      bgRef.current.currentTime = el.currentTime || 0;
    }
  };
  const handleSeek = (e) => {
    const el = videoRef.current;
    if (!el) return;
    const t = Number(e.target.value || 0);
    el.currentTime = t;
    setProgress(t);
    if (bgRef.current) {
      try { bgRef.current.currentTime = t; } catch {}
    }
  };
  const handleFullscreen = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
      return;
    }
    if (el.webkitEnterFullscreen) {
      try { el.webkitEnterFullscreen(); } catch {}
    }
  };

  React.useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onPlay = () => { setPlaying(true); lastWasPlayingRef.current = true; };
    const onPause = () => { setPlaying(false); lastWasPlayingRef.current = false; };
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
    };
  }, []);

  React.useEffect(() => {
    if (!isArmed || !activeSrc) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;
    const bg = bgRef.current;
    try { el.load(); } catch {}
    if (bg) {
      try { bg.load(); } catch {}
      bg.muted = true;
    }
    if (!lastWasPlayingRef.current) return undefined;
    const raf = requestAnimationFrame(() => {
      el.play().catch(() => {});
      try { bg?.play?.().catch(() => {}); } catch {}
    });
    return () => cancelAnimationFrame(raf);
  }, [isArmed, activeSrc]);

  const handleClick = () => {
    if (clickTimerRef.current) return;
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      togglePlay();
    }, 200);
  };
  const handleDoubleClick = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    if (!isArmed) {
      armSource(false);
      return;
    }
    handleFullscreen();
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`relative w-full overflow-hidden bg-black ${immersive ? 'rounded-none' : 'rounded-[24px]'} ${frameClassName || 'aspect-video'}`}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <div className="absolute inset-0">
          <video
            ref={bgRef}
            src={activeSrc}
            className="w-full h-full object-cover scale-110 blur-2xl opacity-60"
            muted
            playsInline
            preload="none"
            controls={false}
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
        </div>
        <video
          ref={videoRef}
          src={activeSrc}
          className="relative z-10 w-full h-full object-contain"
          playsInline
          muted={muted}
          preload="none"
          controls={false}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoaded}
        />
        {(!isArmed || !playing) && (
          <div className="absolute inset-0 flex items-center justify-center text-white/90 pointer-events-none">
            <div className="rounded-full bg-black/50 px-5 py-3 flex items-center justify-center gap-2">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              {!isArmed && <span className="text-xs font-semibold tracking-wide uppercase">Load video</span>}
            </div>
          </div>
        )}
        <div
          className="absolute left-3 right-3 bottom-3 z-20 flex items-center gap-2 rounded-full bg-black/50 px-3 py-2 text-[11px] text-white backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (!isArmed) armSource(true); else togglePlay(); }}
            className="h-7 w-7 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label={!isArmed ? 'Load and play video' : (playing ? 'Pause video' : 'Play video')}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <rect x="6" y="5" width="4" height="14" />
                <rect x="14" y="5" width="4" height="14" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleMute(); }}
            className="h-7 w-7 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label={!isArmed ? (muted ? 'Unmute when video loads' : 'Mute when video loads') : (muted ? 'Unmute video' : 'Mute video')}
          >
            {muted ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M16.5 12L19 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M19 9.5L16.5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M5 10v4h3l4 4V6L8 10H5z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                <path d="M5 10v4h3l4 4V6L8 10H5z" />
                <path d="M14.5 8.5a5 5 0 010 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
            )}
          </button>
          {controlsMode !== 'minimal' && (
            <input
              type="range"
              min={0}
              max={duration || 0}
              step="0.1"
              value={progress}
              onChange={handleSeek}
              disabled={!isArmed || !duration}
              className="flex-1 accent-white"
            />
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleFullscreen(); }}
            className="h-7 w-7 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label="Fullscreen"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M9 3H3v6" />
              <path d="M15 3h6v6" />
              <path d="M9 21H3v-6" />
              <path d="M15 21h6v-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function STLViewer({ src, className = '', interactive = true, immersive = false, autoLoad = false }) {
  const containerRef = React.useRef(null);
  const [isArmed, setIsArmed] = React.useState(!!autoLoad);

  React.useEffect(() => {
    setIsArmed(!!autoLoad);
  }, [src, autoLoad]);

  React.useEffect(() => {
    if (!isArmed) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    let disposed = false;
    let frame = 0;
    let resizeObserver = null;
    let controls = null;
    let renderer = null;
    let mesh = null;
    let camera = null;

    const boot = async () => {
      try {
        const [THREE, { STLLoader }, { OrbitControls }] = await Promise.all([
          import('three'),
          import('three/examples/jsm/loaders/STLLoader.js'),
          import('three/examples/jsm/controls/OrbitControls.js')
        ]);
        if (disposed || !containerRef.current) return;

        const activeContainer = containerRef.current;
        const cacheKey = `stl:${src}`;
        const scene = new THREE.Scene();
        scene.background = immersive ? null : new THREE.Color(0xf3f4f6);
        camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
        camera.position.set(0, 0, 4);

        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        activeContainer.appendChild(renderer.domElement);

        if (interactive) {
          controls = new OrbitControls(camera, renderer.domElement);
          controls.enableDamping = true;
          controls.dampingFactor = 0.08;
        }

        const ambient = new THREE.HemisphereLight(0xffffff, 0xaaaaaa, 1);
        scene.add(ambient);
        const dir = new THREE.DirectionalLight(0xffffff, 0.8);
        dir.position.set(5, 8, 6);
        scene.add(dir);

        const loader = new STLLoader();
        loader.load(
          src,
          (geometry) => {
            if (disposed) {
              geometry.dispose();
              return;
            }
            geometry.computeBoundingBox();
            geometry.computeVertexNormals();
            const material = new THREE.MeshStandardMaterial({ color: 0x9ca3af, metalness: 0.1, roughness: 0.6 });
            mesh = new THREE.Mesh(geometry, material);
            scene.add(mesh);

            const box = geometry.boundingBox;
            if (box) {
              const size = new THREE.Vector3();
              box.getSize(size);
              const center = new THREE.Vector3();
              box.getCenter(center);
              mesh.position.sub(center);
              const maxDim = Math.max(size.x, size.y, size.z) || 1;
              camera.position.set(0, 0, maxDim * 2.2);
              camera.near = maxDim / 100;
              camera.far = maxDim * 20;
              camera.updateProjectionMatrix();
            }

            const cached = modelViewStateCache.get(cacheKey);
            if (cached && cached.position && cached.target && controls) {
              camera.position.copy(cached.position);
              controls.target.copy(cached.target);
              controls.update();
            }
          },
          undefined,
          () => {}
        );

        const resize = () => {
          const targetContainer = containerRef.current;
          if (!targetContainer || !renderer || !camera) return;
          const { clientWidth, clientHeight } = targetContainer;
          const width = clientWidth || 1;
          const height = clientHeight || 1;
          renderer.setSize(width, height, false);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
        };
        resize();
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(activeContainer);

        const animate = () => {
          frame = requestAnimationFrame(animate);
          if (controls) controls.update();
          renderer.render(scene, camera);
        };
        animate();
      } catch {
        // Ignore viewer boot failures and keep the layout stable.
      }
    };

    boot();

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      if (resizeObserver) resizeObserver.disconnect();
      if (controls && camera) {
        modelViewStateCache.set(`stl:${src}`, {
          position: camera.position.clone(),
          target: controls.target.clone()
        });
      }
      if (controls) controls.dispose();
      if (renderer) renderer.dispose();
      if (mesh) {
        mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) mesh.material.forEach(m => m.dispose());
        else mesh.material.dispose();
      }
      if (renderer?.domElement?.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [src, immersive, interactive, isArmed]);

  const needsHeight = !(className.includes('h-') || className.includes('aspect-'));
  const baseStyle = immersive ? 'bg-black rounded-none' : 'bg-gray-50 rounded-[24px]';
  if (!isArmed) {
    return (
      <button
        type="button"
        onClick={() => setIsArmed(true)}
        className={`w-full ${needsHeight ? 'h-[60vh]' : ''} ${baseStyle} overflow-hidden ${className} flex items-center justify-center text-gray-100`}
      >
        <span className="rounded-full bg-black/55 px-5 py-3 flex items-center justify-center gap-2">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          <span className="text-xs font-semibold tracking-wide uppercase">Load 3D preview</span>
        </span>
      </button>
    );
  }
  return (
    <div
      ref={containerRef}
      className={`w-full ${needsHeight ? 'h-[60vh]' : ''} ${baseStyle} overflow-hidden ${className}`}
    />
  );
}

function ModelViewer({ src, interactive = false, className = '', style, autoLoad = false }) {
  const viewerRef = React.useRef(null);
  const [isArmed, setIsArmed] = React.useState(!!autoLoad);
  const [viewerReady, setViewerReady] = React.useState(() => {
    if (typeof window === 'undefined' || !window.customElements) return false;
    return !!window.customElements.get('model-viewer');
  });

  React.useEffect(() => {
    setIsArmed(!!autoLoad);
  }, [src, autoLoad]);

  React.useEffect(() => {
    if (!isArmed) return undefined;
    let cancelled = false;
    ensureModelViewerElementLoaded()
      .then(() => {
        if (!cancelled) setViewerReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isArmed]);

  React.useEffect(() => {
    if (!isArmed) return undefined;
    if (!viewerReady) return undefined;
    const el = viewerRef.current;
    if (!el || !src) return undefined;
    const cacheKey = `model:${src}`;
    const cached = modelViewStateCache.get(cacheKey);
    if (cached) {
      if (cached.orbit) el.cameraOrbit = cached.orbit;
      if (cached.target) el.cameraTarget = cached.target;
      if (cached.fov) el.fieldOfView = cached.fov;
    }
    const handleCameraChange = () => {
      if (!interactive) return;
      try {
        modelViewStateCache.set(cacheKey, {
          orbit: el.cameraOrbit,
          target: el.cameraTarget,
          fov: el.fieldOfView
        });
      } catch {}
    };
    if (interactive) el.addEventListener('camera-change', handleCameraChange);
    return () => {
      if (interactive) {
        handleCameraChange();
        el.removeEventListener('camera-change', handleCameraChange);
      }
    };
  }, [src, interactive, viewerReady, isArmed]);

  if (!isArmed) {
    return (
      <button
        type="button"
        onClick={() => setIsArmed(true)}
        className={`w-full h-full flex items-center justify-center bg-gray-100 text-gray-600 ${className}`}
        style={style}
      >
        <span className="rounded-full bg-black/55 px-5 py-3 text-white text-xs font-semibold tracking-wide uppercase">Load 3D preview</span>
      </button>
    );
  }

  if (!viewerReady) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-500 ${className}`}
        style={style}
      >
        Loading 3D preview...
      </div>
    );
  }

  return (
    <model-viewer
      ref={viewerRef}
      src={src}
      camera-controls={interactive ? true : undefined}
      ar
      interaction-prompt="none"
      style={{ width: '100%', height: '100%', pointerEvents: interactive ? 'auto' : 'none', ...style }}
      className={className}
    />
  );
}

// Lightweight image helper. For local uploads, attempts AVIF/WEBP srcsets
// using server-generated variants: filename-600.webp/avif and filename-1200.webp/avif.
function PVImage({ src, alt = '', className = '', style, onError, sizes = '100vw', imgProps = {} }) {
  const resolved = resolveMediaUrl(src);
  const kind = getMediaKind(src);
  const { variant, onClick: imgClick, interactive3d = false, frameClassName, useVariants = false, ...restImgProps } = imgProps || {};
  if (kind === 'video') {
    if (variant === 'thumb') {
      return (
        <div className={`relative ${className}`} style={style} onClick={imgClick}>
          <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-700" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-full bg-black/55 px-3 py-2 flex items-center gap-2 text-white">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              <span className="text-[10px] font-semibold uppercase tracking-wide">Video</span>
            </div>
          </div>
        </div>
      );
    }
    const inferredFrameClass = frameClassName || ((className || '').includes('aspect-') || (className || '').includes('h-') ? 'h-full' : 'aspect-video');
    const wrapperClass = className || '';
    const playerClass = wrapperClass.includes('h-') || wrapperClass.includes('aspect-') ? 'h-full w-full' : 'w-full';
    return (
      <div className={wrapperClass}>
        <VideoPlayer src={resolved} className={playerClass} frameClassName={inferredFrameClass} />
      </div>
    );
  }
  if (kind === 'model') {
    if (variant === 'thumb') {
      const thumbClass = className || 'h-20 w-20';
      return (
        <div className={`relative ${thumbClass} bg-gradient-to-br from-gray-900 to-gray-700 overflow-hidden`} style={style} onClick={imgClick}>
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <span className="rounded-full bg-black/55 px-3 py-2 text-[10px] font-semibold tracking-wide uppercase">3D</span>
          </div>
        </div>
      );
    }
    const isStl = String(resolved || '').toLowerCase().match(/\.(stl)(\?.*)?$/);
    if (isStl) {
      return (
        <div className={`w-full ${className}`} style={style} onClick={imgClick}>
          <STLViewer src={resolved} interactive={interactive3d} autoLoad={false} />
        </div>
      );
    }
    if (!isModelPreviewable(resolved)) {
      return (
        <div className={`flex items-center justify-center bg-gray-100 text-gray-600 ${className}`} style={style} onClick={imgClick}>
          3D file (preview unavailable)
        </div>
      );
    }
    return (
      <div className={`w-full ${className}`} style={style} onClick={imgClick}>
        <div className="w-full aspect-square bg-gray-50 rounded-[24px] overflow-hidden">
          <ModelViewer src={resolved} interactive={interactive3d} autoLoad={false} />
        </div>
      </div>
    );
  }
  const srcRaw = String(src || '').trim();
  const isLocal = srcRaw.startsWith('/uploads/')
    || srcRaw.startsWith('/shopapi/uploads/')
    || /^[^/:]+\.(jpe?g|png|webp|avif)$/i.test(srcRaw);
  if (!isLocal || !useVariants) {
    return (
      <img
        src={resolved}
        alt={alt}
        className={className}
        style={style}
        onClick={imgClick}
        onError={onError || handleImgError}
        loading="lazy"
        decoding="async"
        {...restImgProps}
      />
    );
  }
  try {
    const normalized = String(resolved || '').trim().replace(/\\/g, '/');
    const noQuery = normalized.split('?')[0].split('#')[0];
    const uploadPrefix = noQuery.startsWith('/shopapi/uploads/')
      ? '/shopapi/uploads/'
      : (noQuery.startsWith('/uploads/') ? '/uploads/' : '');
    if (!uploadPrefix) throw new Error('Unsupported upload URL');
    const relPath = noQuery.slice(uploadPrefix.length);
    const slashIdx = relPath.lastIndexOf('/');
    const relDir = slashIdx >= 0 ? relPath.slice(0, slashIdx + 1) : '';
    const baseName = slashIdx >= 0 ? relPath.slice(slashIdx + 1) : relPath;
    const dot = baseName.lastIndexOf('.');
    if (dot <= 0) throw new Error('Missing extension');
    const nameNoExt = baseName.slice(0, dot);
    const variantBase = `${uploadPrefix}${relDir}${nameNoExt}`;
    const widths = [600, 1200];
    const webpSet = widths.map(w => `${variantBase}-${w}.webp ${w}w`).join(', ');
    const avifSet = widths.map(w => `${variantBase}-${w}.avif ${w}w`).join(', ');
    return (
      <picture>
        <source type="image/avif" srcSet={avifSet} sizes={sizes} />
        <source type="image/webp" srcSet={webpSet} sizes={sizes} />
          <img
            src={resolved}
            alt={alt}
            className={className}
            style={style}
            onClick={imgClick}
            onError={onError || handleImgError}
            loading="lazy"
            decoding="async"
            {...restImgProps}
          />
      </picture>
    );
  } catch {
    return (
      <img
        src={resolved}
        alt={alt}
        className={className}
        style={style}
        onClick={imgClick}
        onError={onError || handleImgError}
        loading="lazy"
        decoding="async"
        {...restImgProps}
      />
    );
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { try { console.error('UI Error:', err, info); } catch {} }
  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-xl mx-auto p-6 m-6 rounded-2xl sm:rounded-none border border-gray-200 bg-white text-center">
          <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
          <p className="mt-2 text-sm text-gray-600">Please try reloading the page. If the problem persists, contact support.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const Icon = {
  Home: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11.5L12 4l9 7.5"/><path d="M9 21V12h6v9"/></svg>
  ),
  Cart: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6h15l-1.5 9h-12z"/><circle cx="9" cy="20" r="1"/><circle cx="19" cy="20" r="1"/></svg>
  ),
  Trash: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
  ),
  Search: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="6"/><path d="M21 21l-4.35-4.35"/></svg>
  ),
  Menu: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/></svg>
  ),
  Loading: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
      <path d="M21 12a9 9 0 11-6.219-8.56"/>
    </svg>
  )
};

// React-medium-image-zoom integration removed due to runtime errors; using custom lightbox implementation

const API_BASE = '/shopapi';

// Default accent color (overridden by settings.accentColor if provided)
const THEME = { accent: '#fbbf24', accentRgb: '251,191,36' };

function hexToRgbStr(hex) {
  try {
    let h = String(hex || '').trim();
    if (!h) return THEME.accentRgb;
    if (/^rgba?\(/i.test(h)) {
      const fnMatch = h.match(/^rgba?\((.*)\)$/i);
      if (fnMatch) {
        let body = String(fnMatch[1] || '').trim();
        body = body.split('/')[0].trim();
        const parts = body.replace(/,/g, ' ').trim().split(/\s+/).filter(Boolean);
        if (parts.length >= 3) {
          const parseChannel = (token) => {
            const t = String(token || '').trim();
            if (!t) return null;
            if (t.endsWith('%')) {
              const p = Number.parseFloat(t.slice(0, -1));
              if (!Number.isFinite(p)) return null;
              return Math.max(0, Math.min(255, Math.round((p / 100) * 255)));
            }
            const n = Number.parseFloat(t);
            if (!Number.isFinite(n)) return null;
            return Math.max(0, Math.min(255, Math.round(n)));
          };
          const r = parseChannel(parts[0]);
          const g = parseChannel(parts[1]);
          const b = parseChannel(parts[2]);
          if (r !== null && g !== null && b !== null) return `${r},${g},${b}`;
        }
      }
      return THEME.accentRgb;
    }
    if (h.startsWith('#')) h = h.slice(1);
    if (h.length === 3) {
      h = h.split('').map(ch => ch + ch).join('');
    }
    const int = parseInt(h, 16);
    if (!Number.isFinite(int)) return THEME.accentRgb;
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `${r},${g},${b}`;
  } catch {
    return THEME.accentRgb;
  }
}

const DEFAULT_PRICE_MIN = 0;
const DEFAULT_PRICE_MAX = 15000;

function genId() {
  try {
    const a = crypto.getRandomValues(new Uint8Array(16));
    a[6] = (a[6] & 0x0f) | 0x40;
    a[8] = (a[8] & 0x3f) | 0x80;
    const h = [...a].map((b,i)=> (i===4||i===6||i===8||i===10?'-':'') + b.toString(16).padStart(2,'0')).join('');
    return h;
  } catch {
    return String(Date.now()) + Math.random().toString(16).slice(2);
  }
}

// Common Bangladesh cities for checkout datalist
const BD_CITIES = [
  'Dhaka', 'Chattogram', 'Chittagong', 'Gazipur', 'Narayanganj', 'Khulna', 'Rajshahi', 'Sylhet',
  'Mymensingh', 'Barishal', 'Rangpur', 'Cumilla', "Cox's Bazar", 'Jessore', 'Bogra', 'Tangail',
  'Narsingdi', 'Savar', 'Dinajpur', 'Pabna'
];

const ORDER_STATUS_STEPS = [
  {
    id: 'pending',
    label: 'Pending',
    description: 'We received your order and are reviewing the details.'
  },
  {
    id: 'confirmed',
    label: 'Confirmed',
    description: 'Our team confirmed payment and item availability.'
  },
  {
    id: 'processing',
    label: 'Processing',
    description: 'We are preparing your items for dispatch.'
  },
  {
    id: 'shipped',
    label: 'Shipped',
    description: 'The order is on its way with the courier.'
  },
  {
    id: 'delivered',
    label: 'Delivered',
    description: 'Delivered to the address provided.'
  }
];

const ORDER_STATUS_CANCELLED_STEP = {
  id: 'cancelled',
  label: 'Cancelled',
  description: 'This order was cancelled. Contact support if you need assistance.'
};

const AMBIENT_STYLE_ID = 'shopstarter-ambient-style';
const STARTUP_PRELOAD_SESSION_KEY = 'shopstarter_startup_preloaded_v1';

// Lightweight fuzzy search helpers for header typeahead
function normalize(str) {
  return String(str || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
function slugify(str) {
  const base = normalize(str);
  if (!base) return 'item';
  return base.replace(/\s+/g, '-');
}
const SEARCH_SYNONYMS = {
  lantern: ['lamp', 'light', 'torch'],
  soul: ['soul lantern', 'blue lantern'],
  sword: ['blade', 'weapon'],
  pickaxe: ['pick axe', 'pick-axe', 'miner pick'],
  relic: ['collectible', 'collectibles', 'prop']
};
function expandTerm(term) {
  const t = normalize(term);
  const extra = Object.entries(SEARCH_SYNONYMS).flatMap(([k, vals]) =>
    (t === k || vals.includes(t)) ? [k, ...vals] : []
  );
  return [t, ...extra];
}
function levenshtein(a, b) {
  a = normalize(a); b = normalize(b);
  const m = a.length, n = b.length;
  if (m === 0) return n; if (n === 0) return m;
  const dp = Array(n + 1).fill(0).map((_, j) => j);
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = dp[j];
      if (a[i - 1] === b[j - 1]) dp[j] = prev;
      else dp[j] = Math.min(prev + 1, dp[j] + 1, dp[j - 1] + 1);
      prev = temp;
    }
  }
  return dp[n];
}
function fuzzyIncludes(haystack, term) {
  const h = normalize(haystack);
  const candidates = expandTerm(term);
  for (const t of candidates) {
    if (!t) continue;
    if (h.includes(t)) return true;
    if (t.length >= 4) {
      const words = h.split(' ');
      if (words.some(w => Math.abs(w.length - t.length) <= 1 && levenshtein(w, t) <= 1)) return true;
    }
  }
  return false;
}

const formatOrderStatus = (status) => {
  if (!status) return '';
  const normalized = String(status).replace(/_/g, ' ');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const extractTrackingIdFromPath = (pathname) => {
  const rawPath = String(pathname || '').replace(/\/+$/, '') || '/';
  if (rawPath === '/track') return '';
  if (!rawPath.startsWith('/track/')) return '';
  const encodedId = rawPath.slice('/track/'.length);
  try {
    return decodeURIComponent(encodedId).replace(/\s+/g, '').replace(/[^a-z0-9-]/gi, '').toUpperCase();
  } catch {
    return String(encodedId || '').replace(/\s+/g, '').replace(/[^a-z0-9-]/gi, '').toUpperCase();
  }
};

const extractRequestedProductSlug = (locationLike) => {
  try {
    const pathname = String(locationLike?.pathname || '');
    if (pathname.startsWith('/product/')) {
      return decodeURIComponent(pathname.split('/product/')[1] || '').trim();
    }
    const search = String(locationLike?.search || '');
    const params = new URLSearchParams(search);
    return String(params.get('product') || '').trim();
  } catch {
    return '';
  }
};

const DEFAULT_META_PIXEL_ID = '';

// Default settings used as a safe fallback everywhere
const DEFAULT_SETTINGS = {
  siteName: 'ItemSmith',
  siteTitle: 'ItemSmith — Products Worth Sharing',
  currency: '৳',
  facebookUrl: '',
  instagramUrl: '',
  supportEmail: '',
  tagline: 'Discover standout products, seasonal drops, and everyday essentials from ItemSmith.',
  shareImageUrl: '',
  metaPixelId: DEFAULT_META_PIXEL_ID,
  homeHeroDescription: 'Discover standout products, fresh arrivals, and practical finds curated for everyday life.',
  shippingRates: { inside: 60, outside: 120 },
  bkashNumber: '018XXXXXXXX',
  bkashQrUrl: '',
  bkashCashoutEnabled: false,
  bkashCashoutAmount: 20,
  orderIdPrefix: 'PV',
  orderIdSuffix: '',
  enableCampaignPopup: true,
  campaignPopupDelaySeconds: 3,
  logoUrl: '/logo.png',
  logoAccentUrl: '',
  faviconUrl: '',
  heroBanners: [
    {
      id: 'default-1',
      image: 'https://images.pexels.com/photos/167225/pexels-photo-167225.jpeg',
      headline: 'Fresh Finds for Everyday Use',
      subheadline: 'Explore well-made products, practical picks, and standout pieces for work, home, and gifting.',
      ctaLabel: 'Shop now',
      href: '/catalog',
      active: true
    },
    {
      id: 'default-2',
      image: 'https://images.pexels.com/photos/112811/pexels-photo-112811.jpeg',
      headline: 'New Drops, Limited Offers',
      subheadline: 'Keep up with new arrivals, featured products, and limited-time offers across the store.',
      ctaLabel: 'Browse offers',
      href: '/catalog',
      active: true
    },
    {
      id: 'default-3',
      image: 'https://images.pexels.com/photos/974470/pexels-photo-974470.jpeg',
      headline: 'More Items Coming Soon',
      subheadline: 'New drops land regularly. Browse the catalog for the latest items.',
      ctaLabel: 'Browse Catalog',
      href: '/catalog',
      active: true
    }
  ],
  homepageBanners: []
};

function normalizeSettings(data = {}) {
  const merged = {
    ...DEFAULT_SETTINGS,
    ...data
  };
  if (!merged.siteName) {
    merged.siteName = 'ItemSmith';
  }
  merged.siteTitle = (typeof data.siteTitle === 'string' && data.siteTitle.trim().length > 0)
    ? data.siteTitle.trim()
    : DEFAULT_SETTINGS.siteTitle;
  merged.tagline = (typeof data.tagline === 'string' && data.tagline.trim().length > 0)
    ? data.tagline.trim()
    : DEFAULT_SETTINGS.tagline;
  merged.shareImageUrl = (typeof data.shareImageUrl === 'string') ? data.shareImageUrl.trim() : DEFAULT_SETTINGS.shareImageUrl;
  merged.metaPixelId = (typeof data.metaPixelId === 'string') ? data.metaPixelId.trim() : DEFAULT_SETTINGS.metaPixelId;
  merged.supportEmail = (typeof data.supportEmail === 'string') ? data.supportEmail.trim() : DEFAULT_SETTINGS.supportEmail;
  merged.homeHeroDescription = (typeof data.homeHeroDescription === 'string') ? data.homeHeroDescription : DEFAULT_SETTINGS.homeHeroDescription;
  const inside = Number(data?.shippingRates?.inside);
  const outside = Number(data?.shippingRates?.outside);
  merged.shippingRates = {
    inside: Number.isFinite(inside) ? inside : DEFAULT_SETTINGS.shippingRates.inside,
    outside: Number.isFinite(outside) ? outside : DEFAULT_SETTINGS.shippingRates.outside
  };
  merged.bkashCashoutEnabled = data.bkashCashoutEnabled ?? DEFAULT_SETTINGS.bkashCashoutEnabled;
  merged.bkashCashoutAmount = Math.max(0, Number(data.bkashCashoutAmount ?? DEFAULT_SETTINGS.bkashCashoutAmount) || 0);
  merged.orderIdPrefix = typeof data.orderIdPrefix === 'string' ? data.orderIdPrefix : DEFAULT_SETTINGS.orderIdPrefix;
  merged.orderIdSuffix = typeof data.orderIdSuffix === 'string' ? data.orderIdSuffix : DEFAULT_SETTINGS.orderIdSuffix;
  merged.heroBanners = Array.isArray(data.heroBanners) ? data.heroBanners : [];
  merged.homepageBanners = Array.isArray(data.homepageBanners) ? data.homepageBanners : [];
  merged.logoUrl = (typeof data.logoUrl === 'string' && data.logoUrl.trim().length > 0) ? data.logoUrl : '';
  merged.logoAccentUrl = (typeof data.logoAccentUrl === 'string') ? data.logoAccentUrl : DEFAULT_SETTINGS.logoAccentUrl;
  merged.faviconUrl = (typeof data.faviconUrl === 'string') ? data.faviconUrl : DEFAULT_SETTINGS.faviconUrl;
  merged.enableCampaignPopup = data.enableCampaignPopup ?? DEFAULT_SETTINGS.enableCampaignPopup;
  if (typeof data.accentColor === 'string' && data.accentColor.trim().length > 0) {
    merged.accentColor = data.accentColor.trim();
  }
  const popupDelay = Number(data.campaignPopupDelaySeconds);
  merged.campaignPopupDelaySeconds = Number.isFinite(popupDelay) && popupDelay >= 0
    ? popupDelay
    : DEFAULT_SETTINGS.campaignPopupDelaySeconds;
  return merged;
}

const currency = (n) => `৳${n.toLocaleString()}`;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const formatCurrency = (n) => '৳' + Number(n || 0).toLocaleString();
const BASE_PRODUCT_OPTION_ID = '__base__';

function PriceDisplay({
  price,
  originalPrice,
  showFrom = false,
  align = 'left',
  wrapperClassName = '',
  currentClassName = '',
  originalClassName = '',
  currentStyle,
  originalStyle,
}) {
  const safePrice = Math.max(0, Number(price || 0) || 0);
  const safeOriginalPrice = Math.max(0, Number(originalPrice || 0) || 0);
  const hasDiscount = safeOriginalPrice > safePrice;
  const alignmentClasses = align === 'right' ? 'items-end text-right' : 'items-start text-left';
  const currentText = `${showFrom ? 'From ' : ''}${currency(safePrice)}`;
  const originalText = `${showFrom ? 'From ' : ''}${currency(safeOriginalPrice)}`;

  return (
    <div className={`flex flex-col ${alignmentClasses} ${wrapperClassName}`.trim()}>
      {hasDiscount && (
        <span className={originalClassName} style={originalStyle}>
          {originalText}
        </span>
      )}
      <span className={currentClassName} style={currentStyle}>
        {currentText}
      </span>
    </div>
  );
}

function MiniCloseIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l12 12" />
      <path d="M18 6l-12 12" />
    </svg>
  );
}

const promoRemoveButtonClass = 'appearance-none inline-flex h-6 w-6 min-h-6 min-w-6 max-h-6 max-w-6 aspect-square shrink-0 items-center justify-center overflow-hidden rounded-sm bg-transparent p-0 align-middle leading-none text-[var(--accent)] transition hover:bg-[var(--accent)]/8 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/25';

function parseStringArrayInput(input) {
  try {
    const raw = Array.isArray(input) ? input : JSON.parse(input || '[]');
    return Array.isArray(raw) ? raw.map((entry) => String(entry || '').trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function parseProductVariantsForStorefront(input) {
  try {
    const raw = Array.isArray(input) ? input : JSON.parse(input || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .map((entry, index) => ({
        id: String(entry?.id || `variant_${index + 1}`).trim(),
        label: String(entry?.label || '').trim() || `Option ${index + 1}`,
        sku: String(entry?.sku || '').trim(),
        description: String(entry?.description || '').trim(),
        price: Math.max(0, Number(entry?.price ?? entry?.basePrice) || 0),
        basePrice: Math.max(0, Number(entry?.basePrice ?? entry?.price) || 0),
        originalPrice: Number(entry?.originalPrice) > 0 ? Number(entry.originalPrice) : null,
        stock: Math.max(0, parseInt(entry?.stock, 10) || 0),
        active: entry?.active !== false && entry?.active !== 0 && entry?.active !== 'false',
        img: String(entry?.img || entry?.imageUrl || '').trim() || '',
        images: parseStringArrayInput(entry?.images),
      }))
      .filter((entry) => entry.id);
  } catch {
    return [];
  }
}

function formatSelectedOptionLabel(variantLabel) {
  const value = String(variantLabel || '').trim();
  return value ? `Option: ${value}` : '';
}

function normalizeStorefrontProduct(product) {
  const variants = parseProductVariantsForStorefront(product?.variants);
  const activeVariants = variants.filter((variant) => variant.active !== false);
  const galleryImages = parseStringArrayInput(product?.images);
  const hideBaseModel = product?.hideBaseModel === true || product?.hide_base_model === 1 || product?.hide_base_model === true;
  const basePrice = Math.max(0, Number(product?.basePrice ?? product?.price) || 0);
  const baseDisplayPrice = Math.max(0, Number(product?.baseDisplayPrice ?? basePrice) || 0);
  const baseOriginalPrice = Number(product?.baseOriginalPrice) > baseDisplayPrice
    ? Number(product.baseOriginalPrice)
    : (Number(product?.originalPrice) > baseDisplayPrice && !activeVariants.length ? Number(product.originalPrice) : null);
  const displayCandidate = [
    ...(!hideBaseModel ? [{ price: baseDisplayPrice, originalPrice: baseOriginalPrice, img: String(product?.img || '').trim() || galleryImages[0] || '' }] : []),
    ...activeVariants
  ]
    .reduce((best, current) => (
      !best || Number(current.price) < Number(best.price) ? current : best
    ), null);
  return {
    ...product,
    img: String(product?.img || '').trim() || displayCandidate?.img || galleryImages[0] || '',
    images: galleryImages,
    variants,
    hideBaseModel,
    hasVariants: activeVariants.length > 0,
    optionResolved: activeVariants.length === 0,
    basePrice,
    baseDisplayPrice,
    baseOriginalPrice,
    displayPrice: Number(displayCandidate?.price || 0),
    displayOriginalPrice: Number(displayCandidate?.originalPrice) > Number(displayCandidate?.price)
      ? Number(displayCandidate.originalPrice)
      : null,
    displayStock: activeVariants.length > 0
      ? (!hideBaseModel ? Math.max(0, Number(product?.baseStock ?? product?.base_stock ?? product?.stock) || 0) : 0) + activeVariants.reduce((sum, variant) => sum + (Number(variant.stock) || 0), 0)
      : Math.max(0, Number(product?.stock) || 0),
    displayVariant: activeVariants.length > 0 && displayCandidate && activeVariants.some((variant) => variant.id === displayCandidate.id)
      ? displayCandidate
      : null
  };
}

function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const t = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return debounced;
}

function useToasts() {
  const [toasts, setToasts] = useState([]);
  const toastTimeouts = React.useRef(new Map());
  
  useEffect(() => {
    // Capture the current value inside the effect to avoid stale reference in cleanup
    const timeouts = toastTimeouts.current;
    return () => {
      // Clear all timeouts on unmount
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);
  
  const push = React.useCallback((msg, type = 'info') => {
    const id = Date.now() + Math.random(); // More unique ID
    setToasts((prev) => {
      // Prevent duplicate messages within a short time window
      const isDuplicate = prev.some(t => 
        t.msg === msg && (id - t.id) < 100
      );
      if (isDuplicate) return prev;
      
      // Add new toast
      const newToasts = [...prev, { id, msg, type }];
      
      // Set timeout to remove this toast
      const timeout = setTimeout(() => {
        setToasts((s) => s.filter(t => t.id !== id));
        toastTimeouts.current.delete(id);
      }, 3500);
      
      toastTimeouts.current.set(id, timeout);
      
      return newToasts;
    });
  }, []);
  const dismiss = React.useCallback((id) => {
    const timeout = toastTimeouts.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      toastTimeouts.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  
  return { toasts, push, dismiss };
}

function useApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stabilize request identity so effects that depend on it don't re-run every render
  const request = React.useCallback(async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const method = (options.method || 'GET').toUpperCase();
      const ts = Date.now();
      const urlBase = `${API_BASE}${endpoint}`;
      const url = method === 'GET'
        ? (urlBase.includes('?') ? `${urlBase}&__t=${ts}` : `${urlBase}?__t=${ts}`)
        : urlBase;
      const fetchOpts = {
        cache: options.cache ?? 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Pragma': 'no-cache',
          'Cache-Control': 'no-cache',
          ...options.headers,
        },
        ...options,
      };
      const doFetch = async (u, opts) => {
        const res = await fetch(u, opts);
        if (res.status === 304) {
          // Try once more bypassing caches
          const retry = await fetch(u, { ...opts, cache: 'reload' });
          return retry;
        }
        return res;
      };
      const response = await doFetch(url, fetchOpts);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { request, loading, error };
}

export default function ShopStarterApp() {
  // Banner carousel state (using provided demo images)
  const [bannerIdx, setBannerIdx] = useState(0);
  const [page, setPage] = useState(() => {
    const url = window.location.pathname;
    const requestedProductSlug = extractRequestedProductSlug(window.location);
    if (requestedProductSlug) return 'product';
    if (url === '/' || url === '') return 'home';
    if (url.startsWith('/product/')) return 'product';
    if (url === '/catalog') return 'catalog';
    if (url === '/cart') return 'cart';
    if (url === '/checkout') return 'checkout';
    if (url === '/thanks') return 'thanks';
    if (url === '/track' || url.startsWith('/track/')) return 'track';
    if (url === '/offers') return 'offers';
    if (url === '/upcoming' || url === '/request') return 'catalog';
    if (url === '/terms') return 'terms';
    if (url === '/privacy') return 'privacy';
    if (url === '/cookies') return 'cookies';
    if (url === '/returns') return 'returns';
    if (url === '/help') return 'help';
    if (url.startsWith('/legal/')) return 'legal';
    return 'notfound';
  });
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const legacyPath = window.location.pathname;
    if (legacyPath === '/upcoming' || legacyPath === '/request') {
      window.history.replaceState({}, '', '/catalog');
    }
  }, []);
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem('shopstarter_settings');
      if (raw) return normalizeSettings(JSON.parse(raw));
    } catch {}
    return normalizeSettings({ heroBanners: [], logoUrl: '' });
  });
  const [settingsReady, setSettingsReady] = useState(() => {
    try { return !!localStorage.getItem('shopstarter_settings'); } catch { return false; }
  });
  const [showStartupLoader, setShowStartupLoader] = useState(() => {
    try {
      return sessionStorage.getItem(STARTUP_PRELOAD_SESSION_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const [startupLoadProgress, setStartupLoadProgress] = useState(0);
  const startupLoaderStartedRef = React.useRef(false);
  const startupCriticalImageUrlsRef = React.useRef([]);
  const [isLiteMode, setIsLiteMode] = useState(() => detectPerformanceLiteMode());
  // Toast notifications (must be declared before any use)
  const { toasts, push, dismiss } = useToasts();
  const [legalPage, setLegalPage] = useState(null);
  const [legalPageLoading, setLegalPageLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 250);
  // Catalog-specific search (independent from header search)
  const [catalogSearch, setCatalogSearch] = useState('');
  const debouncedCatalogSearch = useDebounced(catalogSearch, 250);
  const [isSearchFocused, setSearchFocused] = useState(false);
  const [isTypeaheadOpen, setTypeaheadOpen] = useState(false);
  const [typeaheadIndex, setTypeaheadIndex] = useState(-1);
  const productSlugIndex = useMemo(() => {
    const slugById = new Map();
    const productBySlug = new Map();
    const slugCounters = new Map();
    (products || []).forEach((p) => {
      const base = slugify(p?.title || p?.name || 'item');
      const nextIndex = (slugCounters.get(base) || 0) + 1;
      slugCounters.set(base, nextIndex);
      const slug = nextIndex === 1 ? base : `${base}-${nextIndex}`;
      if (p?.id) slugById.set(String(p.id), slug);
      if (p?.dna_id) slugById.set(String(p.dna_id), slug);
      if (!productBySlug.has(slug)) productBySlug.set(slug, p);
      if (!productBySlug.has(base)) productBySlug.set(base, p);
    });
    return { slugById, productBySlug };
  }, [products]);
  const typeaheadResults = useMemo(() => {
    const q = normalize(debouncedSearch || '');
    if (q.length < 2) return [];
    const terms = q.split(/\s+/).filter(Boolean);
    const safeProducts = (products || []).filter(Boolean);
    const matches = safeProducts.filter((p) => {
      const t = `${p?.title || ''} ${p?.category || ''} ${p?.color || ''}`;
      return terms.every((term) => fuzzyIncludes(t, term));
    });
    return matches.slice(0, 8);
  }, [products, debouncedSearch]);
  useEffect(() => {
    if (isSearchFocused && typeaheadResults.length > 0) setTypeaheadOpen(true);
    else setTypeaheadOpen(false);
    setTypeaheadIndex(-1);
  }, [isSearchFocused, typeaheadResults.length]);
  
  const [filters, setFilters] = useState(() => {
    try {
      const raw = localStorage.getItem('shopstarter_catalog_filters');
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          category: parsed.category || 'All',
          tags: Array.isArray(parsed.tags) ? parsed.tags : [],
          priceMin: Number.isFinite(parsed.priceMin) ? parsed.priceMin : DEFAULT_PRICE_MIN,
          priceMax: Number.isFinite(parsed.priceMax) ? parsed.priceMax : DEFAULT_PRICE_MAX,
          sort: parsed.sort || 'priceLow'
        };
      }
    } catch {}
    return { category: 'All', tags: [], priceMin: DEFAULT_PRICE_MIN, priceMax: DEFAULT_PRICE_MAX, sort: 'priceLow' };
  });
  useEffect(() => {
    try { localStorage.setItem('shopstarter_catalog_filters', JSON.stringify(filters)); } catch {}
  }, [filters]);
  const [activePriceFilter, setActivePriceFilter] = useState('all');
  const catalogCategories = useMemo(() => {
    return Array.from(new Set((products || []).filter(Boolean).map(p => p?.category).filter(Boolean)));
  }, [products]);
  const catalogTags = useMemo(() => {
    const all = [];
    (products || []).forEach(p => {
      const tags = Array.isArray(p?.tags) ? p.tags : [];
      tags.forEach(t => { if (t) all.push(String(t)); });
    });
    return Array.from(new Set(all.map(s => s.toLowerCase())));
  }, [products]);
  const activeCatalogFilterCount = useMemo(() => {
    let count = 0;
    if (filters.category !== 'All') count += 1;
    if (filters.tags && filters.tags.length > 0) count += 1;
    if (filters.priceMin > DEFAULT_PRICE_MIN || filters.priceMax < DEFAULT_PRICE_MAX) count += 1;
    return count;
  }, [filters]);
  
  // Helpers to manage <head> metadata and JSON-LD without external libs
  const setMeta = useCallback((key, content, { property = false } = {}) => {
    try {
      const selector = property ? `meta[property="${key}"]` : `meta[name="${key}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (property) el.setAttribute('property', key); else el.setAttribute('name', key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', String(content || ''));
    } catch {}
  }, []);

  const setJsonLd = useCallback((id, data) => {
    try {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        document.head.appendChild(el);
      }
      el.text = JSON.stringify(data);
    } catch {}
  }, []);

  const removeEl = useCallback((id) => {
    try { const el = document.getElementById(id); if (el) el.remove(); } catch {}
  }, []);

  const setPreconnect = useCallback((href, id) => {
    try {
      if (typeof document === 'undefined') return;
      const linkId = id || `preconnect-${href.replace(/[^a-z0-9]/gi, '-')}`;
      if (document.getElementById(linkId)) return;
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = '';
      document.head.appendChild(link);
    } catch {}
  }, []);

  const setCanonical = useCallback((href) => {
    try {
      if (typeof document === 'undefined') return;
      if (!href) return;
      let el = document.head.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    } catch {}
  }, []);

  const setFaviconHref = useCallback((href) => {
    try {
      if (typeof document === 'undefined') return;
      const rawHref = String(href || '/logo.png').trim() || '/logo.png';
      const finalHref = resolveMediaUrl(rawHref);
      const lower = finalHref.toLowerCase();
      let iconType = '';
      if (lower.endsWith('.ico') || lower.includes('.ico?')) iconType = 'image/x-icon';
      else if (lower.endsWith('.svg') || lower.includes('.svg?')) iconType = 'image/svg+xml';
      else if (lower.endsWith('.png') || lower.includes('.png?')) iconType = 'image/png';
      else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.includes('.jpg?') || lower.includes('.jpeg?')) iconType = 'image/jpeg';
      const ensureLink = (relValue) => {
        let el = document.head.querySelector(`link[rel="${relValue}"]`);
        if (!el) {
          el = document.createElement('link');
          el.setAttribute('rel', relValue);
          document.head.appendChild(el);
        }
        el.setAttribute('href', finalHref);
        if (iconType) el.setAttribute('type', iconType);
        else el.removeAttribute('type');
      };
      ensureLink('icon');
      ensureLink('shortcut icon');
    } catch {}
  }, []);

  const shimmerLayerStyle = useMemo(() => ({
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.6), rgba(255,255,255,0))',
    transform: 'translateX(-100%)',
    animation: 'shopstarter-shimmer 1.3s linear infinite'
  }), []);
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('shopstarter_cart_v2');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [selected, setSelected] = useState(null);
  const [compare, setCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const toggleCompare = useCallback((id) => {
    setCompare(prev => {
      const exists = prev.includes(id);
      if (exists) return prev.filter(x => x !== id);
      if (prev.length >= 2) { push('Compare up to 2 items'); return prev; }
      return [...prev, id];
    });
  }, [push]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [headerBlend, setHeaderBlend] = useState(0);
  const headerRef = React.useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [reservedHeaderHeight, setReservedHeaderHeight] = useState(0);
  const measureHeaderHeight = useCallback(() => {
    if (!headerRef.current) return 0;
    const { height } = headerRef.current.getBoundingClientRect();
    return Math.ceil(height);
  }, []);
  const [stickyPriceOffset, setStickyPriceOffset] = useState(112);
  const stickyPriceTop = useMemo(() => `${stickyPriceOffset}px`, [stickyPriceOffset]);
  const priceAsideRef = React.useRef(null);
  const priceAsideContainerRef = React.useRef(null);
  const productGridRef = React.useRef(null);
  // Sticky filters (catalog) – robust JS fallback
  const filtersAsideRef = React.useRef(null);
  const filtersPanelRef = React.useRef(null);
  const catalogGridRef = React.useRef(null);
  const [filtersFixed, setFiltersFixed] = useState(false);
  const [filtersBox, setFiltersBox] = useState({ left: 0, width: 0 });
  const [filtersTranslate, setFiltersTranslate] = useState(0);
  const [filtersPanelHeight, setFiltersPanelHeight] = useState(0);
  useEffect(() => {
    let ticking = false;
    const measure = () => {
      const asideEl = filtersAsideRef.current;
      const panelEl = filtersPanelRef.current;
      const gridEl = catalogGridRef.current;
      if (!asideEl || !panelEl) { ticking = false; return; }
      const rect = asideEl.getBoundingClientRect();
      const docLeft = rect.left + window.scrollX;
      const width = rect.width;
      const offset = stickyPriceOffset;
      const startThreshold = offset - 2;
      const stopThreshold = offset + 8;
      const shouldFix = rect.top <= startThreshold || (filtersFixed && rect.top <= stopThreshold);
      if (shouldFix !== filtersFixed) setFiltersFixed(shouldFix);
      const newHeight = panelEl.offsetHeight;
      setFiltersPanelHeight(prev => (prev !== newHeight ? newHeight : prev));
      setFiltersBox(prev => (Math.abs(prev.left - docLeft) > 0.5 || Math.abs(prev.width - width) > 0.5 ? { left: docLeft, width } : prev));
      let translate = 0;
      if (shouldFix && gridEl) {
        const panelH = panelEl.offsetHeight;
        const fixedTopY = window.scrollY + offset;
        const bottomY = gridEl.getBoundingClientRect().bottom + window.scrollY;
        const overflow = (fixedTopY + panelH) - bottomY;
        if (overflow > 0) translate = overflow;
      }
      setFiltersTranslate(prev => (Math.abs(prev - translate) > 0.5 ? translate : prev));
      ticking = false;
    };
    const onScrollOrResize = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(measure);
      }
    };
    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [stickyPriceOffset, filtersFixed]);
  const [isDesktopWidth, setIsDesktopWidth] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false));
  const [priceRailMode, setPriceRailMode] = useState('static');
  const [priceRailBox, setPriceRailBox] = useState({ left: 0, width: 0, height: 0, bottomTop: 0, staticTop: 0, followTop: 0 });
  const priceRailModeRef = React.useRef('static');
  useLayoutEffect(() => {
    if (!isDesktopWidth || page !== 'product' || !selected) {
      setPriceRailMode('static');
      priceRailModeRef.current = 'static';
      return;
    }
    let ticking = false;
    let resizeObserver = null;
    const measure = () => {
      const asideEl = priceAsideRef.current;
      const containerEl = priceAsideContainerRef.current;
      const boundaryEl = productGridRef.current;
      const leftAnchorEl = imageContainerRef.current;
      if (!asideEl || !containerEl || !boundaryEl || !leftAnchorEl) {
        ticking = false;
        return;
      }

      const containerRect = containerEl.getBoundingClientRect();
      const boundaryRect = boundaryEl.getBoundingClientRect();
      const leftAnchorRect = leftAnchorEl.getBoundingClientRect();
      const left = containerRect.left + window.scrollX;
      const width = containerRect.width;
      const height = asideEl.offsetHeight;
      const containerTopDoc = containerRect.top + window.scrollY;
      const startTopDoc = leftAnchorRect.top + window.scrollY;
      const boundaryBottomDoc = boundaryRect.bottom + window.scrollY;
      const staticTop = Math.max(0, startTopDoc - containerTopDoc);
      const followTop = Math.max(leftAnchorRect.top, 0);
      const shouldStick = leftAnchorRect.top <= stickyPriceOffset
        || (priceRailModeRef.current === 'fixed' && leftAnchorRect.top <= stickyPriceOffset + 6);
      const hitBottom = stickyPriceOffset + height >= boundaryRect.bottom - 2;
      const bottomTop = Math.max(staticTop, boundaryBottomDoc - containerTopDoc - height);

      let nextMode = 'static';
      if (shouldStick) {
        nextMode = hitBottom ? 'bottom' : 'fixed';
      }

      priceRailModeRef.current = nextMode;
      setPriceRailMode((prev) => (prev !== nextMode ? nextMode : prev));
      setPriceRailBox((prev) => (
        Math.abs(prev.left - left) > 0.5
        || Math.abs(prev.width - width) > 0.5
        || Math.abs(prev.height - height) > 0.5
        || Math.abs(prev.bottomTop - bottomTop) > 0.5
        || Math.abs(prev.staticTop - staticTop) > 0.5
        || Math.abs(prev.followTop - followTop) > 0.5
          ? { left, width, height, bottomTop, staticTop, followTop }
          : prev
      ));
      ticking = false;
    };
    const onScrollOrResize = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(measure);
      }
    };
    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => onScrollOrResize());
      if (priceAsideRef.current) resizeObserver.observe(priceAsideRef.current);
      if (priceAsideContainerRef.current) resizeObserver.observe(priceAsideContainerRef.current);
      if (productGridRef.current) resizeObserver.observe(productGridRef.current);
      if (imageContainerRef.current) resizeObserver.observe(imageContainerRef.current);
    }
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isDesktopWidth, page, selected, stickyPriceOffset]);
  const livePriceRailHeight = priceRailBox.height || priceAsideRef.current?.offsetHeight || 0;
  const livePriceRailWidth = priceRailBox.width || priceAsideContainerRef.current?.getBoundingClientRect?.().width || 0;
  const livePriceRailLeft = priceRailBox.left || (
    priceAsideContainerRef.current
      ? (priceAsideContainerRef.current.getBoundingClientRect().left + window.scrollX)
      : 0
  );
  const priceStickyStyle = useMemo(() => {
    if (!isDesktopWidth || page !== 'product' || !selected) return {};
    if (priceRailMode === 'fixed') {
      return {
        position: 'fixed',
        top: stickyPriceOffset,
        left: livePriceRailLeft,
        width: livePriceRailWidth,
        zIndex: 40,
        boxSizing: 'border-box'
      };
    }
    if (priceRailMode === 'bottom') {
      return {
        position: 'absolute',
        top: priceRailBox.bottomTop,
        left: 0,
        width: '100%'
      };
    }
    return {
      position: 'fixed',
      top: priceRailBox.followTop,
      left: livePriceRailLeft,
      width: livePriceRailWidth,
      zIndex: 40,
      boxSizing: 'border-box'
    };
  }, [isDesktopWidth, page, selected, priceRailMode, priceRailBox.bottomTop, priceRailBox.followTop, stickyPriceOffset, livePriceRailLeft, livePriceRailWidth]);
  const priceAsideInnerStyle = useMemo(() => {
    if (!isDesktopWidth || page !== 'product' || !selected) return {};
    return {
      maxHeight: `calc(100vh - ${stickyPriceOffset + 16}px)`,
      overflowY: 'auto'
    };
  }, [isDesktopWidth, page, selected, stickyPriceOffset]);

  // Checkout sticky summary panel
  const checkoutAsideRef = React.useRef(null);
  const checkoutAsideContainerRef = React.useRef(null);
  const checkoutGridRef = React.useRef(null);
  const [checkoutRailMode, setCheckoutRailMode] = useState('static');
  const [checkoutRailBox, setCheckoutRailBox] = useState({ left: 0, width: 0, height: 0, bottomTop: 0 });
  const checkoutRailModeRef = React.useRef('static');
  useLayoutEffect(() => {
    if (!isDesktopWidth || page !== 'checkout') {
      setCheckoutRailMode('static');
      checkoutRailModeRef.current = 'static';
      return;
    }
    let ticking = false;
    let resizeObserver = null;
    const measure = () => {
      const asideEl = checkoutAsideRef.current;
      const containerEl = checkoutAsideContainerRef.current;
      const boundaryEl = checkoutGridRef.current;
      if (!asideEl || !containerEl) {
        ticking = false;
        return;
      }
      const containerRect = containerEl.getBoundingClientRect();
      const boundaryRect = (boundaryEl || containerEl).getBoundingClientRect();
      const left = containerRect.left + window.scrollX;
      const width = containerRect.width;
      const height = asideEl.offsetHeight;
      const containerTop = containerRect.top + window.scrollY;
      const boundaryBottom = boundaryRect.bottom + window.scrollY;
      const fixedTop = window.scrollY + stickyPriceOffset;
      const bottomTop = Math.max(0, boundaryBottom - containerTop - height);

      let nextMode = checkoutRailModeRef.current || 'static';
      if (nextMode === 'static') {
        if (fixedTop >= containerTop) {
          nextMode = (fixedTop + height >= boundaryBottom) ? 'bottom' : 'fixed';
        }
      } else if (nextMode === 'fixed') {
        if (fixedTop <= containerTop) {
          nextMode = 'static';
        } else if (fixedTop + height >= boundaryBottom) {
          nextMode = 'bottom';
        }
      } else {
        if (fixedTop <= containerTop) {
          nextMode = 'static';
        } else if (fixedTop + height < boundaryBottom - 20) {
          nextMode = 'fixed';
        }
      }

      checkoutRailModeRef.current = nextMode;
      setCheckoutRailMode((prev) => (prev !== nextMode ? nextMode : prev));
      setCheckoutRailBox((prev) => (
        Math.abs(prev.left - left) > 0.5
        || Math.abs(prev.width - width) > 0.5
        || Math.abs(prev.height - height) > 0.5
        || Math.abs(prev.bottomTop - bottomTop) > 0.5
          ? { left, width, height, bottomTop }
          : prev
      ));
      ticking = false;
    };
    const onScrollOrResize = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(measure);
      }
    };
    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => onScrollOrResize());
      if (checkoutAsideRef.current) resizeObserver.observe(checkoutAsideRef.current);
      if (checkoutAsideContainerRef.current) resizeObserver.observe(checkoutAsideContainerRef.current);
      if (checkoutGridRef.current) resizeObserver.observe(checkoutGridRef.current);
    }
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isDesktopWidth, page, stickyPriceOffset]);
  const liveCheckoutRailHeight = checkoutRailBox.height || checkoutAsideRef.current?.offsetHeight || 0;
  const liveCheckoutRailWidth = checkoutRailBox.width || checkoutAsideContainerRef.current?.getBoundingClientRect?.().width || 0;
  const liveCheckoutRailLeft = checkoutRailBox.left || (
    checkoutAsideContainerRef.current
      ? (checkoutAsideContainerRef.current.getBoundingClientRect().left + window.scrollX)
      : 0
  );
  const checkoutStickyStyle = useMemo(() => {
    if (!isDesktopWidth || page !== 'checkout') return {};
    if (checkoutRailMode === 'fixed') {
      return {
        position: 'fixed',
        top: stickyPriceOffset,
        left: liveCheckoutRailLeft,
        width: liveCheckoutRailWidth,
        zIndex: 40,
        boxSizing: 'border-box'
      };
    }
    if (checkoutRailMode === 'bottom') {
      return {
        position: 'absolute',
        top: checkoutRailBox.bottomTop,
        left: 0,
        width: '100%'
      };
    }
    return {
      position: 'relative',
      top: 0,
      left: 0,
      width: '100%'
    };
  }, [isDesktopWidth, page, checkoutRailMode, checkoutRailBox.bottomTop, stickyPriceOffset, liveCheckoutRailLeft, liveCheckoutRailWidth]);
  const checkoutAsideInnerStyle = useMemo(() => {
    if (!isDesktopWidth || page !== 'checkout') return {};
    return {
      maxHeight: `calc(100vh - ${stickyPriceOffset + 16}px)`,
      overflowY: 'auto'
    };
  }, [isDesktopWidth, page, stickyPriceOffset]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [activePromotions, setActivePromotions] = useState(() => {
    try {
      const raw = localStorage.getItem('shopstarter_active_promotions');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [campaignPromotion, setCampaignPromotion] = useState(null);
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [isPromoModalOpen, setPromoModalOpen] = useState(false);
  // Remember checkout details toggle
  const [rememberDetails, setRememberDetails] = useState(() => {
    try { return localStorage.getItem('shopstarter_remember') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('shopstarter_remember', rememberDetails ? 'true' : 'false'); } catch {}
  }, [rememberDetails]);
  // Simple confetti flag for celebratory moments (e.g., order placed)
  const [showConfetti, setShowConfetti] = useState(false);
  // Quick view modal state
  const [quickLookOpen, setQuickLookOpen] = useState(false);
  const [quickLook, setQuickLook] = useState(null);
  const normalizedPromotions = useMemo(() => {
    const now = Date.now();
    return (Array.isArray(activePromotions) ? activePromotions : [])
      .map((promotion) => {
        let displayPages = [];
        try {
          const parsed = promotion?.display_pages ? JSON.parse(promotion.display_pages) : [];
          displayPages = Array.isArray(parsed)
            ? parsed.map((entry) => String(entry || '').trim().toLowerCase()).filter(Boolean)
            : [];
        } catch {
          displayPages = [];
        }
        const startTime = promotion?.start_date ? new Date(promotion.start_date).getTime() : null;
        const endTime = promotion?.end_date ? new Date(promotion.end_date).getTime() : null;
        const isLive = (!startTime || startTime <= now) && (!endTime || endTime >= now) && Number(promotion?.active ?? 1) === 1;
        return {
          ...promotion,
          _endTime: endTime,
          _displayPages: displayPages,
          _showOnOffers: Number(promotion?.show_on_offers ?? 1) === 1,
          _showAsPopup: Number(promotion?.show_as_popup ?? 0) === 1,
          _showAsHeader: Number(promotion?.show_as_header ?? 0) === 1,
          _isLive: isLive
        };
      })
      .filter((promotion) => {
        if (!promotion._isLive) return false;
        if (!promotion._displayPages || promotion._displayPages.length === 0) return true;
        return promotion._displayPages.includes(String(page || '').toLowerCase());
      })
      .sort((a, b) => {
        if (a._endTime && b._endTime) return a._endTime - b._endTime;
        if (a._endTime) return -1;
        if (b._endTime) return 1;
        return 0;
      });
  }, [activePromotions, page]);
  const primaryPromotion = normalizedPromotions[0] || null;
  const offersPromotions = useMemo(
    () => normalizedPromotions.filter((promotion) => promotion._showOnOffers && Boolean(String(promotion.image_url || '').trim())),
    [normalizedPromotions]
  );
  const headerBarPromotion = useMemo(
    () => normalizedPromotions.find((promotion) => promotion._showAsHeader) || null,
    [normalizedPromotions]
  );
  const popupPromotion = useMemo(
    () => normalizedPromotions.find((promotion) => promotion._showAsPopup && Boolean(String(promotion.image_url || '').trim())) || null,
    [normalizedPromotions]
  );
  const promoModalRef = React.useRef(null);
  const promoCloseRef = React.useRef(null);
  const [showConsent, setShowConsent] = useState(() => {
    try { return !localStorage.getItem('shopstarter_cookie_consent'); } catch { return true; }
  });
  // Auto-trigger confetti briefly when landing on the thanks page
  useEffect(() => {
    if (page === 'thanks' && !isLiteMode) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(t);
    }
  }, [page, isLiteMode]);

  const heroBanners = useMemo(() => {
    const configured = Array.isArray(settings.heroBanners)
      ? settings.heroBanners.filter(banner => {
          if (!banner) return false;
          const isActive = banner.active ?? true;
          // Validate that image URL is not empty and is a valid string
          const hasValidImage = typeof banner.image === 'string' && banner.image.trim().length > 0;
          return isActive && hasValidImage;
        })
      : [];
    return configured;
  }, [settings.heroBanners]);
  const hasMultipleHeroBanners = heroBanners.length > 1;

  useEffect(() => {
    setBannerIdx(0);
  }, [heroBanners.length]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    if (typeof document === 'undefined') return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [mobileFiltersOpen]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    if (page !== 'catalog') {
      setMobileFiltersOpen(false);
    }
  }, [mobileFiltersOpen, page]);

  useEffect(() => {
    if (mobileFiltersOpen && isDesktopWidth) {
      setMobileFiltersOpen(false);
    }
  }, [isDesktopWidth, mobileFiltersOpen]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setMobileFiltersOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mobileFiltersOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return undefined;
    const updateMode = () => setIsLiteMode(detectPerformanceLiteMode());
    updateMode();

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    connection?.addEventListener?.('change', updateMode);
    window.addEventListener('resize', updateMode);
    window.addEventListener('orientationchange', updateMode);
    window.addEventListener('online', updateMode);
    window.addEventListener('offline', updateMode);
    window.addEventListener('storage', updateMode);

    return () => {
      connection?.removeEventListener?.('change', updateMode);
      window.removeEventListener('resize', updateMode);
      window.removeEventListener('orientationchange', updateMode);
      window.removeEventListener('online', updateMode);
      window.removeEventListener('offline', updateMode);
      window.removeEventListener('storage', updateMode);
    };
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof performance === 'undefined') return undefined;
    if (isLiteMode || !isLikelyMobileDevice()) return undefined;
    if (showStartupLoader) return undefined;
    let cancelled = false;
    let rafId = 0;
    let kickoffTimer = 0;
    const deltas = [];
    let previous = 0;
    let samples = 0;
    const sampleTarget = 42;

    const complete = () => {
      if (cancelled || deltas.length < 10) return;
      const sorted = deltas.slice().sort((a, b) => a - b);
      const avg = deltas.reduce((sum, value) => sum + value, 0) / deltas.length;
      const p90 = sorted[Math.floor(sorted.length * 0.9)] || avg;
      if (avg > 21 || p90 > 34) {
        setIsLiteMode(true);
      }
    };

    const tick = (now) => {
      if (cancelled) return;
      if (previous > 0) deltas.push(now - previous);
      previous = now;
      samples += 1;
      if (samples >= sampleTarget) {
        complete();
        return;
      }
      rafId = window.requestAnimationFrame(tick);
    };

    kickoffTimer = window.setTimeout(() => {
      if (cancelled) return;
      rafId = window.requestAnimationFrame(tick);
    }, 650);

    return () => {
      cancelled = true;
      if (kickoffTimer) window.clearTimeout(kickoffTimer);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [isLiteMode, showStartupLoader]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const tileSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240" fill="none">
  <rect width="240" height="240" fill="#fef9f3"/>
  <g font-family="Segoe UI Emoji,Apple Color Emoji,Noto Color Emoji,sans-serif" font-size="48">
    <text x="30" y="60">👜</text>
    <text x="134" y="64">👛</text>
    <text x="28" y="150">🎒</text>
    <text x="136" y="148">💼</text>
    <text x="90" y="110">🛍️</text>
    <text x="188" y="206">👝</text>
    <text x="96" y="204">🧳</text>
    <text x="196" y="118">👜</text>
  </g>
</svg>`;
    const encodedTile = encodeURIComponent(tileSvg)
      .replace(/'/g, '%27')
      .replace(/"/g, '%22')
      .replace(/%0A/g, '');

    let styleTag = document.getElementById('shopstarter-animated-bg');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'shopstarter-animated-bg';
      styleTag.innerHTML = `:root {
  --pv-bg-base: #fef9f3;
  --pv-emoji-tile: url("data:image/svg+xml,${encodedTile}");
  --pv-tile-size: 240px;
}
body {
  min-height: 100vh;
  background: var(--pv-bg-base);
  overflow-x: hidden;
  position: relative;
  color-scheme: light;
}
#shopstarter-parallax {
  position: fixed;
  inset: -18vh -18vw;
  pointer-events: none;
  z-index: -1;
  overflow: hidden;
}
#shopstarter-parallax .pv-layer {
  position: absolute;
  inset: -20%;
  background-image: var(--pv-emoji-tile);
  background-size: var(--pv-tile-size) var(--pv-tile-size);
  background-repeat: repeat;
  animation: shopstarter-parallax-shift 120s linear infinite;
  opacity: 0.18;
  transform: translateZ(0);
}
@keyframes shopstarter-parallax-shift {
  0% { background-position: 0 0; }
  100% { background-position: -480px -480px; }
}`;
      document.head.appendChild(styleTag);
    }

    const existingParallax = document.getElementById('shopstarter-parallax');
    if (existingParallax) {
      existingParallax.remove();
    }

    const parallaxRoot = document.createElement('div');
    parallaxRoot.id = 'shopstarter-parallax';
    parallaxRoot.setAttribute('aria-hidden', 'true');

    const layer = document.createElement('div');
    layer.className = 'pv-layer';
    parallaxRoot.appendChild(layer);

    document.body.appendChild(parallaxRoot);

    return () => {
      if (parallaxRoot.isConnected) {
        parallaxRoot.remove();
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (typeof window === 'undefined') return;
      const y = window.scrollY || 0;
      setHasScrolled(y > 24);
      const blend = Math.max(0, Math.min(1, y / 96));
      setHeaderBlend(blend);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event) => setIsDesktopWidth(event.matches);
    setIsDesktopWidth(media.matches);
    if (media.addEventListener) media.addEventListener('change', handleChange);
    else media.addListener(handleChange);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', handleChange);
      else media.removeListener(handleChange);
    };
  }, []);

  // removed legacy priceDock-based sticky implementation

  const syncStickyPriceOffset = useCallback(() => {
    const baseHeight = Math.max(measureHeaderHeight() || 0, 88);
    const nextOffset = baseHeight + 24;
    setStickyPriceOffset((prev) => (Math.abs(prev - nextOffset) > 0.5 ? nextOffset : prev));
  }, [measureHeaderHeight]);

  useLayoutEffect(() => {
    setHeaderHeight(measureHeaderHeight());
  }, [measureHeaderHeight]);

  useLayoutEffect(() => {
    const next = measureHeaderHeight();
    setReservedHeaderHeight((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
  }, [measureHeaderHeight, page, mobileOpen, isDesktopWidth]);

  useLayoutEffect(() => {
    syncStickyPriceOffset();
  }, [syncStickyPriceOffset, page, mobileOpen, isDesktopWidth]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => syncStickyPriceOffset();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [syncStickyPriceOffset]);

  useLayoutEffect(() => {
    const node = headerRef.current;
    if (!node) return undefined;
    const syncHeight = () => setHeaderHeight(measureHeaderHeight());
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const next = Math.ceil(entry.contentRect.height);
          setHeaderHeight((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
        });
      });
      observer.observe(node);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', syncHeight);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', syncHeight);
      }
      if (observer) observer.disconnect();
    };
  }, [measureHeaderHeight]);

  useEffect(() => {
    const next = measureHeaderHeight();
    setHeaderHeight((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
  }, [measureHeaderHeight, hasScrolled, mobileOpen, page]);

  useEffect(() => {
    setReservedHeaderHeight((prev) => (headerHeight > prev + 0.5 ? headerHeight : prev));
  }, [headerHeight]);

  useEffect(() => {
    if (!hasMultipleHeroBanners || isLiteMode) return;
    const interval = setInterval(() => {
      setBannerIdx(idx => {
        if (heroBanners.length <= 1) return 0;
        return (idx + 1) % heroBanners.length;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [heroBanners.length, hasMultipleHeroBanners, isLiteMode]);
  const currentBanner = useMemo(() => {
    if (!heroBanners.length) return DEFAULT_SETTINGS.heroBanners[0];
    const normalized = ((bannerIdx % heroBanners.length) + heroBanners.length) % heroBanners.length;
    return heroBanners[normalized];
  }, [heroBanners, bannerIdx]);
  const [bannerImgSrc, setBannerImgSrc] = useState('');
  const [bannerLoaded, setBannerLoaded] = useState(false);
  const bannerImgRef = React.useRef(null);
  const [heroBgSrc, setHeroBgSrc] = useState('');
  const [heroOverlaySrc, setHeroOverlaySrc] = useState('');
  const [heroAltSrc, setHeroAltSrc] = useState('');
  const [heroOverlayOpacity, setHeroOverlayOpacity] = useState(0);
  const heroFadeTimer = React.useRef(null);
  useEffect(() => {
    // Choose primary image immediately and compute an alternate path for local uploads
    const raw = currentBanner?.image || '';
    const primary = resolveMediaUrl(raw);
    const alt = primary.startsWith('/shopapi/uploads/')
      ? primary.replace('/shopapi', '')
      : (primary.startsWith('/uploads/') ? `/shopapi${primary}` : '');
    setHeroAltSrc(alt);
    setBannerImgSrc(primary);
  }, [currentBanner?.image, resolveMediaUrl]);

  // Preload neighbor hero images for smoother transitions
  useEffect(() => {
    if (!hasMultipleHeroBanners) return;
    const len = heroBanners.length;
    if (len <= 1) return;
    const prev = heroBanners[(bannerIdx - 1 + len) % len];
    const next = heroBanners[(bannerIdx + 1) % len];
    const toPreload = [prev?.image, next?.image]
      .filter((raw) => isImageMedia(raw))
      .map((raw) => (raw ? resolveMediaUrl(raw) : ''))
      .filter(Boolean);
    toPreload.forEach((src) => {
      try { const img = new Image(); img.src = src; } catch {}
    });
  }, [bannerIdx, heroBanners, hasMultipleHeroBanners, resolveMediaUrl]);

  // Reset loaded state and ensure any stale inline display:none is cleared when src changes
  useEffect(() => {
    setBannerLoaded(false);
    if (bannerImgRef.current) {
      try { bannerImgRef.current.style.display = ''; } catch {}
      try { delete bannerImgRef.current.dataset.altTried; } catch {}
    }
  }, [bannerImgSrc]);

  // Crossfade: when bannerImgSrc changes, fade in overlay then commit to bg
  useEffect(() => {
    if (!bannerImgSrc) return;
    if (!heroBgSrc) {
      setHeroBgSrc(bannerImgSrc);
      setHeroOverlaySrc('');
      setHeroOverlayOpacity(0);
      return;
    }
    setHeroOverlaySrc(bannerImgSrc);
    setHeroOverlayOpacity(0);
  }, [bannerImgSrc]);

  useEffect(() => {
    return () => { if (heroFadeTimer.current) clearTimeout(heroFadeTimer.current); };
  }, []);

  const marketingCurrencyCode = useMemo(() => {
    const raw = String(settings.currencyCode || settings.currency || 'BDT').trim();
    if (/^[A-Za-z]{3}$/.test(raw)) return raw.toUpperCase();
    const bySymbol = {
      '৳': 'BDT',
      '$': 'USD',
      '€': 'EUR',
      '£': 'GBP',
      '¥': 'JPY',
      '₹': 'INR',
      '₨': 'PKR'
    };
    return bySymbol[raw] || 'BDT';
  }, [settings.currencyCode, settings.currency]);

  const dispatchMetaPixelEvent = useCallback((name, payload = {}) => {
    try {
      if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
      const items = Array.isArray(payload.items) ? payload.items : [];
      const normalizedContents = items.length > 0
        ? items.map((item) => ({
            id: String(item.productId || item.id || ''),
            quantity: Math.max(1, Number(item.qty || item.quantity || 1) || 1),
            item_price: Math.max(0, Number(item.price || 0) || 0)
          })).filter((item) => item.id)
        : (payload.product_id ? [{
            id: String(payload.product_id),
            quantity: Math.max(1, Number(payload.qty || 1) || 1),
            item_price: Math.max(0, Number(payload.price || 0) || 0)
          }] : []);
      const contentIds = normalizedContents.map((item) => item.id);
      const numItems = normalizedContents.reduce((sum, item) => sum + item.quantity, 0);
      switch (name) {
        case 'view_product':
          window.fbq('track', 'ViewContent', {
            content_ids: contentIds,
            content_name: String(payload.title || ''),
            content_type: 'product',
            contents: normalizedContents,
            currency: marketingCurrencyCode,
            value: Math.max(0, Number(payload.price || 0) || 0)
          });
          break;
        case 'add_to_cart':
          window.fbq('track', 'AddToCart', {
            content_ids: contentIds,
            content_name: String(payload.title || ''),
            content_type: 'product',
            contents: normalizedContents,
            currency: marketingCurrencyCode,
            value: Math.max(0, Number(payload.price || 0) || 0) * Math.max(1, Number(payload.qty || 1) || 1),
            num_items: Math.max(1, Number(payload.qty || numItems || 1) || 1)
          });
          break;
        case 'initiate_checkout':
          window.fbq('track', 'InitiateCheckout', {
            content_ids: contentIds,
            content_type: 'product',
            contents: normalizedContents,
            currency: marketingCurrencyCode,
            value: Math.max(0, Number(payload.total || 0) || 0),
            num_items: numItems
          });
          break;
        case 'purchase':
          window.fbq('track', 'Purchase', {
            content_ids: contentIds,
            content_type: 'product',
            contents: normalizedContents,
            currency: marketingCurrencyCode,
            value: Math.max(0, Number(payload.total || 0) || 0),
            num_items: numItems
          });
          break;
        default:
          break;
      }
    } catch {}
  }, [marketingCurrencyCode]);

  // Lightweight analytics event dispatcher
  const trackEvent = useCallback((name, payload = {}) => {
    try {
      if (typeof window !== 'undefined') {
        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event: name, ...payload });
        } else if (process.env.NODE_ENV !== 'production') {
          console.debug('[track]', name, payload);
        }
      }
      dispatchMetaPixelEvent(name, payload);
    } catch {}
  }, [dispatchMetaPixelEvent]);
  const isDefaultHeroBanner = String(currentBanner?.id || '').startsWith('default-');
  const bannerHeadline = currentBanner?.headline && currentBanner.headline.trim().length > 0
    ? currentBanner.headline.trim()
    : isDefaultHeroBanner
      ? (settings.siteName ? `Welcome to ${settings.siteName}` : 'Welcome to ItemSmith')
      : '';
  const bannerSubheadline = currentBanner?.subheadline && currentBanner.subheadline.trim().length > 0
    ? currentBanner.subheadline
    : (isDefaultHeroBanner
      ? 'Discover standout products, fresh arrivals, and practical finds for everyday life.'
      : '');
  const bannerCtaLabel = currentBanner?.ctaLabel && currentBanner.ctaLabel.trim().length > 0
    ? currentBanner.ctaLabel.trim()
    : '';
  const bannerLink = currentBanner?.href && currentBanner.href.trim().length > 0
    ? currentBanner.href.trim()
    : '';
  const bannerImageLink = currentBanner?.imageHref && currentBanner.imageHref.trim().length > 0
    ? currentBanner.imageHref.trim()
    : bannerLink;

  const heroHasOverlayContent = useMemo(() => {
    const rawHeadline = bannerHeadline || currentBanner?.headline || '';
    const rawSub = bannerSubheadline || currentBanner?.subheadline || '';
    const rawCta = bannerCtaLabel || currentBanner?.ctaLabel || '';
    return Boolean(String(rawHeadline).trim() || String(rawSub).trim() || String(rawCta).trim());
  }, [bannerHeadline, bannerSubheadline, bannerCtaLabel, currentBanner?.headline, currentBanner?.subheadline, currentBanner?.ctaLabel]);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const configuredPixelId = String(settings.metaPixelId || '').trim();
      if (!configuredPixelId) return;

      if (typeof window.fbq !== 'function') {
        const fbqStub = function fbqStub() {
          fbqStub.callMethod ? fbqStub.callMethod.apply(fbqStub, arguments) : fbqStub.queue.push(arguments);
        };
        fbqStub.push = fbqStub;
        fbqStub.loaded = true;
        fbqStub.version = '2.0';
        fbqStub.queue = [];
        window.fbq = fbqStub;
        if (!window._fbq) window._fbq = fbqStub;
      }

      if (!document.getElementById('meta-pixel-script')) {
        const script = document.createElement('script');
        script.id = 'meta-pixel-script';
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          document.head.appendChild(script);
        }
      }

      if (window.__itemsmithMetaPixelInitialized !== configuredPixelId) {
        window.fbq('init', configuredPixelId);
        window.__itemsmithMetaPixelInitialized = configuredPixelId;
        window.__itemsmithMetaPixelLastPageView = '';
      }
    } catch {}
  }, [settings.metaPixelId]);

  // SEO: Organization JSON-LD and dynamic meta
  useEffect(() => {
    try {
      const siteName = settings.siteName || 'ItemSmith';
      const siteTitle = settings.siteTitle || DEFAULT_SETTINGS.siteTitle;
      document.title = siteTitle;
      setMeta('og:site_name', siteName, { property: true });
      setMeta('og:type', 'website', { property: true });
      setMeta('twitter:card', 'summary_large_image');
      const baseDescription = settings.tagline || bannerSubheadline || 'Discover standout products, seasonal drops, and everyday essentials from ItemSmith.';
      const baseImage = resolveMediaUrl(settings.shareImageUrl || settings.logoUrl || '/logo.png');
      setMeta('og:title', siteTitle, { property: true });
      setMeta('twitter:title', siteTitle);
      setMeta('description', baseDescription);
      setMeta('og:description', baseDescription, { property: true });
      setMeta('twitter:description', baseDescription);
      if (baseImage) {
        setMeta('og:image', baseImage, { property: true });
        setMeta('twitter:image', baseImage);
      }
      // Preconnect to common external origins used in images/maps
      setPreconnect('https://images.pexels.com', 'preconnect-pexels');
      setPreconnect('https://www.google.com', 'preconnect-google');
      if (settings.metaPixelId) {
        setPreconnect('https://connect.facebook.net', 'preconnect-meta-pixel');
      }
      const org = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteName,
        url: typeof window !== 'undefined' ? window.location.origin : '',
        logo: settingsReady ? (resolveMediaUrl(settings.logoUrl) || undefined) : undefined
      };
      setJsonLd('ld-org', org);
      setJsonLd('ld-website', {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteName,
        url: typeof window !== 'undefined' ? window.location.origin : ''
      });
    } catch {}
  }, [settings.siteName, settings.siteTitle, settings.logoUrl, settings.shareImageUrl, settings.tagline, settings.metaPixelId, bannerSubheadline, setJsonLd, setMeta, resolveMediaUrl, setPreconnect, settingsReady]);

  useEffect(() => {
    const configured = String(settings?.faviconUrl || '').trim();
    const favicon = configured ? resolveMediaUrl(configured) : '/logo.png';
    setFaviconHref(favicon);
  }, [settings?.faviconUrl, resolveMediaUrl, setFaviconHref]);

  useEffect(() => {
    try {
      const siteName = settings.siteName || 'ItemSmith';
      const siteTitle = settings.siteTitle || DEFAULT_SETTINGS.siteTitle;
      if (page === 'product' && selected) return;
      const indexableRobots = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
      const noindexRobots = 'noindex,nofollow,noarchive';
      const genericDescriptionMap = {
        catalog: `Browse the full ${siteName} catalog of featured products, new arrivals, and everyday picks.`,
        cart: `Review the products currently in your ${siteName} cart.`,
        checkout: `Complete your order securely with ${siteName}.`,
        thanks: `Your ${siteName} order has been received.`,
        track: `Track the status of your ${siteName} order.`,
        offers: `Explore current offers and promotions from ${siteName}.`,
        help: `Get support, shipping information, and answers from ${siteName}.`,
        terms: `Read the ${siteName} terms and conditions.`,
        privacy: `Review the ${siteName} privacy policy.`,
        cookies: `Learn how ${siteName} uses cookies and similar technologies.`,
        returns: `See the ${siteName} returns and refunds policy.`,
        order: `Review ${siteName} order cancellation information.`,
        shipping: `Learn about ${siteName} shipping and delivery policies.`,
        sitemap: `Browse the ${siteName} page index.`,
        notfound: `The requested ${siteName} page could not be found.`
      };

      if (page === 'legal') {
        const legalTitle = String(legalPage?.title || '').trim();
        const title = legalTitle ? `${legalTitle} - ${siteName}` : `Legal - ${siteName}`;
        const legalDescription = String(legalPage?.content || '')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 180) || `Legal information for ${siteName}.`;
        document.title = title;
        setMeta('description', legalDescription);
        setMeta('og:description', legalDescription, { property: true });
        setMeta('twitter:description', legalDescription);
        setMeta('og:title', title, { property: true });
        setMeta('twitter:title', title);
        setMeta('og:type', 'article', { property: true });
        setMeta('robots', indexableRobots);
        return;
      }

      const pageTitleMap = {
        home: siteTitle,
        catalog: `Catalog - ${siteName}`,
        cart: `Cart - ${siteName}`,
        checkout: `Checkout - ${siteName}`,
        thanks: `Order Confirmed - ${siteName}`,
        track: `Track Order - ${siteName}`,
        offers: `Offers - ${siteName}`,
        help: `Help Center - ${siteName}`,
        terms: `Terms & Conditions - ${siteName}`,
        privacy: `Privacy Policy - ${siteName}`,
        cookies: `Cookie Policy - ${siteName}`,
        returns: `Returns & Refunds - ${siteName}`,
        order: `Order Cancellation - ${siteName}`,
        shipping: `Shipping & Delivery - ${siteName}`,
        sitemap: `Sitemap - ${siteName}`,
        notfound: `Page Not Found - ${siteName}`
      };
      const title = pageTitleMap[page] || siteName;
      const description = genericDescriptionMap[page] || (settings.tagline || bannerSubheadline || `Explore products and offers from ${siteName}.`);
      const robotsContent = ['cart', 'checkout', 'thanks', 'track', 'sitemap', 'notfound'].includes(page)
        ? noindexRobots
        : indexableRobots;
      document.title = title;
      setMeta('description', description);
      setMeta('og:description', description, { property: true });
      setMeta('twitter:description', description);
      setMeta('og:title', title, { property: true });
      setMeta('twitter:title', title);
      setMeta('og:type', 'website', { property: true });
      setMeta('robots', robotsContent);
    } catch {}
  }, [page, selected, settings.siteName, settings.siteTitle, settings.tagline, bannerSubheadline, legalPage?.title, legalPage?.content, setMeta]);

  // SEO: Canonical + og:url updates per navigation
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const { origin, pathname, search } = window.location;
      const canonicalUrl = `${origin}${pathname}${search}`;
      setCanonical(canonicalUrl);
      setMeta('og:url', canonicalUrl, { property: true });
    } catch {}
  }, [page, selected, setCanonical, setMeta]);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
      const currentPath = `${window.location.pathname}${window.location.search}`;
      if (window.__itemsmithMetaPixelLastPageView === currentPath) return;
      window.__itemsmithMetaPixelLastPageView = currentPath;
      window.fbq('track', 'PageView');
    } catch {}
  }, [page, selected, settings.metaPixelId]);

  // Product page state: image viewing, selected variant, and purchasable product snapshot
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const selectedVariantProductIdRef = React.useRef('');
  const selectedActiveVariants = useMemo(
    () => (selected?.variants || []).filter((variant) => variant.active !== false),
    [selected]
  );
  const baseModelHidden = !!selected?.hideBaseModel && selectedActiveVariants.length > 0;
  const selectedVariant = useMemo(() => {
    if (!selected || selectedActiveVariants.length === 0) return null;
    if (selectedVariantId === BASE_PRODUCT_OPTION_ID) return null;
    return selectedActiveVariants.find((variant) => variant.id === selectedVariantId) || null;
  }, [selected, selectedActiveVariants, selectedVariantId]);
  const selectedPurchaseProduct = useMemo(() => {
    if (!selected) return null;
    if (!selectedVariant) {
      return {
        ...selected,
        productId: selected.id,
        variantId: null,
        variantLabel: null,
        variantSku: null,
        optionResolved: !selected.hasVariants || (selectedVariantId === BASE_PRODUCT_OPTION_ID && !baseModelHidden),
        price: Number(selected.baseDisplayPrice ?? selected.basePrice ?? selected.price) || 0,
        originalPrice: Number(selected.baseOriginalPrice) || (!selected.hasVariants && (Number(selected.originalPrice) || null)),
        stock: Math.max(0, Number(selected.baseStock ?? selected.stock) || 0),
        img: selected.img || '',
        images: Array.isArray(selected.images) ? selected.images : parseStringArrayInput(selected.images),
        description: selected.shortDescription || selected.description || '',
      };
    }
    const variantImages = Array.isArray(selectedVariant.images) ? selectedVariant.images : parseStringArrayInput(selectedVariant.images);
    return {
      ...selected,
      productId: selected.id,
      variantId: selectedVariant.id,
      variantLabel: selectedVariant.label,
      variantSku: selectedVariant.sku || null,
      optionResolved: true,
      price: Number(selectedVariant.price) || 0,
      originalPrice: Number(selectedVariant.originalPrice) || null,
      stock: Math.max(0, Number(selectedVariant.stock) || 0),
      img: selectedVariant.img || selected.img || '',
      images: variantImages.length > 0 ? variantImages : (Array.isArray(selected.images) ? selected.images : parseStringArrayInput(selected.images)),
      description: selectedVariant.description || selected.shortDescription || selected.description || '',
    };
  }, [selected, selectedVariant, selectedVariantId, baseModelHidden]);
  const productOptionChoices = useMemo(() => {
    if (!selected?.hasVariants || selectedActiveVariants.length === 0) return [];
    return [
      ...(!baseModelHidden ? [{
        id: BASE_PRODUCT_OPTION_ID,
        label: 'Base model',
        price: Number(selected.baseDisplayPrice ?? selected.basePrice ?? selected.price) || 0,
        originalPrice: Number(selected.baseOriginalPrice) || null,
        sku: '',
        stock: Math.max(0, Number(selected.baseStock ?? selected.stock) || 0)
      }] : []),
      ...selectedActiveVariants.map((variant) => ({
        id: variant.id,
        label: variant.label,
        price: Number(variant.price) || 0,
        originalPrice: Number(variant.originalPrice) || null,
        sku: variant.sku || '',
        stock: Math.max(0, Number(variant.stock) || 0)
      }))
    ];
  }, [selected, selectedActiveVariants, baseModelHidden]);

  // SEO: Product JSON-LD + meta when on product page
  useEffect(() => {
    if (page === 'product' && selected) {
      try {
        const name = selected.title || 'Product';
        document.title = `${name} - ${settings.siteName || 'ItemSmith'}`;
        const image = resolveMediaUrl(selectedPurchaseProduct?.img || selected.img || (selectedPurchaseProduct?.images ? (Array.isArray(selectedPurchaseProduct.images) ? selectedPurchaseProduct.images[0] : (parseStringArrayInput(selectedPurchaseProduct.images)[0] || '')) : ''));
        const descSource = selectedPurchaseProduct?.description || selected.shortDescription || selected.description || `Discover this featured product from ${settings.siteName || 'ItemSmith'}.`;
        const description = String(descSource).replace(/\s+/g, ' ').trim().slice(0, 180);
        if (description) {
          setMeta('description', description);
          setMeta('og:description', description, { property: true });
          setMeta('twitter:description', description);
        }
        setMeta('robots', 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1');
        setMeta('og:type', 'product', { property: true });
        setMeta('og:title', name, { property: true });
        setMeta('og:image', image, { property: true });
        setMeta('twitter:title', name);
        setMeta('twitter:image', image);
        const productLd = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name,
          image: [image].filter(Boolean),
          brand: settings.siteName || 'ItemSmith',
          description,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          offers: {
            '@type': 'Offer',
            priceCurrency: settings.currency || 'BDT',
            price: String((selectedPurchaseProduct?.price ?? selected.displayPrice ?? selected.price) || ''),
            availability: (((selectedPurchaseProduct?.stock ?? selected.displayStock ?? selected.stock) || 0) > 0) ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
          }
        };
        setJsonLd('ld-product', productLd);
      } catch {}
    } else {
      removeEl('ld-product');
    }
  }, [page, selected, selectedPurchaseProduct, settings.currency, settings.siteName, setJsonLd, setMeta, removeEl, resolveMediaUrl]);

  // SEO: Home and Offers meta; Breadcrumb on product
  useEffect(() => {
    try {
      if (page === 'home') {
        const title = bannerHeadline || (settings.siteName ? `Welcome to ${settings.siteName}` : 'Welcome');
        const image = resolveMediaUrl(settings.shareImageUrl || '') || heroBgSrc || bannerImgSrc || resolveMediaUrl(currentBanner?.image) || resolveMediaUrl(settings.logoUrl || '/logo.png');
        const description = (bannerSubheadline || currentBanner?.subheadline || settings.tagline || 'Discover standout products, fresh arrivals, and practical finds from ItemSmith.').trim();
        setMeta('og:title', title, { property: true });
        setMeta('twitter:title', title);
        if (description) {
          setMeta('description', description);
          setMeta('og:description', description, { property: true });
          setMeta('twitter:description', description);
        }
        if (image) {
          setMeta('og:image', image, { property: true });
          setMeta('twitter:image', image);
        }
      } else if (page === 'offers') {
        const promo = selectedPromotion || offersPromotions[0] || primaryPromotion || null;
        const title = promo ? (promo.title || promo.banner_text || 'Offers') : 'Offers';
        const description = promo?.description || promo?.text || 'Limited-time deals on this week’s best-sellers.';
        setMeta('og:title', title, { property: true });
        setMeta('twitter:title', title);
        if (description) {
          setMeta('description', description);
          setMeta('og:description', description, { property: true });
          setMeta('twitter:description', description);
        }
        const img = promo && (promo.image_url || promo.imageUrl);
        const image = img ? resolveMediaUrl(img) : (resolveMediaUrl(settings.shareImageUrl || '') || heroBgSrc || bannerImgSrc || resolveMediaUrl(currentBanner?.image) || resolveMediaUrl(settings.logoUrl || '/logo.png'));
        if (image) {
          setMeta('og:image', image, { property: true });
          setMeta('twitter:image', image);
        }
      }
    } catch {}
  }, [page, selectedPromotion, offersPromotions, primaryPromotion, bannerHeadline, heroBgSrc, bannerImgSrc, currentBanner?.image, settings.shareImageUrl, settings.logoUrl, settings.tagline, setMeta, resolveMediaUrl]);

  useEffect(() => {
    if (page === 'product' && selected) {
      try {
        const crumbs = [
          { name: 'Home', item: '/' },
          { name: 'Catalog', item: '/catalog' },
          ...(selected.category ? [{ name: selected.category, item: '/catalog' }] : []),
          { name: selected.title || 'Product', item: typeof window !== 'undefined' ? window.location.pathname : '/product' }
        ];
        const ld = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.item }))
        };
        setJsonLd('ld-breadcrumb', ld);
      } catch {}
    } else {
      removeEl('ld-breadcrumb');
    }
  }, [page, selected, setJsonLd, removeEl]);

  const handleBannerCtaClick = useCallback(() => {
    if (!bannerLink) return;
    trackEvent('promo_click', { href: bannerLink, title: bannerCtaLabel || bannerHeadline });
    if (bannerLink.startsWith('/')) {
      const routeMap = {
        '/': 'home',
        '/catalog': 'catalog',
        '/cart': 'cart',
        '/checkout': 'checkout',
        '/offers': 'offers',
        '/track': 'track',
        '/terms': 'terms',
        '/privacy': 'privacy',
        '/cookies': 'cookies',
        '/help': 'help'
      };
      const [pathOnly] = bannerLink.split('?');
      const trimmed = (pathOnly || bannerLink).replace(/\/+$/, '') || '/';
      const target = routeMap[trimmed];
      if (target) {
        if (target === 'catalog') {
          // Preserve query string for filters
          window.history.pushState({ page: 'catalog' }, '', bannerLink);
          setPage('catalog');
          return;
        }
        goto(target);
        return;
      }
      window.location.assign(bannerLink);
    } else {
      window.open(bannerLink, '_blank', 'noopener,noreferrer');
    }
  }, [bannerLink]);
  // Last order info for Thank You page
  const [lastOrderDisplayId, setLastOrderDisplayId] = useState(null);
  // Buy Now flow control
  const [isBuyNow, setIsBuyNow] = useState(false);
  const [buyNowCheckoutItems, setBuyNowCheckoutItems] = useState([]);
  const [showDiscardBuyNowDialog, setShowDiscardBuyNowDialog] = useState(false);
  // Tracking state
  const [trackingOrderId, setTrackingOrderId] = useState(() => {
    try {
      return extractTrackingIdFromPath(window.location.pathname);
    } catch {
      return '';
    }
  });
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingError, setTrackingError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [pendingAutoTrackId, setPendingAutoTrackId] = useState(() => {
    try {
      return extractTrackingIdFromPath(window.location.pathname);
    } catch {
      return '';
    }
  });

  useEffect(() => {
    const selectedProductId = selected?.id ? String(selected.id) : '';
    const productChanged = selectedVariantProductIdRef.current !== selectedProductId;
    selectedVariantProductIdRef.current = selectedProductId;

    if (!selected || selectedActiveVariants.length === 0) {
      if (selectedVariantId !== '') setSelectedVariantId('');
      return;
    }
    if (productChanged) {
      if (selectedVariantId !== '') setSelectedVariantId('');
      return;
    }
    if (
      selectedVariantId === ''
      || selectedVariantId === BASE_PRODUCT_OPTION_ID
      || selectedActiveVariants.some((variant) => variant.id === selectedVariantId)
    ) return;
    setSelectedVariantId('');
  }, [selected, selectedActiveVariants, selectedVariantId]);

  const setProductImage = useCallback((url, nextSelectionId = null) => {
    if (selected?.hasVariants && typeof nextSelectionId === 'string') {
      setSelectedVariantId(nextSelectionId);
    }
    setCurrentImage(url || null);
  }, [selected?.hasVariants]);
  const detailTouchRef = React.useRef({ x: 0, y: 0, t: 0, swiping: false });
  const detailGallery = useMemo(() => {
    try {
      if (!selected) return [];
      const entries = [];
      const seen = new Set();
      const addEntry = (url, selectionId = null, label = '', forceSelection = false) => {
        const cleanUrl = String(url || '').trim();
        if (!cleanUrl) return;
        const key = `${selectionId || 'general'}::${cleanUrl}`;
        if (seen.has(key)) return;
        seen.add(key);
        entries.push({ url: cleanUrl, selectionId, label, forceSelection });
      };

      const baseImages = [selected.img, ...(Array.isArray(selected.images) ? selected.images : parseStringArrayInput(selected.images))].filter(Boolean);
      if (selected.hasVariants) {
        baseImages.forEach((url, index) => addEntry(
          url,
          baseModelHidden ? null : BASE_PRODUCT_OPTION_ID,
          baseModelHidden ? '' : (index === 0 ? 'Base model' : ''),
          !baseModelHidden && index === 0
        ));
        selectedActiveVariants.forEach((variant) => {
          const variantImages = [variant.img, ...(Array.isArray(variant.images) ? variant.images : parseStringArrayInput(variant.images))].filter(Boolean);
          variantImages.forEach((url, index) => addEntry(url, variant.id, index === 0 ? variant.label : '', index === 0));
        });
      } else {
        baseImages.forEach((url) => addEntry(url));
      }
      return entries;
    } catch {
      return selectedPurchaseProduct?.img ? [{ url: selectedPurchaseProduct.img, selectionId: null, label: '', forceSelection: false }] : [];
    }
  }, [selected, selectedPurchaseProduct, selectedActiveVariants, baseModelHidden]);
  const selectedPurchaseGallery = useMemo(() => {
    try {
      if (!selectedPurchaseProduct) return [];
      const directImages = Array.isArray(selectedPurchaseProduct.images)
        ? selectedPurchaseProduct.images
        : parseStringArrayInput(selectedPurchaseProduct.images);
      return [selectedPurchaseProduct.img, ...directImages].filter(Boolean).reduce((acc, url) => (
        acc.includes(url) ? acc : acc.concat(url)
      ), []);
    } catch {
      return [selectedPurchaseProduct?.img].filter(Boolean);
    }
  }, [selectedPurchaseProduct]);
  const [productShowLayerA, setProductShowLayerA] = useState(true);
  const [productLayerASrc, setProductLayerASrc] = useState('');
  const [productLayerBSrc, setProductLayerBSrc] = useState('');
  const [productLayerASquare, setProductLayerASquare] = useState(false);
  const [productLayerBSquare, setProductLayerBSquare] = useState(false);
  const productSwapTimer = React.useRef(null);
  const productTargetSrc = useMemo(() => {
    if (!selectedPurchaseProduct) return '';
    return currentImage || selectedPurchaseProduct.img || selectedPurchaseGallery[0] || '';
  }, [currentImage, selectedPurchaseProduct, selectedPurchaseGallery]);

  useEffect(() => {
    if (!detailGallery || detailGallery.length === 0) return;
    detailGallery.forEach((entry) => {
      const raw = entry?.url;
      if (!isImageMedia(raw)) return;
      try {
        const img = new Image();
        img.src = resolveMediaUrl(raw);
      } catch {}
    });
  }, [detailGallery, resolveMediaUrl]);

  useEffect(() => {
    if (!productTargetSrc) {
      setProductLayerASrc('');
      setProductLayerBSrc('');
      return;
    }
    const targetKind = getMediaKind(productTargetSrc);
    if (targetKind !== 'image') {
      if (productLayerASrc !== productTargetSrc || productLayerBSrc) {
        setProductLayerASrc(productTargetSrc);
        setProductLayerBSrc('');
        setProductShowLayerA(true);
      }
      return;
    }
    if (!productLayerASrc && !productLayerBSrc) {
      setProductLayerASrc(productTargetSrc);
      setProductShowLayerA(true);
      return;
    }
    const currentVisible = productShowLayerA ? productLayerASrc : productLayerBSrc;
    if (productTargetSrc === currentVisible) return;
    if (productSwapTimer.current) clearTimeout(productSwapTimer.current);
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      if (productShowLayerA) {
        setProductLayerBSrc(productTargetSrc);
        setProductShowLayerA(false);
      } else {
        setProductLayerASrc(productTargetSrc);
        setProductShowLayerA(true);
      }
    };
    img.onerror = () => {
      if (cancelled) return;
      if (productShowLayerA) {
        setProductLayerBSrc(productTargetSrc);
        setProductShowLayerA(false);
      } else {
        setProductLayerASrc(productTargetSrc);
        setProductShowLayerA(true);
      }
    };
    img.src = resolveMediaUrl(productTargetSrc);
    return () => { cancelled = true; };
  }, [productTargetSrc, productLayerASrc, productLayerBSrc, productShowLayerA, resolveMediaUrl]);
  const [selectedQty, setSelectedQty] = useState(1);
  const imageContainerRef = React.useRef(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const openMediaPreview = useCallback((src) => {
    if (!src) return;
    setMediaPreview(resolveMediaUrl(src));
  }, [resolveMediaUrl]);
  // (medium-zoom removed in favor of unified lightbox)
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.matchMedia('(max-width: 639px)').matches;
    } catch {
      return window.innerWidth < 640;
    }
  });

  // Toasts initialized above for early usage

  // Reviews state and form for product pages
  const REVIEWS_PER_PAGE = 5;
  const [reviews, setReviews] = useState([]);
  const [reviewsPage, setReviewsPage] = useState(1);
  const reviewPhotos = useMemo(() => {
    try {
      return (reviews || []).filter(r => {
        const u = String(r?.media_url || '').toLowerCase();
        if (!u) return false;
        return u.endsWith('.jpg') || u.endsWith('.jpeg') || u.endsWith('.png') || u.endsWith('.webp') || u.endsWith('.avif');
      });
    } catch {
      return [];
    }
  }, [reviews]);
  const [reviewForm, setReviewForm] = useState({
    text: '',
    rating: 5,
    mediaFile: null,
    mediaUrl: null,
    mediaType: '',
  });
  const handleReviewMediaChange = React.useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setReviewForm((f) => ({ ...f, mediaFile: null, mediaUrl: null, mediaType: '' }));
      return;
    }
    const url = URL.createObjectURL(file);
    setReviewForm((f) => ({ ...f, mediaFile: file, mediaUrl: url, mediaType: file.type }));
  }, []);
  const handleAddReview = React.useCallback(async () => {
    if (!reviewForm.text.trim() || !selected?.id) return;
    try {
      let uploadedUrl = null;
      // If a file is chosen, upload it first to get a persistent URL
      if (reviewForm.mediaFile) {
        const fd = new FormData();
        fd.append('file', reviewForm.mediaFile);
        const upRes = await fetch(`${API_BASE}/reviews/upload`, { method: 'POST', body: fd });
        if (!upRes.ok) {
          const upErr = await upRes.json().catch(() => ({}));
          throw new Error(upErr?.error || 'Media upload failed');
        }
        const upData = await upRes.json();
        uploadedUrl = upData?.url || null;
      }

      const res = await fetch(`${API_BASE}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: selected.id,
          text: reviewForm.text.trim(),
          rating: clamp(reviewForm.rating, 1, 5),
          mediaUrl: uploadedUrl,
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error || 'Failed to submit review');
      }
      // Reload approved reviews later; for now just clear and toast
      push('Thanks! Your review will appear after approval.');
      setReviewForm({ text: '', rating: 5, mediaFile: null, mediaUrl: null, mediaType: '' });
    } catch (e) {
      push(e?.message || 'Could not submit review. Please try again.', 'error');
    }
  }, [reviewForm, selected?.id, push]);

  const totalReviewPages = useMemo(() => {
    return Math.max(1, Math.ceil((reviews?.length || 0) / REVIEWS_PER_PAGE));
  }, [reviews, REVIEWS_PER_PAGE]);

  const pagedReviews = useMemo(() => {
    const start = (reviewsPage - 1) * REVIEWS_PER_PAGE;
    return (reviews || []).slice(start, start + REVIEWS_PER_PAGE);
  }, [reviews, reviewsPage, REVIEWS_PER_PAGE]);

  useEffect(() => {
    const loadReviews = async () => {
      if (!selected?.id) { setReviews([]); return; }
      try {
        const res = await fetch(`${API_BASE}/reviews?productId=${encodeURIComponent(selected.id)}`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setReviews(Array.isArray(data) ? data : []);
      } catch {
        setReviews([]);
      }
    };
    loadReviews();
  }, [selected?.id]);

  useEffect(() => {
    setReviewsPage(1);
  }, [selected?.id]);

  useEffect(() => {
    setReviewsPage((prev) => Math.min(prev, totalReviewPages));
  }, [totalReviewPages]);

  const sanitizeTrackingInput = React.useCallback((value) => {
    if (typeof value !== 'string') return '';
    const trimmed = value.replace(/\s+/g, '');
    const sanitized = trimmed.replace(/[^a-z0-9-]/gi, '').toUpperCase();
    return sanitized;
  }, []);

  const handleTrackingInputChange = React.useCallback((value) => {
    const sanitized = sanitizeTrackingInput(value);
    setTrackingOrderId(sanitized);
    if (sanitized.length === 0) {
      setTrackingResult(null);
    }
    setTrackingError(null);
  }, [sanitizeTrackingInput]);
  const resetTrackingSearch = React.useCallback(() => {
    setTrackingResult(null);
    setTrackingError(null);
    setTrackingOrderId('');
    setPendingAutoTrackId('');
    try {
      window.history.replaceState({ page: 'track' }, '', '/track');
    } catch {}
  }, []);
  const isTrackingIdValid = trackingOrderId.length >= 4;
  const trackingTimeline = React.useMemo(() => {
    if (!trackingResult) return null;
    if (trackingResult.status === 'cancelled') {
      const steps = [...ORDER_STATUS_STEPS.slice(0, 2), ORDER_STATUS_CANCELLED_STEP];
      return {
        steps,
        currentIndex: steps.length - 1,
        isCancelled: true
      };
    }
    const steps = ORDER_STATUS_STEPS;
    const idx = steps.findIndex(step => step.id === trackingResult.status);
    return {
      steps,
      currentIndex: idx >= 0 ? idx : 0,
      isCancelled: false
    };
  }, [trackingResult]);
  const trackingSubtotal = React.useMemo(
    () => (trackingResult?.items || []).reduce((sum, item) => sum + (Math.max(0, Number(item?.price) || 0) * Math.max(1, Number(item?.qty) || 1)), 0),
    [trackingResult]
  );
  const trackingProductDiscount = React.useMemo(
    () => (trackingResult?.items || []).reduce((sum, item) => {
      const price = Math.max(0, Number(item?.price) || 0);
      const originalPrice = Math.max(0, Number(item?.originalPrice) || 0);
      const qty = Math.max(1, Number(item?.qty) || 1);
      return sum + (originalPrice > price ? (originalPrice - price) * qty : 0);
    }, 0),
    [trackingResult]
  );
  const trackingOriginalSubtotal = trackingSubtotal + trackingProductDiscount;
  const trackingPrefix = String(settings?.orderIdPrefix || '').trim();
  const trackingSuffix = String(settings?.orderIdSuffix || '').trim();
  const trackingExampleId = `${trackingPrefix}83425190${trackingSuffix}`;
  const trackingFormatDescription = [
    trackingPrefix ? `${trackingPrefix} +` : '',
    '8 digits',
    trackingSuffix ? `+ ${trackingSuffix}` : ''
  ].filter(Boolean).join(' ');
  
  // Checkout state
  const [checkoutState, setCheckoutState] = useState(() => {
    try {
      const saved = localStorage.getItem('shopstarter_checkout_v2');
      const base = saved ? JSON.parse(saved) : {
        firstName: '', lastName: '', country: 'Bangladesh', house: '', city: '', phone: '', email: '',
        shipDifferent: false, notes: '', bkashFrom: '', bkashTxn: '', bkashScreenshotUrl: '', shippingOption: 'inside', paymentMethod: 'cod'
      };
      if (!base.paymentMethod || !['cod', 'bkash'].includes(base.paymentMethod)) base.paymentMethod = 'cod';
      return base;
    } catch {
      return {
        firstName: '', lastName: '', country: 'Bangladesh', house: '', city: '', phone: '', email: '',
        shipDifferent: false, notes: '', bkashFrom: '', bkashTxn: '', bkashScreenshotUrl: '', shippingOption: 'inside', paymentMethod: 'cod'
      };
    }
  });
  
  const copyOrderId = React.useCallback(async (id) => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      push('Order ID copied to clipboard');
    } catch (err) {
      console.error('Copy failed', err);
      push('Could not copy Order ID', 'error');
    }
  }, [push]);
  const { request, loading: apiLoading } = useApi();
  const sitemapXmlHref = useMemo(() => {
    if (typeof window === 'undefined') return '/sitemap.xml';
    const { protocol, hostname, port } = window.location;
    // In CRA dev (port 3000), navigate directly to backend (3001) to bypass SPA fallback
    if (port === '3000') return `${protocol}//${hostname}:3001/sitemap.xml`;
    return '/sitemap.xml';
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('shopstarter_cart_v2', JSON.stringify(cart));
    } catch (err) {
      console.warn('Failed to save cart:', err);
    }
  }, [cart]);

  // Prefill checkout details from localStorage
  useEffect(() => {
    if (page === 'checkout') {
      try {
        const raw = localStorage.getItem('shopstarter_address');
        if (raw) {
          const saved = JSON.parse(raw);
          setCheckoutState(s => ({ ...s, ...saved }));
        }
      } catch {}
    }
  }, [page]);

  // Load legal page content when on terms, privacy, cookies, order, shipping, or legal pages
  useEffect(() => {
    const legalPages = ['terms', 'privacy', 'cookies', 'order', 'shipping'];
    if (!(legalPages.includes(page) || page === 'legal')) return;

    const LEGAL_KEY_ALIASES = {
      terms: ['terms', 'terms-and-conditions', 'terms-conditions', 'terms-of-service', 'tos'],
      privacy: ['privacy', 'privacy-policy', 'data-privacy', 'policy'],
      cookies: ['cookies', 'cookie-policy', 'cookies-policy'],
      order: ['order', 'order-cancellation', 'order-cancellation-policy', 'returns', 'return-policy', 'refund', 'refund-policy'],
      shipping: ['shipping', 'delivery', 'shipping-delivery', 'shipping-policy', 'delivery-policy']
    };

    const normalizeKey = (value) => slugify(String(value || '').replace(/[^a-z0-9\s-]/gi, ' '));
    const resolveByAliases = (pages, baseKey) => {
      if (!Array.isArray(pages)) return null;
      const activePages = pages.filter(p => p && (p.active === undefined || p.active === 1 || p.active === true));
      const candidates = LEGAL_KEY_ALIASES[baseKey] || [baseKey];
      const keyed = new Map();
      activePages.forEach(p => {
        const key = normalizeKey(p.page_key || '');
        if (key && !keyed.has(key)) keyed.set(key, p);
      });
      for (const candidate of candidates) {
        const match = keyed.get(normalizeKey(candidate));
        if (match) return match;
      }
      const base = normalizeKey(baseKey);
      if (base) {
        for (const p of activePages) {
          const titleKey = normalizeKey(p.title || '');
          if (titleKey && (titleKey.includes(base) || base.includes(titleKey))) return p;
        }
      }
      return null;
    };

    let pageKey = '';
    if (page === 'legal') {
      pageKey = window.location.pathname.replace('/legal/', '');
    } else {
      pageKey = page;
    }
    if (!pageKey) return;

    const controller = new AbortController();
    const fetchLegal = async () => {
      setLegalPageLoading(true);
      try {
        const res = await fetch(`${API_BASE}/legal-pages/${encodeURIComponent(pageKey)}`, { signal: controller.signal });
        if (res.ok) {
          const data = await res.json();
          setLegalPage(data);
          return;
        }
        if (res.status === 404) {
          const listRes = await fetch(`${API_BASE}/legal-pages`, { signal: controller.signal });
          if (listRes.ok) {
            const list = await listRes.json();
            const fallback = resolveByAliases(list, pageKey);
            setLegalPage(fallback || null);
            return;
          }
        }
        setLegalPage(null);
      } catch {
        setLegalPage(null);
      } finally {
        setLegalPageLoading(false);
      }
    };
    fetchLegal();
    return () => controller.abort();
  }, [page]);

  // Load data with auto-refresh capability
  useEffect(() => {
    let refreshInterval;
    const getRefreshIntervalMs = () => {
      try {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (!connection) return 60000;
        if (connection.saveData) return 120000;
        const effectiveType = String(connection.effectiveType || '').toLowerCase();
        if (effectiveType.includes('2g') || effectiveType.includes('slow-2g')) return 120000;
      } catch {}
      return 60000;
    };
    
    const loadData = async () => {
      try {
        setIsLoadingProducts(true);
        const [productsData, settingsDataRaw, promotionsData] = await Promise.all([
          request('/products?active=true').catch(() => []), // fallback to empty products
          request('/settings').catch(() => { try { const raw=localStorage.getItem('shopstarter_settings'); return raw? JSON.parse(raw): {}; } catch { return {}; } }),
          request('/deals/active').catch(() => []) // fallback to empty promotions
        ]);
        // Normalize tags field if present
        const normalizedProducts = (productsData || []).map(p => {
          let tags = [];
          try { if (p.tags) tags = Array.isArray(p.tags) ? p.tags : JSON.parse(p.tags || '[]'); } catch {}
          return normalizeStorefrontProduct({ ...p, tags });
        });
        setProducts(normalizedProducts);
        setSettings(normalizeSettings(settingsDataRaw));
        try { localStorage.setItem('shopstarter_settings', JSON.stringify(settingsDataRaw || {})); } catch {}
        setActivePromotions(promotionsData);
        try { localStorage.setItem('shopstarter_active_promotions', JSON.stringify(promotionsData || [])); } catch {}
        setSettingsReady(true);
        // Do not toast on empty – let the UI show the empty state without spamming
      } catch (err) {
        console.error('Failed to load data:', err);
        // Set fallback data even on complete failure (no toasts spam)
        setProducts([]);
        // keep cached settings; just mark ready
        setSettingsReady(true);
        setActivePromotions([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    // Initial load
    loadData();
    
    // Set up periodic refresh (less aggressive on slower networks/devices)
    const refreshIntervalMs = getRefreshIntervalMs();
    refreshInterval = setInterval(async () => {
      try {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        const shouldSkip = page === 'product' && selected && getMediaKind(productTargetSrc) !== 'image';
        if (shouldSkip) return;
        // Silent refresh - don't show loading state
        const [productsData, settingsData, promotionsData] = await Promise.all([
          request('/products?active=true'),
          request('/settings').catch(() => null),
          request('/deals/active').catch(() => [])
        ]);
        const normalizedProducts = (productsData || []).map(p => {
          let tags = [];
          try { if (p.tags) tags = Array.isArray(p.tags) ? p.tags : JSON.parse(p.tags || '[]'); } catch {}
          return normalizeStorefrontProduct({ ...p, tags });
        });
        setProducts(normalizedProducts);
        if (settingsData) {
          setSettings(normalizeSettings(settingsData));
          try { localStorage.setItem('shopstarter_settings', JSON.stringify(settingsData)); } catch {}
        }
        setActivePromotions(promotionsData);
        try { localStorage.setItem('shopstarter_active_promotions', JSON.stringify(promotionsData || [])); } catch {}
      } catch (err) {
        console.warn('Background refresh failed:', err);
        // Don't show error for background refresh failures
      }
    }, refreshIntervalMs);
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [request, page, selected, productTargetSrc]); // Removed 'settings' to avoid infinite re-renders

  // Featured (home spotlight): prefer in‑stock products and prioritize obvious hero items (e.g., lanterns).
  const featured = useMemo(() => {
    const score = (p) => {
      try {
        const title = String(p?.title || '').toLowerCase();
        const tags = Array.isArray(p?.tags) ? p.tags.map(String).map(s => s.toLowerCase()) : [];
        let s = 0;
        if (tags.includes('featured') || tags.includes('hero') || tags.includes('spotlight')) s += 100;
        if (title.includes('lantern') || title.includes('lamp')) s += 80;
        if (tags.includes('bestseller') || tags.includes('best')) s += 10;
        return s;
      } catch {
        return 0;
      }
    };

    const getPrimaryMedia = (p) => {
      try {
        if (p?.img) return p.img;
        if (!p?.images) return '';
        const arr = Array.isArray(p.images) ? p.images : JSON.parse(p.images || '[]');
        return (arr && arr[0]) || '';
      } catch {
        return '';
      }
    };
    const isImageProduct = (p) => getMediaKind(getPrimaryMedia(p)) === 'image';

    const inStock = products
      .filter(p => (p?.stock ?? 0) > 0)
      .filter(isImageProduct)
      .slice()
      .sort((a, b) => score(b) - score(a));

    const picks = inStock.slice(0, 3);
    if (picks.length < 3) {
      const outOfStock = products
        .filter(p => (p?.stock ?? 0) <= 0)
        .filter(isImageProduct)
        .slice()
        .sort((a, b) => score(b) - score(a));
      picks.push(...outOfStock.slice(0, 3 - picks.length));
    }
    return picks;
  }, [products]);
  const startupLogoSrc = useMemo(() => {
    const configured = String(settings?.logoUrl || '').trim();
    if (configured) return resolveMediaUrl(configured);
    return '/logo.png';
  }, [settings?.logoUrl, resolveMediaUrl]);
  const startupCriticalImageUrls = useMemo(() => {
    const seen = new Set();
    const list = [];
    const add = (rawValue) => {
      const raw = String(rawValue || '').trim();
      if (!raw) return;
      const resolved = resolveMediaUrl(raw);
      if (!resolved || !isImageMedia(resolved)) return;
      if (seen.has(resolved)) return;
      seen.add(resolved);
      list.push(resolved);
    };

    add(startupLogoSrc);
    (heroBanners || []).slice(0, 3).forEach((banner) => add(banner?.image));
    try {
      (settings?.homepageBanners || [])
        .filter((banner) => banner && banner.active !== false)
        .slice(0, 6)
        .forEach((banner) => add(banner?.image));
    } catch {}
    (featured || []).slice(0, 2).forEach((product) => {
      collectProductMediaSources(product).slice(0, 4).forEach((raw) => add(raw));
    });
    if (page === 'product') {
      let targetProduct = selected || null;
      if (!targetProduct && typeof window !== 'undefined') {
        try {
          const slugOrId = extractRequestedProductSlug(window.location);
          if (slugOrId) {
            targetProduct = products.find((entry) => String(entry?.id) === slugOrId || String(entry?.dna_id) === slugOrId)
              || productSlugIndex.productBySlug.get(slugOrId)
              || null;
          }
        } catch {}
      }
      if (targetProduct) {
        collectProductMediaSources(targetProduct).slice(0, 6).forEach((raw) => add(raw));
      }
    }
    return list.slice(0, 24);
  }, [startupLogoSrc, heroBanners, settings?.homepageBanners, featured, page, selected, products, productSlugIndex, resolveMediaUrl]);

  useEffect(() => {
    if (startupLoaderStartedRef.current) return;
    startupCriticalImageUrlsRef.current = Array.isArray(startupCriticalImageUrls) ? startupCriticalImageUrls : [];
  }, [startupCriticalImageUrls]);

  const catalog = useMemo(() => {
    const q = (debouncedCatalogSearch || '').trim().toLowerCase();
    // Optional tag filter via URL query (?filter=new|trending|best|general)
    let tagFilter = null;
    try {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const f = (params.get('filter') || '').toLowerCase();
        if (['new','trending','best','bestseller','general'].includes(f)) tagFilter = f === 'best' ? 'bestseller' : f;
      }
    } catch {}
    let filtered = products.filter(p => {
      if (filters.category !== 'All' && p.category !== filters.category) return false;
      if (typeof filters.priceMin === 'number' && p.price < filters.priceMin) return false;
      if (p.price > filters.priceMax) return false;
      if (Array.isArray(filters.tags) && filters.tags.length > 0) {
        const pt = Array.isArray(p.tags) ? p.tags.map(String).map(s => s.toLowerCase()) : [];
        const any = filters.tags.some(tag => pt.includes(String(tag).toLowerCase()));
        if (!any) return false;
      }
      if (tagFilter) {
        const tags = Array.isArray(p.tags) ? p.tags : [];
        if (!tags.map(String).map(s => s.toLowerCase()).includes(tagFilter)) return false;
      }
      if (!q) return true;
      return p.title.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.color.toLowerCase().includes(q);
    });
    if (filters.sort === 'priceLow') {
      filtered = filtered.slice().sort((a, b) => a.price - b.price);
    } else if (filters.sort === 'priceHigh') {
      filtered = filtered.slice().sort((a, b) => b.price - a.price);
    } else if (filters.sort === 'alpha') {
      filtered = filtered.slice().sort((a, b) => a.title.localeCompare(b.title));
    } else if (filters.sort === 'bestseller') {
      const isBest = (p) => {
        const tags = Array.isArray(p.tags) ? p.tags.map(String).map(s => s.toLowerCase()) : [];
        return tags.includes('bestseller') || tags.includes('best');
      };
      filtered = filtered.slice().sort((a, b) => (isBest(b) - isBest(a)) || (b.created_at > a.created_at ? 1 : -1));
    } else if (filters.sort === 'discounted') {
      const hasDisc = (p) => !!p?.hasDiscount || (Number(p?.originalPrice) > Number(p?.price));
      filtered = filtered.slice().sort((a, b) => (hasDisc(b) - hasDisc(a)) || (a.price - b.price));
    } else if (filters.sort === 'trending') {
      const trendScore = (product) => {
        const liveStock = Number(product?.displayStock ?? product?.stock ?? 0);
        const lowStock = liveStock > 0 && liveStock <= 5 ? 5 : 0;
        const discount = product?.hasDiscount ? 4 : 0;
        const recency = product?.created_at
          ? Math.max(0, 30 - ((Date.now() - new Date(product.created_at).getTime()) / (1000 * 60 * 60 * 24)))
          : 0;
        return lowStock + discount + recency;
      };
      filtered = filtered.slice().sort((a, b) => trendScore(b) - trendScore(a));
    } else {
      // Default / newest first
      filtered = filtered.slice().sort((a, b) => {
        const aDate = new Date(a.created_at || a.createdAt || 0).getTime();
        const bDate = new Date(b.created_at || b.createdAt || 0).getTime();
        return bDate - aDate;
      });
    }
    return filtered;
  }, [products, filters, debouncedCatalogSearch]);

  // URL <-> state sync for catalog filters/search (decoupled from header search)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (page !== 'catalog') return;
    try {
      const params = new URLSearchParams(window.location.search || '');
      const get = (k, d) => params.get(k) ?? d;
      const num = (k, d) => {
        const raw = params.get(k);
        if (raw === null || raw === '') return d;
        const v = Number(raw);
        return Number.isFinite(v) ? v : d;
      };
      if (params.has('q')) {
        const q = (get('q', '') || '').trim();
        if (q !== catalogSearch) setCatalogSearch(q);
      }

      setFilters((prev) => {
        const next = { ...prev };
        let changed = false;

        if (params.has('category')) {
          const category = get('category', 'All');
          if (category && category !== next.category) {
            next.category = category;
            changed = true;
          }
        }

        if (params.has('sort')) {
          const sort = get('sort', next.sort || 'priceLow');
          if (sort && sort !== next.sort) {
            next.sort = sort;
            changed = true;
          }
        }

        if (params.has('priceMin')) {
          const priceMinRaw = num('priceMin', DEFAULT_PRICE_MIN);
          const priceMin = Number.isFinite(priceMinRaw) ? Math.max(DEFAULT_PRICE_MIN, priceMinRaw) : DEFAULT_PRICE_MIN;
          if (priceMin !== next.priceMin) {
            next.priceMin = priceMin;
            changed = true;
          }
        }

        if (params.has('priceMax')) {
          const priceMaxRaw = num('priceMax', DEFAULT_PRICE_MAX);
          const priceMax = Number.isFinite(priceMaxRaw) && priceMaxRaw > 0 ? priceMaxRaw : DEFAULT_PRICE_MAX;
          if (priceMax !== next.priceMax) {
            next.priceMax = priceMax;
            changed = true;
          }
        }

        if (params.has('tags')) {
          const tags = (get('tags', '') || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
          if (JSON.stringify(tags) !== JSON.stringify(next.tags || [])) {
            next.tags = tags;
            changed = true;
          }
        }

        return changed ? next : prev;
      });
    } catch {}
  }, [catalogSearch, page, setFilters]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (page !== 'catalog') return;
    try {
      const params = new URLSearchParams(window.location.search || '');
      if ((catalogSearch || '').trim()) params.set('q', catalogSearch.trim()); else params.delete('q');
      if (filters.category && filters.category !== 'All') params.set('category', filters.category); else params.delete('category');
      if (Array.isArray(filters.tags) && filters.tags.length > 0) params.set('tags', filters.tags.join(',')); else params.delete('tags');
      if (Number(filters.priceMin) !== DEFAULT_PRICE_MIN) params.set('priceMin', String(filters.priceMin)); else params.delete('priceMin');
      if (Number(filters.priceMax) !== DEFAULT_PRICE_MAX) params.set('priceMax', String(filters.priceMax)); else params.delete('priceMax');
      if (filters.sort && filters.sort !== 'priceLow') params.set('sort', filters.sort); else params.delete('sort');
      const qs = params.toString();
      const nextUrl = qs ? `/catalog?${qs}` : '/catalog';
      if ((window.location.pathname + window.location.search) !== nextUrl) {
        window.history.replaceState({ page: 'catalog' }, '', nextUrl);
      }
    } catch {}
  }, [filters, catalogSearch, page]);

  const categoryChips = useMemo(() => {
    const categories = Array.from(
      new Set(
        products
          .map(p => p?.category)
          .filter(Boolean)
      )
    );
    return ['All', ...categories];
  }, [products]);

  const homeFilterChips = useMemo(() => {
    const categoryEntries = categoryChips.map(cat => ({
      id: `category-${cat}`,
      label: cat,
      type: 'category',
      category: cat
    }));
    return [...categoryEntries];
  }, [categoryChips]);
  const applyPriceFilter = useCallback((option) => {
    setActivePriceFilter(option.id);
    const range = option.id === 'all'
      ? { min: DEFAULT_PRICE_MIN, max: DEFAULT_PRICE_MAX }
      : (option.range ?? {});
    setFilters(prev => ({
      ...prev,
      priceMin: range.min ?? DEFAULT_PRICE_MIN,
      priceMax: range.max ?? DEFAULT_PRICE_MAX
    }));
  }, []);

  const goToNewArrivals = useCallback(() => {
    setFilters(prev => ({ ...prev, category: 'All', sort: 'newest' }));
    goto('catalog');
  }, [goto]);

  const goToTrending = useCallback(() => {
    setFilters(prev => ({ ...prev, category: 'All', sort: 'trending' }));
    goto('catalog');
  }, [goto]);

  const [collectionsMenuOpen, setCollectionsMenuOpen] = useState(false);
  const collectionsMenuCloseTimerRef = useRef(null);

  const clearCollectionsMenuCloseTimer = useCallback(() => {
    if (collectionsMenuCloseTimerRef.current) {
      clearTimeout(collectionsMenuCloseTimerRef.current);
      collectionsMenuCloseTimerRef.current = null;
    }
  }, []);

  const openCollectionsMenu = useCallback(() => {
    clearCollectionsMenuCloseTimer();
    setCollectionsMenuOpen(true);
  }, [clearCollectionsMenuCloseTimer]);

  const closeCollectionsMenuDelayed = useCallback((delay = 320) => {
    clearCollectionsMenuCloseTimer();
    collectionsMenuCloseTimerRef.current = setTimeout(() => {
      setCollectionsMenuOpen(false);
      collectionsMenuCloseTimerRef.current = null;
    }, delay);
  }, [clearCollectionsMenuCloseTimer]);

  useEffect(() => {
    return () => {
      clearCollectionsMenuCloseTimer();
    };
  }, [clearCollectionsMenuCloseTimer]);

  const handleCollectionSelect = useCallback((category) => {
    clearCollectionsMenuCloseTimer();
    setFilters(prev => ({ ...prev, category }));
    goto('catalog');
    setCollectionsMenuOpen(false);
  }, [clearCollectionsMenuCloseTimer, goto, setFilters]);

  const primaryNav = useMemo(() => [
    {
      key: 'shop',
      label: 'Collections',
      action: () => goto('catalog'),
      matchPage: 'catalog'
    },
    {
      key: 'stories',
      label: 'Offers',
      action: () => goto('offers'),
      matchPage: 'offers'
    },
    {
      key: 'track',
      label: 'Track',
      action: () => goto('track'),
      matchPage: 'track'
    },
    {
      key: 'help',
      label: 'Help',
      action: () => goto('help'),
      matchPage: 'help'
    }
  ], [goto, goToNewArrivals, goToTrending]);

  const handlePrevHero = useCallback(() => {
    if (heroBanners.length <= 1) return;
    setBannerIdx(idx => (idx - 1 + heroBanners.length) % heroBanners.length);
  }, [heroBanners.length]);

  const handleNextHero = useCallback(() => {
    if (heroBanners.length <= 1) return;
    setBannerIdx(idx => (idx + 1) % heroBanners.length);
  }, [heroBanners.length]);


  const homeShowcase = useMemo(() => {
    const base = filters.category === 'All'
      ? products
      : products.filter(p => p.category === filters.category);
    return base
      .slice()
      .sort((a, b) => {
        const aStock = (a?.stock ?? 0) > 0 ? 1 : 0;
        const bStock = (b?.stock ?? 0) > 0 ? 1 : 0;
        if (aStock !== bStock) return bStock - aStock;
        return (b.createdAt || b.id || 0) - (a.createdAt || a.id || 0);
      })
      .slice(0, 9);
  }, [products, filters.category]);

  const whatsNew = useMemo(() => {
    const sorted = products
      .slice()
      .sort((a, b) => {
        const aDate = a?.created_at || a?.createdAt;
        const bDate = b?.created_at || b?.createdAt;
        return new Date(bDate || 0) - new Date(aDate || 0);
      });
    return sorted.slice(0, 6);
  }, [products]);

  const trendingProducts = useMemo(() => {
    if (!products.length) return [];
    const scored = products.map((product, idx) => {
      const liveStock = Number(product?.displayStock ?? product?.stock ?? 0);
      const isLowStock = liveStock > 0 && liveStock <= 5;
      const hasDiscount = product?.hasDiscount ? 1 : 0;
      const recencyBoost = product?.created_at ? Math.max(0, 30 - ((Date.now() - new Date(product.created_at).getTime()) / (1000 * 60 * 60 * 24))) : 0;
      const score = (isLowStock ? 5 : 0) + hasDiscount * 4 + recencyBoost + (products.length - idx) * 0.01;
      return { product, score };
    });
    return scored
      .sort((a, b) => b.score - a.score)
      .map(entry => entry.product)
      .slice(0, 6);
  }, [products]);

  // Best sellers from API (aggregate orders)
  const [bestSellers, setBestSellers] = useState([]);

  // Determine how many items to show per row on home sections (always 1 row)
  const [homeCols, setHomeCols] = useState(4);
  useEffect(() => {
    const getCols = () => {
      if (typeof window === 'undefined') return 4;
      const w = window.innerWidth;
      if (w >= 1024) return 4; // lg and up
      if (w >= 640) return 3;  // sm to lg
      return 2;                // mobile
    };
    setHomeCols(getCols());
    const onResize = () => setHomeCols(getCols());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/products/bestsellers`);
        const data = await res.json();
        if (Array.isArray(data) && data.length) {
          const ids = data.map(x => x.id);
          const map = new Map(products.map(p => [p.id, p]));
          const list = ids.map(id => map.get(id)).filter(Boolean).slice(0, 6);
          setBestSellers(list);
        } else {
          // Fallback: show featured picks if no bestsellers yet (e.g., fresh store)
          setBestSellers(featured.slice(0, 6));
        }
      } catch {
        setBestSellers(featured.slice(0, 6));
      }
    })();
  }, [products, featured]);
  useEffect(() => {
    if (startupLoaderStartedRef.current) return undefined;
    if (!showStartupLoader) return undefined;

    startupLoaderStartedRef.current = true;
    let cancelled = false;
    let maxDelayTimer = null;
    const maxDelayMs = 3500;

    const imageSources = startupCriticalImageUrlsRef.current;
    const totalTasks = Math.max(1, imageSources.length);
    let completed = 0;
    setStartupLoadProgress(0);

    const markTaskComplete = () => {
      if (cancelled) return;
      completed += 1;
      const percent = Math.min(99, Math.round((completed / totalTasks) * 100));
      setStartupLoadProgress((prev) => (percent > prev ? percent : prev));
    };

    const preloadImage = (src) => new Promise((resolve) => {
      try {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = src;
        if (img.complete) resolve(true);
      } catch {
        resolve(false);
      }
    });

    const imageTasks = imageSources.map((src) => preloadImage(src).finally(markTaskComplete));
    const preloadTasks = Promise.allSettled(imageTasks);
    const timeout = new Promise((resolve) => { maxDelayTimer = setTimeout(resolve, maxDelayMs); });

    Promise.race([
      preloadTasks,
      timeout
    ]).then(() => {
      if (cancelled) return;
      setStartupLoadProgress(100);
      try { sessionStorage.setItem(STARTUP_PRELOAD_SESSION_KEY, '1'); } catch {}
      const reveal = () => {
        if (cancelled) return;
        setShowStartupLoader(false);
      };
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(() => window.requestAnimationFrame(reveal));
      } else {
        reveal();
      }
    });

    return () => {
      cancelled = true;
      if (maxDelayTimer) clearTimeout(maxDelayTimer);
    };
  }, [showStartupLoader]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!showStartupLoader) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [showStartupLoader]);

  // Related products for PDP
  const relatedProducts = useMemo(() => {
    if (!selected) return [];
    try {
      let list = products.filter(p => p && p.id !== selected.id);
      if (selected.category) list = list.filter(p => p.category === selected.category);
      if (list.length < 8 && selected.color) {
        const more = products.filter(p => p && p.id !== selected.id && (!selected.category || p.category !== selected.category) && p.color === selected.color);
        const ids = new Set(list.map(p => p.id));
        for (const m of more) if (!ids.has(m.id)) list.push(m);
      }
      if (list.length === 0) list = products.slice(0, 8);
      return list.slice(0, 12);
    } catch { return []; }
  }, [products, selected]);

  const renderHomeProductCard = useCallback((product, options = {}) => {
    if (!product) return null;
    const {
      badge,
      badgeTone = 'bg-white/90 text-gray-900',
      badgeShadow = '0 1px 2px rgba(0,0,0,0.10)',
      delay = 0
    } = options;
    const liveStock = Number(product?.displayStock ?? product?.stock ?? 0);
    const livePrice = Number(product?.displayPrice ?? product?.price ?? 0);
    const liveOriginalPrice = Number(product?.displayOriginalPrice ?? product?.originalPrice ?? 0);
    const isOutOfStock = liveStock === 0;
    const isLowStock = liveStock > 0 && liveStock <= 5;
    const hasDiscount = liveOriginalPrice > livePrice;

    return (
      <article
        key={product.id}
        className="bg-white rounded-2xl sm:rounded-none shadow-sm overflow-hidden min-w-[200px] sm:min-w-[240px] lg:min-w-[260px]"
        style={isLiteMode
          ? undefined
          : { opacity: 0, transform: 'translateY(12px)', animation: 'shopstarter-fade-up 0.45s ease forwards', animationDelay: `${delay}s`, willChange: 'transform, opacity' }}
      >
        <div
          className="relative cursor-pointer group"
          onClick={() => { setSelected(product); goto('product', product, false, product.img); }}
        >
          <PVImage onError={handleImgError} src={product.img} alt={product.title} className="w-full aspect-square object-cover" imgProps={{ variant: 'thumb' }} />
          {badge && (
            <div className="absolute top-3 left-3">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${badgeTone}`}
                style={{ textShadow: badgeShadow }}
              >
                {badge}
              </span>
            </div>
          )}
          {isLowStock && !isOutOfStock && (
            <div className="absolute top-3 right-3">
              <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.10)' }}>
                Only {liveStock} left
              </span>
            </div>
          )}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.10)' }}>
                Out of Stock
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setSelected(product); setQuickLook(product); setQuickLookOpen(true); }}
            className="absolute bottom-3 right-3 hidden sm:inline-flex items-center gap-2 rounded-full bg-white/90 text-gray-700 border border-gray-200 px-3 py-1 text-xs font-semibold shadow group-hover:flex"
            aria-label="👁️"
          >
            Quick view
          </button>
        </div>
        <div className="px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold truncate">{product.title}</h3>
              <div className="text-xs text-gray-500 truncate">
                {product.category}
                {product.color ? ` / ${product.color}` : ''}
              </div>
            </div>
            <div className="text-right">
              <PriceDisplay
                price={livePrice}
                originalPrice={hasDiscount ? liveOriginalPrice : null}
                showFrom={!!product?.hasVariants}
                align="right"
                originalClassName="text-[11px] sm:text-xs text-gray-400 line-through"
                currentClassName="text-base sm:text-lg font-bold text-[var(--accent)]"
                currentStyle={{ color: 'var(--accent)', textShadow: '0 1px 2px rgba(0,0,0,0.10)' }}
              />
            </div>
          </div>
        </div>
      </article>
    );
  }, [addToCart, goto, handleImgError, isLiteMode]);

  const renderCatalogFilters = useCallback((variant = 'desktop') => {
    const handleClear = () => {
      setCatalogSearch('');
      setFilters({ category: 'All', tags: [], priceMin: DEFAULT_PRICE_MIN, priceMax: DEFAULT_PRICE_MAX, sort: 'newest' });
      setActivePriceFilter('all');
      push('Filters cleared');
      if (variant === 'mobile') {
        setMobileFiltersOpen(false);
      }
    };

    const sectionSpacing = variant === 'mobile' ? 'space-y-2' : 'space-y-3';
    const actionLayout = variant === 'mobile'
      ? 'mt-4 flex flex-col gap-2 sm:flex-row sm:gap-2'
      : 'mt-4 flex gap-2';

    return (
      <>
        <div className={sectionSpacing}>
          <label className="block text-xs text-gray-500">Sort by</label>
          <select
            value={filters.sort}
            onChange={e => setFilters(f => ({ ...f, sort: e.target.value }))}
            className="w-full rounded-md border-gray-200 text-sm"
          >
            <option value="newest">Newest</option>
            <option value="priceLow">Price: Low to High</option>
            <option value="priceHigh">Price: High to Low</option>
            <option value="alpha">Alphabetical</option>
            <option value="trending">Trending</option>
            <option value="bestseller">Best sellers</option>
            <option value="discounted">Discounted first</option>
          </select>
        </div>

        <div className={sectionSpacing}>
          <label className="block text-xs text-gray-500">Category</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
            className="w-full rounded-md border-gray-200 text-sm"
          >
            <option>All</option>
            {catalogCategories.map(c => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>

        {catalogTags.length > 0 && (
          <div className={sectionSpacing}>
            <label className="block text-xs text-gray-500">Tags</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {catalogTags.map(tag => {
                const active = Array.isArray(filters.tags) && filters.tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setFilters(f => ({ ...f, tags: active ? f.tags.filter(t => t !== tag) : [...(f.tags||[]), tag] }))}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${active ? 'bg-[var(--accent)] text-gray-900 border-transparent' : 'bg-white text-gray-700 border-gray-200'}`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className={`${sectionSpacing} mt-1`}>
          <label className="block text-xs text-gray-500">Price range</label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              min="0"
              max={filters.priceMax}
              value={filters.priceMin}
              onChange={e => setFilters(f => ({ ...f, priceMin: +e.target.value }))}
              className="w-20 rounded-md border-gray-200 px-2 py-1 text-sm"
              placeholder="Min"
            />
            <span className="text-gray-400">-</span>
            <input
              type="number"
              min={filters.priceMin}
              max={DEFAULT_PRICE_MAX}
              value={filters.priceMax}
              onChange={e => setFilters(f => ({ ...f, priceMax: +e.target.value }))}
              className="w-20 rounded-md border-gray-200 px-2 py-1 text-sm"
              placeholder="Max"
            />
          </div>
          <input
            type="range"
            min={filters.priceMin}
            max={DEFAULT_PRICE_MAX}
            step="50"
            value={filters.priceMax}
            onChange={(e) => setFilters(f => ({ ...f, priceMax: +e.target.value }))}
            className="mt-1 w-full"
          />
          <div className="mt-0 text-xs text-gray-600">
            {filters.priceMin ? `${currency(filters.priceMin)} - ` : ''}
            Up to {currency(filters.priceMax)}
          </div>
        </div>

        <div className={actionLayout}>
          <button
            type="button"
            onClick={handleClear}
            className="flex-1 rounded-full bg-[var(--accent)] px-3 py-2 text-sm font-semibold text-white"
          >
            Clear
          </button>
          {variant === 'mobile' && (
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(false)}
              className="flex-1 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm"
            >
              Done
            </button>
          )}
        </div>
      </>
    );
  }, [catalogCategories, filters, push, setActivePriceFilter, setFilters, setMobileFiltersOpen, setCatalogSearch]);

  const helpFaqs = useMemo(() => ([
    {
      id: 'shipping',
      question: 'When will my order arrive?',
      answer: 'Inside Dhaka deliveries usually arrive within 1-2 working days. Orders going outside Dhaka typically take 2-4 working days depending on the courier route.'
    },
    {
      id: 'returns',
      question: 'How do returns or exchanges work?',
      answer: 'We accept unused items within 3 days of delivery. Message us with your order ID and reason, and we will arrange the exchange or pickup support.'
    },
    {
      id: 'payments',
      question: 'What payment options do you support?',
      answer: 'We currently support bKash (Personal), COD at the door, and bank transfers on request. Your payment instructions are shown during checkout.'
    },
    {
      id: 'tracking',
      question: 'Where can I find tracking updates?',
      answer: `Use the Track page in the header with your order ID (e.g., ${trackingExampleId || '83425190'}). We also share courier links by SMS as soon as parcels are dispatched.`
    }
  ]), [trackingExampleId]);

  const [openHelpFaqs, setOpenHelpFaqs] = useState(() => []);
  const [helpForm, setHelpForm] = useState({
    name: '',
    contact: '',
    orderId: '',
    topic: '',
    message: ''
  });
  const [isSubmittingHelp, setIsSubmittingHelp] = useState(false);

  const toggleHelpFaq = useCallback((id) => {
    setOpenHelpFaqs(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, []);

  const handleHelpFormChange = useCallback((field, value) => {
    setHelpForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleHelpFormSubmit = useCallback(async (event) => {
    event?.preventDefault();
    const trimmedName = helpForm.name.trim();
    const trimmedContact = (helpForm.contact || '').trim();
    const trimmedMessage = helpForm.message.trim();

    if (!trimmedName || trimmedName.length < 2) {
      push('Please enter your name so we can address you properly.', 'error');
      return;
    }
    if (!trimmedContact) {
      push('Share your best contact (Instagram @, Facebook URL, or phone).', 'error');
      return;
    }
    if (trimmedMessage.length < 10) {
      push('Tell us a little more about how we can help (at least 10 characters).', 'error');
      return;
    }

    setIsSubmittingHelp(true);
    try {
      const payload = { name: trimmedName, contact: trimmedContact, orderId: helpForm.orderId.trim(), topic: helpForm.topic.trim(), message: trimmedMessage };
      const res = await fetch(`${API_BASE}/help`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Failed');
      push('Thanks! Our support team will reach out within one business day.');
      setHelpForm({ name: '', contact: '', orderId: '', topic: '', message: '' });
    } catch (err) {
      console.error('Support form failed', err);
      push('Could not send your message. Please try again shortly.', 'error');
    } finally {
      setIsSubmittingHelp(false);
    }
  }, [helpForm, push]);

  useEffect(() => {
    if (!settings.enableCampaignPopup && showCampaignPopup) {
      setShowCampaignPopup(false);
      setCampaignPromotion(null);
    }
  }, [settings.enableCampaignPopup, showCampaignPopup]);

  useEffect(() => {
    if (!settings.enableCampaignPopup) return;
    if (!popupPromotion) return;
    if (showCampaignPopup || isPromoModalOpen) return;
    if (typeof window === 'undefined') return;

    const delaySeconds = Number(settings.campaignPopupDelaySeconds ?? DEFAULT_SETTINGS.campaignPopupDelaySeconds);
    const delayMs = Number.isFinite(delaySeconds) && delaySeconds >= 0 ? delaySeconds * 1000 : DEFAULT_SETTINGS.campaignPopupDelaySeconds * 1000;

    const timer = setTimeout(() => {
      if (!settings.enableCampaignPopup) return;
      if (showCampaignPopup || isPromoModalOpen) return;
      let sessionSeen = false;
      try {
        sessionSeen = sessionStorage.getItem('shopstarter_campaign_popup_seen') === '1';
      } catch {}
      if (sessionSeen) return;
      const todayKey = new Date().toISOString().slice(0, 10);
      try {
        const lastDismiss = localStorage.getItem('shopstarter_campaign_popup_last_dismiss');
        if (lastDismiss === todayKey) return;
      } catch {}
      setCampaignPromotion(popupPromotion);
      setShowCampaignPopup(true);
      try {
        sessionStorage.setItem('shopstarter_campaign_popup_seen', '1');
      } catch {}
    }, delayMs);

    return () => clearTimeout(timer);
  }, [popupPromotion, page, showCampaignPopup, isPromoModalOpen, settings.enableCampaignPopup, settings.campaignPopupDelaySeconds]);

  function handleCampaignDismiss(scope = 'session') {
    setShowCampaignPopup(false);
    setCampaignPromotion(null);
    if (scope === 'day') {
      try {
        if (typeof window !== 'undefined') {
          const todayKey = new Date().toISOString().slice(0, 10);
          localStorage.setItem('shopstarter_campaign_popup_last_dismiss', todayKey);
        }
      } catch {}
    }
  }

  const scrollToTop = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const closePromoModal = React.useCallback(() => {
    setPromoModalOpen(false);
    setSelectedPromotion(null);
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const getScrollTop = () => {
      const scrollingElement = document.scrollingElement || document.documentElement || document.body;
      return Math.max(
        window.scrollY || 0,
        scrollingElement?.scrollTop || 0,
        document.documentElement?.scrollTop || 0,
        document.body?.scrollTop || 0
      );
    };
    const handleScroll = () => {
      const scrollTop = getScrollTop();
      setShowScrollTop(scrollTop > 64);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [page]);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateViewport = () => {
      try {
        setIsMobileViewport(window.matchMedia('(max-width: 639px)').matches);
      } catch {
        setIsMobileViewport(window.innerWidth < 640);
      }
    };
    updateViewport();
    window.addEventListener('resize', updateViewport, { passive: true });
    return () => {
      window.removeEventListener('resize', updateViewport);
    };
  }, []);

  useEffect(() => {
    if (!isPromoModalOpen) return undefined;
    // Focus close button or first focusable
    try {
      if (promoCloseRef.current) {
        promoCloseRef.current.focus();
      } else if (promoModalRef.current) {
        const root = promoModalRef.current;
        const focusables = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusables.length > 0) focusables[0].focus();
      }
    } catch {}
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePromoModal();
        return;
      }
      if (e.key === 'Tab' && promoModalRef.current) {
        const root = promoModalRef.current;
        const nodes = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPromoModalOpen, closePromoModal]);

  function addToCart(product, qty = 1, customization = null) {
    try {
      if (product?.hasVariants && !product?.optionResolved) {
        goto('product', product);
        push('Choose an option before adding this product to cart', 'info');
        return;
      }
      if (product.stock < qty) {
        push(`Only ${product.stock} items available in stock`, 'error');
        return;
      }

      const baseCartItemId = product.variantId ? `${product.id}__variant_${product.variantId}` : product.id;
      let cartItemId = customization ? `${baseCartItemId}_custom_${Date.now()}` : baseCartItemId;
      let didAdd = false;
      setCart(prev => {
        const idx = prev.findIndex(i => i.id === cartItemId);
        let next;
        if (idx >= 0 && !customization) {
          const newQty = Math.min(prev[idx].qty + qty, product.stock);
          next = prev.map(i => i.id === cartItemId ? { ...i, qty: newQty } : i);
          if (newQty === prev[idx].qty) {
            push('Maximum available quantity already in cart', 'error');
            return prev;
          }
        } else {
          const cartItem = { 
            id: cartItemId,
            productId: product.id, 
            variantId: product.variantId || null,
            variantLabel: product.variantLabel || null,
            variantSku: product.variantSku || null,
            title: product.title, 
            displayTitle: product.title,
            price: product.price + (customization?.additionalCost || 0), 
            basePrice: product.price,
            originalPrice: (Number(product.originalPrice) > Number(product.price))
              ? Number(product.originalPrice) + (customization?.additionalCost || 0)
              : null,
            qty: Math.min(qty, product.stock), 
            img: product.img,
            stock: product.stock,
            category: product.category,
            color: product.color,
            customization: customization
          };
          next = [...prev, cartItem];
          didAdd = true;
        }
        return next;
      });
      // Now, outside of setCart, only show notification once per action
      setTimeout(() => {
        if (didAdd) {
          const optionLabel = formatSelectedOptionLabel(product.variantLabel);
          push(`${product.title}${optionLabel ? ` (${optionLabel})` : ''}${customization ? ' (customized)' : ''} added to cart`);
          trackEvent('add_to_cart', { product_id: product.id, variant_id: product.variantId || undefined, title: product.title, qty, price: product.price });
        }
      }, 0);
    } catch (err) {
      console.error('Add to cart failed', err);
      push('Failed to add to cart', 'error');
    }
  }
  
  // Buy Now helper for cards (home/catalog)
  function buyNow(product, qty = 1) {
    try {
      if (product?.hasVariants && !product?.optionResolved) {
        goto('product', product);
        push('Choose an option before starting Buy Now', 'info');
        return;
      }
      if (!product || (product?.stock ?? 0) === 0) return;
      const tempCart = [
        {
          id: product.variantId ? `${product.id}__variant_${product.variantId}` : product.id,
          productId: product.id,
          variantId: product.variantId || null,
          variantLabel: product.variantLabel || null,
          variantSku: product.variantSku || null,
          title: product.title,
          displayTitle: product.title,
          price: product.price,
          basePrice: product.price,
          originalPrice: Number(product.originalPrice) > Number(product.price) ? Number(product.originalPrice) : null,
          qty: qty,
          img: product.img,
          category: product.category,
          color: product.color,
          stock: product.stock,
          customization: null,
        },
      ];
      setBuyNowCheckoutItems(tempCart);
      setIsBuyNow(true);
      setShowDiscardBuyNowDialog(false);
      goto('checkout', null, true, null, { useBuyNow: true });
    } catch (err) {
      console.error('Buy Now failed', err);
      push('Failed to start Buy Now checkout', 'error');
    }
  }

  function removeFromCart(id) {
    try {
      setCart(prev => prev.filter(i => i.id !== id));
      push('Item removed', 'info');
    } catch (err) {
      console.error('Remove failed', err);
      push('Could not remove item', 'error');
    }
  }

  function updateQty(id, qty) {
    try {
      setCart(prev => prev.map(i => i.id === id ? { ...i, qty: clamp(qty, 1, Math.max(1, Number(i.stock) || 99)) } : i));
    } catch (err) {
      console.error('Qty update failed', err);
      push('Could not update quantity', 'error');
    }
  }

  const cartSubtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const hasBuyNowCheckout = isBuyNow && buyNowCheckoutItems.length > 0;
  const checkoutItems = useMemo(
    () => (hasBuyNowCheckout ? buyNowCheckoutItems : cart),
    [hasBuyNowCheckout, buyNowCheckoutItems, cart]
  );
  const checkoutSubtotal = useMemo(
    () => checkoutItems.reduce((s, i) => s + i.price * i.qty, 0),
    [checkoutItems]
  );
  const subtotal = cartSubtotal;

  // Promotion and delivery tracking for checkout
  const shippingRates = (settings && settings.shippingRates) ? settings.shippingRates : DEFAULT_SETTINGS.shippingRates;

  const normalizePromotionCategoryKey = useCallback((value) => String(value || '').trim().toLowerCase(), []);
  const parsePromotionProductIds = useCallback((promotion) => {
    try {
      const parsed = promotion?.product_ids ? JSON.parse(promotion.product_ids) : [];
      return new Set((Array.isArray(parsed) ? parsed : []).map((entry) => String(entry || '').trim()).filter(Boolean));
    } catch {
      return new Set();
    }
  }, []);
  const promotionMatchesCheckoutItem = useCallback((promotion, item) => {
    const productIds = parsePromotionProductIds(promotion);
    const normalizedCategory = normalizePromotionCategoryKey(promotion?.category);
    if (productIds.size > 0) {
      return productIds.has(String(item?.productId || item?.id || '').trim());
    }
    if (normalizedCategory) {
      return normalizePromotionCategoryKey(item?.category) === normalizedCategory;
    }
    return true;
  }, [normalizePromotionCategoryKey, parsePromotionProductIds]);
  const getShippingPromotionState = useCallback((items, orderSubtotal, deliveryBaseCost) => {
    const safeItems = Array.isArray(items) ? items : [];
    const livePromotions = Array.isArray(normalizedPromotions) ? normalizedPromotions : [];
    let best = { benefit: 0, freeDelivery: false };

    for (const promo of livePromotions) {
      if (!promo || !['free_delivery', 'delivery_fixed', 'delivery_percentage'].includes(String(promo.type || ''))) continue;
      if (Number(orderSubtotal || 0) < Number(promo.min_order_amount || 0)) continue;
      const hasScope = Boolean(String(promo.category || '').trim()) || parsePromotionProductIds(promo).size > 0;
      const applicableItems = hasScope ? safeItems.filter((item) => promotionMatchesCheckoutItem(promo, item)) : safeItems;
      if (hasScope && applicableItems.length === 0) continue;

      let benefit = 0;
      let freeDelivery = false;
      if (promo.type === 'free_delivery') {
        benefit = Math.max(0, Number(deliveryBaseCost) || 0);
        freeDelivery = benefit > 0;
      } else if (promo.type === 'delivery_fixed') {
        benefit = Math.max(0, Math.min(Number(deliveryBaseCost) || 0, Math.round(Number(promo.value) || 0)));
      } else if (promo.type === 'delivery_percentage') {
        benefit = Math.max(0, Math.min(Number(deliveryBaseCost) || 0, Math.round((Number(deliveryBaseCost) || 0) * ((Number(promo.value) || 0) / 100))));
      }

      if (benefit > best.benefit) {
        best = { benefit, freeDelivery };
      }
    }

    return {
      deliveryDiscount: best.freeDelivery ? 0 : best.benefit,
      freeDelivery: best.freeDelivery
    };
  }, [normalizedPromotions, parsePromotionProductIds, promotionMatchesCheckoutItem]);

  const deliveryBaseCost = shippingRates[checkoutState.shippingOption] || 0;
  const { deliveryDiscount: cartDeliveryDiscount, freeDelivery: cartFreeDelivery } = getShippingPromotionState(cart, cartSubtotal, deliveryBaseCost);
  const { deliveryDiscount: checkoutDeliveryDiscount, freeDelivery: checkoutFreeDelivery } = getShippingPromotionState(checkoutItems, checkoutSubtotal, deliveryBaseCost);
  const freeShipPromo = useMemo(() => {
    try { return (normalizedPromotions || []).find(p => p && p.type === 'free_delivery' && Number(p.min_order_amount) > 0) || null; } catch { return null; }
  }, [normalizedPromotions]);
  const freeShipThreshold = freeShipPromo ? Number(freeShipPromo.min_order_amount) : null;
  const toFreeShip = (typeof freeShipThreshold === 'number') ? Math.max(0, freeShipThreshold - cartSubtotal) : null;

  function goto(p, product = null, skipCartCheck = false, imageUrl = null, options = {}) {
    if (p === 'cart' && page === 'checkout' && hasBuyNowCheckout && !options.forceDiscardBuyNow) {
      setShowDiscardBuyNowDialog(true);
      return;
    }
    const nextCheckoutItems = options.useBuyNow ? buyNowCheckoutItems : cart;
    if (p === 'checkout' && nextCheckoutItems.length === 0 && !skipCartCheck) {
      push('Your cart is empty – add something first', 'error');
      return;
    }
    if (p === 'checkout' && !options.useBuyNow && (isBuyNow || buyNowCheckoutItems.length > 0)) {
      setIsBuyNow(false);
      setBuyNowCheckoutItems([]);
      setShowDiscardBuyNowDialog(false);
    }
    let url = '/';
    if (p === 'catalog') url = '/catalog';
    else if (p === 'cart') url = '/cart';
    else if (p === 'checkout') url = '/checkout';
    else if (p === 'thanks') url = '/thanks';
    else if (p === 'product' && product) {
      const slug = productSlugIndex.slugById.get(String(product.id))
        || productSlugIndex.slugById.get(String(product.dna_id))
        || product.dna_id
        || product.id;
      url = `/product/${slug}`;
    }
    else if (p === 'track') {
      const presetTrackingId = sanitizeTrackingInput(String(options?.trackingOrderId || '').trim());
      url = presetTrackingId ? `/track/${encodeURIComponent(presetTrackingId)}` : '/track';
    }
    else if (p === 'offers') url = '/offers';
    else if (p === 'terms') url = '/terms';
    else if (p === 'privacy') url = '/privacy';
    else if (p === 'cookies') url = '/cookies';
    else if (p === 'help') url = '/help';
    window.history.pushState({ page: p, productId: product?.id || null }, '', url);
    setSelected(product);
    setPage(p);
    if (p === 'checkout' && nextCheckoutItems.length > 0) {
      const checkoutTotal = nextCheckoutItems.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.qty) || 0)), 0);
      trackEvent('initiate_checkout', { items: nextCheckoutItems, total: checkoutTotal });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (p === 'track') {
      const presetOrderId = sanitizeTrackingInput(String(options?.trackingOrderId || '').trim());
      setTrackingOrderId(presetOrderId);
      setTrackingResult(null);
      setTrackingError(null);
      setPendingAutoTrackId(options?.autoTrack ? presetOrderId : '');
    }
    if (p === 'help') {
      setOpenHelpFaqs([]);
    }
  }

  function openPromotionTarget(rawTargetUrl) {
    const targetUrl = String(rawTargetUrl || '').trim();
    if (!targetUrl) return;
    if (targetUrl.startsWith('/')) {
      const routeMap = {
        '/': 'home',
        '/catalog': 'catalog',
        '/cart': 'cart',
        '/checkout': 'checkout',
        '/offers': 'offers',
        '/track': 'track',
        '/terms': 'terms',
        '/privacy': 'privacy',
        '/cookies': 'cookies',
        '/help': 'help'
      };
      const [pathOnly] = targetUrl.split('?');
      const normalizedPath = (pathOnly || targetUrl).replace(/\/+$/, '') || '/';
      const internalTarget = routeMap[normalizedPath];
      if (internalTarget) {
        if (internalTarget === 'catalog') {
          window.history.pushState({ page: 'catalog' }, '', targetUrl);
          setPage('catalog');
        } else {
          goto(internalTarget);
        }
        return;
      }
      window.location.assign(targetUrl);
      return;
    }
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }

  const runTrackById = useCallback(async (rawOrderId) => {
    const normalizedOrderId = sanitizeTrackingInput(rawOrderId || '');
    setTrackingResult(null);
    setTrackingError(null);
    if (!normalizedOrderId) {
      setTrackingError('Enter the order ID from your confirmation SMS or email.');
      return;
    }
    if (normalizedOrderId.length < 4) {
      setTrackingError('Order IDs must be at least 4 characters.');
      return;
    }
    setIsTracking(true);
    try {
      const res = await request(`/track/${encodeURIComponent(normalizedOrderId)}`);
      const normalized = res?.order
        ? { ...res.order, display_order_id: res.displayOrderId || res.order.display_order_id }
        : null;
      if (!normalized) throw new Error('Order not found');
      setTrackingResult(normalized);
      setTrackingError(null);
    } catch (err) {
      setTrackingResult(null);
      const message = err.message === 'Order not found'
        ? 'We could not locate an order with that ID. Double-check the ID or reach out to support.'
        : (err.message || 'Order not found');
      setTrackingError(message);
    } finally {
      setIsTracking(false);
    }
  }, [request, sanitizeTrackingInput]);

  // Track order by display order ID
  async function trackOrder(e) {
    e?.preventDefault();
    const normalized = sanitizeTrackingInput(trackingOrderId);
    if (normalized) {
      window.history.replaceState({ page: 'track' }, '', `/track/${encodeURIComponent(normalized)}`);
    }
    await runTrackById(normalized);
  }

  useEffect(() => {
    if (page !== 'track') return;
    if (trackingOrderId || trackingResult || trackingError || isTracking) return;
    try {
      const fromPath = extractTrackingIdFromPath(window.location.pathname);
      if (fromPath) {
        setTrackingOrderId(fromPath);
        setPendingAutoTrackId(fromPath);
        return;
      }
      const params = new URLSearchParams(window.location.search || '');
      const fromQuery = sanitizeTrackingInput(params.get('order') || '');
      if (!fromQuery) return;
      setTrackingOrderId(fromQuery);
      setPendingAutoTrackId(fromQuery);
    } catch {}
  }, [page, trackingOrderId, trackingResult, trackingError, isTracking, sanitizeTrackingInput]);

  useEffect(() => {
    if (page !== 'track') return;
    if (!pendingAutoTrackId || isTracking) return;
    const normalized = sanitizeTrackingInput(pendingAutoTrackId);
    if (!normalized || normalized.length < 4) {
      setPendingAutoTrackId('');
      return;
    }
    setPendingAutoTrackId('');
    setTrackingOrderId(normalized);
    runTrackById(normalized);
  }, [page, pendingAutoTrackId, isTracking, sanitizeTrackingInput, runTrackById]);

  // Listen for browser navigation (back/forward)
  useEffect(() => {
    function onPopState(e) {
      const url = window.location.pathname;
      const requestedProductSlug = extractRequestedProductSlug(window.location);
      if (requestedProductSlug) {
        const prod = products.find(p => String(p.id) === requestedProductSlug || String(p.dna_id) === requestedProductSlug)
          || productSlugIndex.productBySlug.get(requestedProductSlug);
        setSelected(prod || null);
        setPage('product');
        return;
      }
      if (url === '/' || url === '') { setPage('home'); return; }
      if (url.startsWith('/product/')) {
        const id = decodeURIComponent(url.split('/product/')[1]);
        const prod = products.find(p => String(p.id) === id || String(p.dna_id) === id) || productSlugIndex.productBySlug.get(id);
        setSelected(prod || null);
        setPage('product');
      } else if (url === '/catalog') setPage('catalog');
      else if (url === '/cart') setPage('cart');
      else if (url === '/checkout') setPage('checkout');
      else if (url === '/thanks') setPage('thanks');
      else if (url === '/track' || url.startsWith('/track/')) {
        const pathTrackingId = extractTrackingIdFromPath(url);
        setTrackingOrderId(pathTrackingId);
        setPendingAutoTrackId(pathTrackingId);
        setTrackingResult(null);
        setTrackingError(null);
        setPage('track');
      }
      else if (url === '/offers') setPage('offers');
      else if (url === '/upcoming' || url === '/request') {
        setPage('catalog');
        window.history.replaceState({}, '', '/catalog');
      }
      else if (url === '/terms') setPage('terms');
      else if (url === '/privacy') setPage('privacy');
      else if (url === '/cookies') setPage('cookies');
      else if (url === '/help') {
        setPage('help');
        setOpenHelpFaqs([]);
      }
      else setPage('notfound');
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [products, productSlugIndex]);

  useEffect(() => {
    if (page !== 'checkout') return;
    if (checkoutItems.length > 0) return;
    if (isBuyNow || buyNowCheckoutItems.length > 0) {
      setIsBuyNow(false);
      setBuyNowCheckoutItems([]);
      setShowDiscardBuyNowDialog(false);
    }
    window.history.replaceState({ page: 'cart' }, '', '/cart');
    setPage('cart');
    push('Your checkout is empty - add something first', 'error');
  }, [page, checkoutItems.length, isBuyNow, buyNowCheckoutItems.length, push]);

  useEffect(() => {
    const accent = (settings && settings.accentColor) ? settings.accentColor : THEME.accent;
    const rgb = hexToRgbStr(accent);
    document.documentElement.style.setProperty('--accent', accent);
    document.documentElement.style.setProperty('--accent-rgb', rgb);
     document.documentElement.style.setProperty('--accent-blue', '#38bdf8');
  }, [settings.accentColor]);

  useEffect(() => {
    let styleEl = document.getElementById(AMBIENT_STYLE_ID);
    let shouldRemove = false;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = AMBIENT_STYLE_ID;
      styleEl.textContent = `
        @keyframes shopstarter-ambient-float-1 {
          0% { transform: translate3d(-8%, -6%, 0) scale(1); }
          50% { transform: translate3d(6%, 4%, 0) scale(1.12); }
          100% { transform: translate3d(-4%, 6%, 0) scale(0.94); }
        }
        @keyframes shopstarter-ambient-float-2 {
          0% { transform: translate3d(4%, 8%, 0) scale(1); }
          50% { transform: translate3d(-8%, -4%, 0) scale(1.1); }
          100% { transform: translate3d(10%, 2%, 0) scale(0.95); }
        }
        @keyframes shopstarter-ambient-float-3 {
          0% { transform: translate3d(-6%, 8%, 0) scale(1); }
          50% { transform: translate3d(6%, -10%, 0) scale(1.08); }
          100% { transform: translate3d(-8%, 6%, 0) scale(0.96); }
        }
        @keyframes shopstarter-ambient-glimmer {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 0.92; }
        }
        @keyframes shopstarter-ambient-orbit {
          0% { transform: rotate(-6deg) scale(1.04); }
          50% { transform: rotate(9deg) scale(1.09); }
          100% { transform: rotate(-4deg) scale(1.04); }
        }
        @keyframes shopstarter-ambient-grid-motion {
          0% {
            transform: perspective(1400px) rotateX(68deg) rotateZ(-7deg) scale(1.58);
            background-position: 0px 0px, 0px 0px, 0px 0px;
          }
          50% {
            transform: perspective(1400px) rotateX(68deg) rotateZ(7deg) scale(1.5);
            background-position: 140px 80px, -90px 55px, -90px 55px;
          }
          100% {
            transform: perspective(1400px) rotateX(68deg) rotateZ(-7deg) scale(1.58);
            background-position: 0px 0px, 0px 0px, 0px 0px;
          }
        }
        @keyframes shopstarter-grid-parallax {
          0% { background-position: 0px 0px, 0px 0px; }
          100% { background-position: -360px -360px, -360px -360px; }
        }
        .pv-grid-animate {
          animation: shopstarter-grid-parallax 38s linear infinite;
          background-attachment: fixed;
        }
        @keyframes shopstarter-ambient-pulse {
          0%, 100% { opacity: 0.42; }
          50% { opacity: 0.7; }
        }
        .square-ui :where(*):not(button):not(button *):not(.keep-rounded):not(.keep-rounded *) {
          border-radius: 0 !important;
        }
        .square-ui button:not(.keep-rounded):not(.keep-rounded *) {
          border-radius: 0.5rem !important;
        }
      `;
      document.head.appendChild(styleEl);
      shouldRemove = true;
    }
    return () => {
      if (shouldRemove && styleEl?.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    };
  }, []);

  // Do not force navigation based on header search text; typeahead handles actions

  // Reset gallery view on product change
  useEffect(() => {
    if (selected) {
      const currentSelectionImages = selectedPurchaseGallery;
      const nextImage = currentSelectionImages[0] || null;
      setCurrentImage(nextImage);
      setSelectedQty(1);
      // Recently viewed
      try {
        const key = 'shopstarter_recent';
        const raw = localStorage.getItem(key);
        const list = raw ? JSON.parse(raw) : [];
        const entry = { id: selected.id, title: selected.title, img: selectedPurchaseProduct?.img || selected.img, price: selectedPurchaseProduct?.price ?? selected.price, category: selected.category, color: selected.color };
        const next = [entry, ...list.filter(x => x && x.id !== entry.id)].slice(0, 12);
        localStorage.setItem(key, JSON.stringify(next));
        trackEvent('view_product', { product_id: selected.id, title: selected.title });
      } catch {}
    }
  }, [selected, selectedPurchaseProduct, selectedPurchaseGallery]);

  useEffect(() => {
    if (page !== 'product' || !selected?.id) return;
    const slug = productSlugIndex.slugById.get(String(selected.id))
      || productSlugIndex.slugById.get(String(selected.dna_id))
      || selected.dna_id
      || selected.id;
    const base = `/product/${slug}`;
    window.history.replaceState({ page: 'product', productId: selected.id }, '', base);
  }, [page, selected, productSlugIndex]);

  // Deep link support for product page on reload: fetch by dna_id or id if not found
  useEffect(() => {
    (async () => {
      if (page !== 'product') return;
      const idOrDna = extractRequestedProductSlug(window.location);
      if (!idOrDna) return;
      // If already selected or in memory, nothing to do
      const found = products.find(p => String(p.id) === idOrDna || String(p.dna_id) === idOrDna)
        || productSlugIndex.productBySlug.get(idOrDna);
      if (found) {
        if (selected?.id && found.id === selected.id) return;
        setSelected(found);
        return;
      }
      try {
        // Try dna endpoint first
        const byDna = await request(`/products/h/${encodeURIComponent(idOrDna)}`);
        if (byDna && byDna.id) {
          if (!selected?.id || selected.id !== byDna.id) setSelected(byDna);
          return;
        }
      } catch {}
      try {
        const byId = await request(`/products/${encodeURIComponent(idOrDna)}`);
        if (byId && byId.id) {
          if (!selected?.id || selected.id !== byId.id) setSelected(byId);
          return;
        }
      } catch {}
    })();
  }, [page, products, request, selected?.id]);

  // (medium-zoom integration removed)


  useEffect(() => {
    try {
      localStorage.setItem('shopstarter_checkout_v2', JSON.stringify(checkoutState));
    } catch (err) {
      console.warn('Failed to save checkout data:', err);
    }
  }, [checkoutState]);

  const [checkoutError, setCheckoutError] = useState(null);
  const [isPlacing, setIsPlacing] = useState(false);
  const checkoutFormRef = React.useRef(null);
  // Coupon state for checkout
  const [coupon, setCoupon] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const checkoutBaseShippingCost = checkoutState.shippingOption === 'outside'
    ? Number(shippingRates.outside || 0)
    : Number(shippingRates.inside || 0);
  const checkoutShippingDiscount = checkoutFreeDelivery
    ? checkoutBaseShippingCost
    : Math.max(0, Math.min(checkoutBaseShippingCost, Number(checkoutDeliveryDiscount || 0)));
  const checkoutShippingCost = Math.max(0, checkoutBaseShippingCost - checkoutShippingDiscount);
  const appliedCouponDiscount = appliedCoupon ? Math.max(0, Number(couponDiscount || 0)) : 0;
  const checkoutBkashCashoutCharge = checkoutState.paymentMethod === 'bkash' && settings?.bkashCashoutEnabled
    ? Math.max(0, Number(settings?.bkashCashoutAmount ?? 20) || 0)
    : 0;
  const checkoutGrandTotal = Math.max(0, checkoutSubtotal + checkoutShippingCost + checkoutBkashCashoutCharge - appliedCouponDiscount);

  const discardBuyNowCheckout = useCallback(() => {
    setBuyNowCheckoutItems([]);
    setIsBuyNow(false);
    setShowDiscardBuyNowDialog(false);
    window.history.pushState({ page: 'cart' }, '', '/cart');
    setPage('cart');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    push('Buy now item discarded. Your cart is unchanged.', 'info');
  }, [push]);

  const handleCheckoutFormKeyDown = useCallback((event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.nativeEvent?.isComposing) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.tagName === 'BUTTON') {
      event.preventDefault();
      return;
    }
    const form = checkoutFormRef.current;
    if (!form) return;
    event.preventDefault();
    const fields = Array.from(
      form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="file"]):not([disabled]), select:not([disabled]), textarea:not([disabled])')
    ).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.tabIndex < 0) return false;
      if (element.offsetParent === null) return false;
      return true;
    });
    const currentIndex = fields.indexOf(target);
    if (currentIndex === -1) return;
    const nextField = fields[currentIndex + 1];
    if (!nextField || !(nextField instanceof HTMLElement)) {
      target.blur();
      return;
    }
    nextField.focus();
    if (typeof nextField.select === 'function' && nextField.tagName === 'INPUT') {
      nextField.select();
    }
  }, []);

  const applyCouponCode = useCallback(async () => {
    try {
      setCouponApplying(true);
      setCouponError('');
      const itemsPayload = checkoutItems.map(i => ({
        id: i.productId || i.id,
        productId: i.productId || i.id,
        variantId: i.variantId || null,
        price: i.price,
        qty: i.qty,
        category: i.category || null
      }));
      const res = await fetch(`${API_BASE}/deals/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: coupon.trim(), items: itemsPayload, total: checkoutSubtotal })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.valid) {
        setAppliedCoupon(null);
        setCouponDiscount(0);
        setCouponError(data.error || 'Invalid code');
        return;
      }
      setAppliedCoupon({ code: coupon.trim(), promotion: data.promotion });
      setCouponDiscount(Math.max(0, Number(data.discount || 0)));
    } catch {
      setAppliedCoupon(null);
      setCouponDiscount(0);
      setCouponError('Could not validate code');
    } finally {
      setCouponApplying(false);
    }
  }, [checkoutItems, coupon, checkoutSubtotal]);

  const clearAppliedCoupon = useCallback(() => {
    setCoupon('');
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponError('');
  }, []);

  function validateCheckout(data) {
    const errors = [];
    if (!data.firstName.trim()) errors.push('First name is required');
    if (!data.house.trim()) errors.push('House number and street name is required');
    if (!data.city.trim()) errors.push('Town / City is required');
    if (!/^\+?\d{7,15}$/.test(data.phone.replace(/\s+/g, ''))) errors.push('Phone number looks invalid');
    if (data.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) errors.push('Email looks invalid');
    if (data.paymentMethod !== 'cod') {
      if (!data.bkashFrom.trim()) errors.push('bKash number (from) is required');
      if (!data.bkashTxn.trim()) errors.push('bKash transaction ID is required');
    }
    return errors;
  }

  async function placeOrder(e) {
    e?.preventDefault();
    setCheckoutError(null);
    if (!checkoutItems || checkoutItems.length === 0) {
      setCheckoutError('Your cart is empty');
      push('Your cart is empty - add something first', 'error');
      return;
    }
    
    const errs = validateCheckout(checkoutState);
    if (errs.length) {
      setCheckoutError(errs.join('; '));
      push('Fix errors on the form', 'error');
      return;
    }

    try {
      setIsPlacing(true);
      const itemsPayload = checkoutItems.map((i) => ({
        id: i.productId || i.id,
        productId: i.productId || i.id,
        variantId: i.variantId || null,
        variantLabel: i.variantLabel || null,
        variantSku: i.variantSku || null,
        title: i.title,
        price: i.price,
        originalPrice: Number(i.originalPrice) > Number(i.price) ? Number(i.originalPrice) : null,
        qty: i.qty,
        img: i.img || null,
        category: i.category || null,
        color: i.color || null
      }));
      
      const orderData = {
        items: itemsPayload,
        customer: {
          firstName: checkoutState.firstName,
          lastName: checkoutState.lastName,
          email: checkoutState.email.trim(),
          phone: checkoutState.phone
        },
        shipping: {
          country: checkoutState.country,
          house: checkoutState.house,
          city: checkoutState.city,
          option: checkoutState.shippingOption,
          cost: checkoutShippingCost
        },
        payment: checkoutState.paymentMethod === 'cod' 
          ? { method: 'cod' }
          : {
              method: 'bkash',
              bkashFrom: checkoutState.bkashFrom,
              bkashTxn: checkoutState.bkashTxn,
              bkashScreenshotUrl: checkoutState.bkashScreenshotUrl || '',
              cashoutCharge: checkoutBkashCashoutCharge
            },
        total: checkoutGrandTotal,
        notes: checkoutState.notes
      };
      if (appliedCoupon?.code) orderData.promoCode = appliedCoupon.code;

      const resp = await request('/orders', {
        method: 'POST',
        body: JSON.stringify(orderData)
      });
      if (resp && resp.displayOrderId) {
        try {
          localStorage.setItem('shopstarter_remember', rememberDetails ? 'true' : 'false');
          if (rememberDetails) {
            const addr = {
              firstName: checkoutState.firstName,
              lastName: checkoutState.lastName,
              email: checkoutState.email.trim(),
              phone: checkoutState.phone,
              house: checkoutState.house,
              city: checkoutState.city,
              shippingOption: checkoutState.shippingOption
            };
            localStorage.setItem('shopstarter_address', JSON.stringify(addr));
          }
        } catch {}
        setLastOrderDisplayId(resp.displayOrderId);
        try { localStorage.setItem('shopstarter_last_order_id', resp.displayOrderId); } catch {}
      }
      trackEvent('purchase', { total: checkoutGrandTotal, items: itemsPayload.map(i => ({ id: i.productId || i.id, qty: i.qty, price: i.price })) });
      if (hasBuyNowCheckout) {
        setBuyNowCheckoutItems([]);
        setIsBuyNow(false);
      } else {
        setCart([]);
      }
      push('Order placed successfully!', 'info');
      // Trigger brief confetti animation
      try {
        if (!isLiteMode) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 4000);
        }
      } catch {}
      setPage('thanks');
    } catch (err) {
      console.error('Order failed', err);
      setCheckoutError(err.message || 'Order failed – please try again');
      push('Order could not be placed', 'error');
    } finally {
      setIsPlacing(false);
    }
  }

  const Badge = ({ children }) => (
    <span className="inline-block text-xs font-semibold px-2 py-1 rounded-full bg-[rgba(var(--accent-rgb),0.12)] text-[var(--accent)]">
      {children}
    </span>
  );

  const LoadingSpinner = () => (
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-2xl sm:rounded-none border border-gray-100 bg-white/90 p-3">
          <div className="h-44 sm:h-56 bg-gray-200 rounded-xl sm:rounded-none" />
          <div className="mt-3 space-y-1">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
            <div className="h-5 bg-gray-300 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );

  // Maintenance mode overlay
  const isMaintenance = !!settings.maintenanceMode;

  // Overlay scrollbar (client): hidden until scroll; animates from right
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isLiteMode) {
      const inst = document.getElementById('pv-scrollbar-client');
      if (inst && inst.isConnected) inst.remove();
      return undefined;
    }
    let styleTag = document.getElementById('pv-scrollbar-client-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'pv-scrollbar-client-style';
      styleTag.innerHTML = `
html{scrollbar-gutter:stable both-edges}html,body{scrollbar-width:none}body::-webkit-scrollbar{width:0;height:0}
#pv-scrollbar-client{position:fixed;right:10px;top:10px;bottom:10px;width:10px;pointer-events:none;z-index:1000;opacity:0;transform:translateX(10px);transition:opacity .24s ease-out,transform .24s ease-out}
#pv-scrollbar-client.show{opacity:1;transform:translateX(0)}
#pv-scrollbar-client .thumb{position:absolute;left:0;width:10px;border-radius:9999px;background:rgba(17,24,39,.35);box-shadow:0 2px 8px rgba(0,0,0,.12)}
@media(hover:hover){#pv-scrollbar-client .thumb:hover{background:rgba(17,24,39,.5)}}`;
      document.head.appendChild(styleTag);
    }
    let bar = document.getElementById('pv-scrollbar-client');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'pv-scrollbar-client';
      const t = document.createElement('div');
      t.className = 'thumb';
      bar.appendChild(t);
      document.body.appendChild(bar);
    }
    const thumb = bar.querySelector('.thumb');
    let hideTimer = null;
    const showBar = () => {
      bar.classList.add('show');
      if (hideTimer) window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => bar.classList.remove('show'), 700);
    };
    const updateThumb = () => {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
      const scrollHeight = Math.max(body.scrollHeight, doc.scrollHeight, body.offsetHeight, doc.offsetHeight, body.clientHeight, doc.clientHeight);
      const vh = window.innerHeight;
      const barPadding = 20;
      const barHeight = Math.max(0, vh - barPadding);
      const maxScroll = Math.max(1, scrollHeight - vh);
      if (scrollHeight <= vh + 1) { bar.style.display = 'none'; return; }
      bar.style.display = 'block';
      const minThumb = 28;
      const thumbH = Math.max(minThumb, Math.round((vh * vh) / scrollHeight));
      const ratio = Math.min(1, Math.max(0, scrollTop / maxScroll));
      const top = 10 + Math.round((barHeight - thumbH) * ratio);
      thumb.style.height = thumbH + 'px';
      thumb.style.transform = `translateY(${top}px)`;
    };
    const onScroll = () => { updateThumb(); showBar(); };
    const onResize = () => { updateThumb(); };
    updateThumb();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      const inst = document.getElementById('pv-scrollbar-client');
      if (inst && inst.isConnected) inst.remove();
    };
  }, [isLiteMode]);

  // Responsive tweak: add viewport meta if not present
  useEffect(() => {
    const id = 'shopstarter-viewport';
    if (!document.getElementById(id)) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1, shrink-to-fit=no';
      meta.id = id;
      document.head.appendChild(meta);
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const styleId = 'shopstarter-motion-styles';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `@keyframes shopstarter-fade-up {from {opacity:0; transform: translateY(12px);} to {opacity:1; transform: translateY(0);}}
  @keyframes shopstarter-shimmer {0% {transform: translateX(-100%);} 100% {transform: translateX(100%);}}`;
    document.head.appendChild(style);
  }, []);

  // Basic image protection: deter right-click and image dragging
  useEffect(() => {
    const onContext = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'IMG' || (t.closest && t.closest('img')))) {
        e.preventDefault();
      }
    };
    const onDragStart = (e) => {
      const t = e.target;
      if (t && t.tagName === 'IMG') {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', onContext);
    document.addEventListener('dragstart', onDragStart);
    return () => {
      document.removeEventListener('contextmenu', onContext);
      document.removeEventListener('dragstart', onDragStart);
    };
  }, []);

  const hasMobileBottomBar =
    (page === 'product' && selected)
    || (page === 'cart' && cart.length > 0)
    || (page === 'checkout' && checkoutItems.length > 0);
  const cartButtonBottomClass = hasMobileBottomBar ? 'bottom-20' : 'bottom-6';
  const filtersButtonBottomClass = hasMobileBottomBar ? 'bottom-40' : 'bottom-24';
  const hasMobileCartFab = isMobileViewport && page !== 'checkout';
  const hasMobileFiltersFab = isMobileViewport && page === 'catalog';
  let backToTopBottomRem = showConsent ? 7.5 : 1.5;
  if (isMobileViewport) {
    if (hasMobileCartFab) {
      backToTopBottomRem = Math.max(backToTopBottomRem, hasMobileBottomBar ? 9.5 : 6);
    }
    if (hasMobileFiltersFab) {
      backToTopBottomRem = Math.max(backToTopBottomRem, 10);
    }
    if (showConsent) {
      backToTopBottomRem = Math.max(backToTopBottomRem, 11);
    }
  }
  const backToTopBottom = `${backToTopBottomRem}rem`;
  const supportEmail = String(settings?.supportEmail || '').trim();
  const supportEmailHref = supportEmail ? `mailto:${supportEmail}` : '';

  return (
  <ErrorBoundary>
  <div
    className="relative min-h-screen text-gray-900 antialiased flex flex-col overflow-x-hidden square-ui"
    style={{
      isolation: 'isolate',
      backgroundColor: '#f8fafc',
      backgroundImage: 'linear-gradient(rgba(15,23,42,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.1) 1px, transparent 1px)',
      backgroundSize: '24px 24px, 24px 24px',
      backgroundPosition: '0 0, 0 0, 0 0',
      animation: isLiteMode ? 'none' : 'shopstarter-grid-parallax 22s linear infinite',
      willChange: isLiteMode ? 'auto' : 'background-position'
    }}
  >
      <a href="#main-content" className="absolute left-2 top-2 z-50 px-3 py-2 rounded bg-white text-sm border border-gray-300 shadow opacity-0 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]" style={{ clip: 'rect(1px,1px,1px,1px)' }} onFocus={(e) => { e.currentTarget.style.clip = 'auto'; }} onBlur={(e) => { e.currentTarget.style.clip = 'rect(1px,1px,1px,1px)'; }}>Skip to content</a>
      {showStartupLoader && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-white/95 backdrop-blur-sm">
          <div className="w-full max-w-md px-6">
            <div className="rounded-[28px] border border-gray-200 bg-white/95 p-6 sm:p-8 shadow-2xl">
              <div className="flex items-center gap-4">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-[18px] border border-gray-200 bg-gradient-to-br from-white via-white to-[rgba(var(--accent-rgb),0.14)] shadow-sm overflow-hidden">
                  <img
                    src={startupLogoSrc}
                    alt={`${settings.siteName || 'ItemSmith'} logo`}
                    className="h-10 w-10 object-contain"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = '/logo.png';
                    }}
                  />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-gray-500">Loading</p>
                  <h2 className="truncate text-xl font-semibold text-gray-900">{settings.siteName || 'ItemSmith'}</h2>
                </div>
              </div>
              <div className="mt-6">
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-[var(--accent)] transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(100, Math.max(0, startupLoadProgress))}%` }}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <span>Preparing storefront assets...</span>
                  <span>{Math.min(100, Math.max(0, startupLoadProgress))}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div ref={headerRef} className={`fixed top-0 left-0 right-0 z-50`}>
        {headerBarPromotion && (
          <div className="text-[11px] sm:text-xs bg-gray-900 text-gray-100">
            <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center sm:justify-between gap-3 px-4 sm:px-6 lg:px-8 py-2">
              <span className="inline-flex items-center gap-2 font-medium tracking-wide">
                <span className="inline-flex h-2 w-2 rounded-full bg-[var(--accent)] animate-pulse" aria-hidden="true" />
                {headerBarPromotion.header_text || headerBarPromotion.title || headerBarPromotion.description || 'Limited-time offer'}
              </span>
              {headerBarPromotion.target_url ? (
                <button
                  type="button"
                  onClick={() => openPromotionTarget(headerBarPromotion.target_url)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-3 py-1 uppercase tracking-widest text-[10px] font-semibold text-gray-100 hover:bg-white hover:text-gray-900 transition"
                >
                  Open offer
                </button>
              ) : null}
            </div>
          </div>
        )}
        
        <header>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div
            className="relative w-full px-4 sm:px-6 will-change-transform keep-rounded"
            style={{
              transition: isLiteMode
                ? 'margin 180ms cubic-bezier(.2,.8,.2,1), padding 180ms cubic-bezier(.2,.8,.2,1), border-radius 220ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms ease-out, background-color 180ms ease-out'
                : 'margin 220ms cubic-bezier(.2,.8,.2,1), padding 220ms cubic-bezier(.2,.8,.2,1), border-radius 260ms cubic-bezier(.2,.8,.2,1), box-shadow 240ms ease-out, background-color 240ms ease-out, backdrop-filter 240ms ease-out',
              marginTop: `calc(1.25rem - ${0.5 * headerBlend}rem)`,
              paddingTop: `${20 - (10 * headerBlend)}px`,
              paddingBottom: `${20 - (10 * headerBlend)}px`,
              borderRadius: `${28 + (20 * headerBlend)}px`,
              backdropFilter: isLiteMode ? 'none' : `saturate(${100 + Math.round(50 * headerBlend)}%) blur(${Math.round(2 + 8 * headerBlend)}px)`,
              WebkitBackdropFilter: isLiteMode ? 'none' : `saturate(${100 + Math.round(50 * headerBlend)}%) blur(${Math.round(2 + 8 * headerBlend)}px)`,
              // Blend into page background at top on all pages
              backgroundColor: `rgba(255,255,255, ${0 + (0.82 * headerBlend)})`,
              boxShadow:
                headerBlend > 0
                  ? (isLiteMode
                      ? `0 8px 20px rgba(59,26,77, ${0.05 + 0.08 * headerBlend})`
                      : `0 18px 42px rgba(59,26,77, ${0.06 + 0.12 * headerBlend})`)
                  : 'none',
              border: '1px solid',
              borderColor: headerBlend > 0 ? `rgba(0,0,0, ${0.12 + (0.12 * headerBlend)})` : 'transparent'
            }}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
                <button
                  onClick={() => { goto('home'); setMobileOpen(false); }}
                  aria-label="ItemSmith home"
                  className="flex items-center gap-3"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--accent)] shadow-lg relative overflow-hidden">
                    <img src={(settingsReady && resolveMediaUrl(settings.logoUrl)) || (settingsReady ? '/logo.png' : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==')} alt="ItemSmith logo" className="h-10 w-10 object-contain rounded-2xl" />
                    {settings.logoAccentUrl && (
                      <img src={resolveMediaUrl(settings.logoAccentUrl)} alt="Logo accent" className="absolute -right-1 -bottom-1 h-5 w-5 object-contain opacity-90" />
                    )}
                  </span>
                  <span className="hidden sm:flex flex-col text-left">
                    <span className="text-sm font-semibold uppercase text-gray-900">{settings.siteName || 'ItemSmith'}</span>
                  </span>
                </button>
                <nav className="hidden lg:flex items-center gap-1">
                  {primaryNav.map(item => {
                    const isActive = item.matchPage ? page === item.matchPage : false;
                    const isCollections = item.key === 'shop';
                    return (
                      <div
                        key={item.key}
                        className="relative"
                        onMouseEnter={() => {
                          if (isCollections) openCollectionsMenu();
                        }}
                        onMouseLeave={() => {
                          if (isCollections) closeCollectionsMenuDelayed();
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (isCollections && catalogCategories.length > 0) {
                              handleCollectionSelect('All');
                              return;
                            }
                            item.action();
                          }}
                          onTouchStart={() => {
                            if (isCollections && catalogCategories.length > 0) {
                              openCollectionsMenu();
                            }
                          }}
                          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${isActive ? 'bg-gray-900 text-white shadow-lg' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                        >
                          {item.label}
                          {item.meta && (
                            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-gray-600">
                              {item.meta}
                            </span>
                          )}
                        </button>

                        {isCollections && collectionsMenuOpen && catalogCategories.length > 0 && (
                          <div
                            className="absolute left-0 top-full z-50 min-w-[220px] pt-2"
                            onMouseEnter={openCollectionsMenu}
                            onMouseLeave={() => closeCollectionsMenuDelayed(360)}
                          >
                            <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                              <button
                                type="button"
                                onClick={() => handleCollectionSelect('All')}
                                className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100"
                              >
                                All Collections
                              </button>
                              {catalogCategories.map((category) => (
                                <button
                                  key={category}
                                  type="button"
                                  onClick={() => handleCollectionSelect(category)}
                                  className="block w-full rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  {category}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </div>
              <div className="flex-1 px-3 sm:px-5 sm:max-w-2xl relative">
                <label className="relative block">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    onKeyDown={(e) => {
                      if (!typeaheadResults || typeaheadResults.length === 0) return;
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setTypeaheadOpen(true);
                        setTypeaheadIndex((idx) => (idx + 1) % typeaheadResults.length);
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setTypeaheadOpen(true);
                        setTypeaheadIndex((idx) => (idx <= 0 ? typeaheadResults.length - 1 : idx - 1));
                      } else if (e.key === 'Enter') {
                        if (typeaheadIndex >= 0 && typeaheadIndex < typeaheadResults.length) {
                          const p = typeaheadResults[typeaheadIndex];
                          if (p) { setSelected(p); goto('product', p, false, p.img); }
                          setTypeaheadOpen(false);
                        }
                      } else if (e.key === 'Escape') {
                        setTypeaheadOpen(false);
                      }
                    }}
                    type="search"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={isTypeaheadOpen}
                    aria-controls="header-search-listbox"
                    aria-describedby="header-search-help header-search-status"
                    placeholder="Search silhouettes, colors, textures."
                    className={`w-full rounded-full border bg-white/80 px-11 py-2.5 text-sm shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent ${hasScrolled ? 'border-gray-200' : 'border-white/70'}`}
                  />
                  <span className="absolute inset-y-0 left-4 flex items-center text-gray-400" aria-hidden="true">
                    <Icon.Search width={18} height={18} />
                  </span>
                </label>
                <div id="header-search-help" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px,1px,1px,1px)' }}>
                  Use arrow keys to navigate results and Enter to open.
                </div>
                {isTypeaheadOpen && (
                  <ul
                    id="header-search-listbox"
                    role="listbox"
                    className="absolute left-0 right-0 mt-2 max-h-80 overflow-auto rounded-2xl sm:rounded-none border border-gray-200 bg-white shadow-2xl z-50"
                  >
                    {typeaheadResults.map((p, i) => (
                      <li
                        key={p.id}
                        role="option"
                        aria-selected={i === typeaheadIndex}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${i === typeaheadIndex ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelected(p);
                          setTypeaheadOpen(false);
                          goto('product', p, false, p.img);
                        }}
                      >
                        <PVImage src={p.img} alt="" onError={handleImgError} className="h-8 w-8 rounded object-cover" imgProps={{ variant: 'thumb' }} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{p.title}</div>
                          <div className="text-xs text-gray-500 truncate">{p.category}{p.color ? ` / ${p.color}` : ''}</div>
                        </div>
                        <PriceDisplay
                          price={p.displayPrice ?? p.price}
                          originalPrice={p.displayOriginalPrice ?? p.originalPrice}
                          showFrom={!!p.hasVariants}
                          align="right"
                          originalClassName="text-[10px] text-gray-400 line-through"
                          currentClassName="text-sm font-semibold text-[var(--accent)]"
                        />
                      </li>
                    ))}
                  </ul>
                )}
                <div id="header-search-status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px,1px,1px,1px)' }}>
                  {isTypeaheadOpen ? `${typeaheadResults.length} results available.` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goto('cart')}
                  className="relative hidden sm:inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm hover:border-[var(--accent)] hover:text-gray-900 transition"
                >
                  <Icon.Cart width={20} height={20} />
                  {cart.length > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-semibold text-white">
                      {cart.reduce((s, i) => s + i.qty, 0)}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setMobileOpen(v => !v)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:border-[var(--accent)] hover:text-gray-900 transition lg:hidden"
                  aria-label="Toggle navigation"
                  aria-expanded={mobileOpen}
                >
                  <Icon.Menu width={20} height={20} />
                </button>
              </div>

              </div>
            </div>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white/95 backdrop-blur">
            <nav className="px-4 py-4 flex flex-col gap-2">
              {primaryNav.map(item => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    item.action();
                    setMobileOpen(false);
                  }}
                  className="w-full rounded-2xl sm:rounded-none border border-gray-200 bg-white px-4 py-3 text-left text-sm font-semibold text-gray-700 shadow-sm hover:border-[var(--accent)] hover:text-gray-900"
                >
                  <div>{item.label}</div>
                  {item.meta && (
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mt-1">
                      {item.meta}
                    </div>
                  )}
                </button>
              ))}
            </nav>
          </div>
        )}
        </header>
      </div>

      <main id="main-content" className="flex-1" style={{ paddingTop: reservedHeaderHeight || headerHeight, '--header-height': `${reservedHeaderHeight || headerHeight}px` }}>

      {showCampaignPopup && campaignPromotion && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6 sm:py-12">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => handleCampaignDismiss('day')} />
          <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-2xl">
            <button
              type="button"
              onClick={() => handleCampaignDismiss('day')}
              className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center text-white mix-blend-difference hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              aria-label="Close campaign announcement"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            {campaignPromotion.target_url ? (
              <button
                type="button"
                onClick={() => {
                  openPromotionTarget(campaignPromotion.target_url);
                  handleCampaignDismiss('session');
                }}
                className="block w-full text-left"
              >
                <PVImage
                  src={resolveMediaUrl(campaignPromotion.image_url || campaignPromotion.imageUrl)}
                  alt={campaignPromotion.title || 'Promotion'}
                  className="w-full h-auto object-cover"
                  imgProps={{ loading: 'eager' }}
                />
              </button>
            ) : (
              <PVImage
                src={resolveMediaUrl(campaignPromotion.image_url || campaignPromotion.imageUrl)}
                alt={campaignPromotion.title || 'Promotion'}
                className="w-full h-auto object-cover"
                imgProps={{ loading: 'eager' }}
              />
            )}
          </div>
        </div>
      )}

      {/* Home page experience */}
      {page === 'home' && (
        <HomePage
          settings={settings}
          heroBanners={heroBanners}
          bannerIdx={bannerIdx}
          onPrevHero={handlePrevHero}
          onNextHero={handleNextHero}
          onSelectHero={(i) => { try { setBannerIdx(i); } catch {} }}
          featured={featured}
          cartCount={cart.length}
          goto={goto}
          addToCart={addToCart}
          buyNow={buyNow}
          currency={currency}
          PVImage={PVImage}
          resolveMediaUrl={resolveMediaUrl}
          handleImgError={handleImgError}
          liteMode={isLiteMode}
        />
      )}
      {page === 'home' && false && (currentBanner || heroBgSrc || bannerImgSrc) && (
        <>
          <section
            className="relative overflow-hidden bg-center bg-cover min-h-[50vh] md:min-h-[60vh] flex items-center"
            style={{
              backgroundImage: `url(${heroBgSrc || bannerImgSrc || resolveMediaUrl(currentBanner?.image)})`,
              cursor: bannerImageLink ? 'pointer' : 'default'
            }}
            onClick={() => {
              const target = bannerImageLink;
              if (!target) return;
              try {
                if (target.startsWith('http')) {
                  window.open(target, '_blank', 'noopener,noreferrer');
                } else if (target.startsWith('/')) {
                  if (target === '/catalog') goto('catalog');
                  else if (target.startsWith('/product')) goto('product');
                  else {
                    window.history.pushState({ page: 'home' }, '', target);
                  }
                }
              } catch {}
            }}
          >
            {heroOverlaySrc && (
              <img
                src={heroOverlaySrc}
                alt=""
                loading="eager"
                fetchpriority="high"
                decoding="async"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
                style={{ opacity: heroOverlayOpacity }}
                onLoad={() => {
                  setHeroOverlayOpacity(1);
                  if (heroFadeTimer.current) clearTimeout(heroFadeTimer.current);
                  heroFadeTimer.current = setTimeout(() => {
                    setHeroBgSrc(heroOverlaySrc);
                    setHeroOverlayOpacity(0);
                    setHeroOverlaySrc('');
                  }, 350);
                }}
                onError={(e) => {
                  try {
                    const img = e.currentTarget;
                    if (heroAltSrc && img.src !== heroAltSrc) {
                      img.src = heroAltSrc;
                      return;
                    }
                  } catch {}
                  // Fallback: keep current background; hide overlay
                  setHeroOverlayOpacity(0);
                  setHeroOverlaySrc('');
                }}
              />
            )}
            {heroHasOverlayContent && (
              <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/55 to-transparent"></div>
            )}
            <div className="relative max-w-6xl mx-auto h-full flex items-center px-4 sm:px-6 lg:px-8">
              <div className="max-w-xl space-y-5 text-center md:text-left md:-ml-[24rem] lg:-ml-[32rem]">
                  {bannerHeadline && (
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-gray-900">
                      {bannerHeadline}
                    </h1>
                  )}
                  {bannerSubheadline && (
                    <p className="max-w-xl mx-auto md:mx-0 text-base sm:text-lg leading-relaxed text-gray-700">
                      {bannerSubheadline}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    {bannerCtaLabel && bannerLink && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleBannerCtaClick(); }}
                        className="inline-flex items-center gap-3 rounded-full sm:rounded-none border border-black bg-[var(--accent)] px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-[0_0_0_2px_#000,0_4px_0_0_#000] transition-transform duration-100 hover:-translate-y-[1px] hover:shadow-[0_0_0_2px_#000,0_6px_0_0_#000]"
                      >
                        {bannerCtaLabel}
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M5 12h14" strokeLinecap="round" />
                          <path d="M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                    {!bannerCtaLabel && !bannerLink && isDefaultHeroBanner && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); goto('catalog'); }}
                        className="inline-flex items-center gap-3 rounded-full sm:rounded-none border border-black bg-[var(--accent)] px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-[0_0_0_2px_#000,0_4px_0_0_#000] transition-transform duration-100 hover:-translate-y-[1px] hover:shadow-[0_0_0_2px_#000,0_6px_0_0_#000]"
                      >
                        Browse the smithy
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M5 12h14" strokeLinecap="round" />
                          <path d="M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                {/* moved nav/dots outside inner container to anchor to full banner */}
              </div>
            </div>
            {hasMultipleHeroBanners && (
              <div className="absolute bottom-6 right-6 hidden sm:flex items-center gap-2 z-10 pointer-events-auto">
                <button
                  type="button"
                  onClick={handlePrevHero}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white/85 backdrop-blur text-gray-700 hover:border-[var(--accent)] hover:text-gray-900"
                  aria-label="Previous banner"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleNextHero}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white/85 backdrop-blur text-gray-700 hover:border-[var(--accent)] hover:text-gray-900"
                  aria-label="Next banner"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            )}
            {hasMultipleHeroBanners && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-2 z-10 pointer-events-auto">
                {heroBanners.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to banner ${i + 1}`}
                    aria-current={i === bannerIdx ? 'true' : 'false'}
                    className="appearance-none bg-transparent p-0 m-0 border-0 inline-flex h-5 w-5 items-center justify-center"
                    onClick={() => setBannerIdx(i)}
                  >
                    <span
                      className={`block h-2 w-2 rounded-full transition ${i === bannerIdx ? 'bg-gray-900' : 'bg-white/70 hover:bg-white'}`}
                    />
                  </button>
                ))}
              </div>
            )}
          </section>

          {whatsNew.length > 0 && (
            <section className="max-w-6xl mx-auto px-4 sm:px-4 lg:px-8 py-8 sm:py-12">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gray-400">Studio edit</p>
                  <h3 className="text-2xl font-semibold text-gray-900">Fresh arrivals</h3>
                </div>
                <button onClick={goToNewArrivals} className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--accent)] hover:underline">
                  Shop new arrivals
                </button>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {whatsNew.slice(0, homeCols).map((product, idx) =>
                  renderHomeProductCard(product, { badge: 'New arrival', badgeTone: 'bg-emerald-500 text-white', delay: idx * 0.05 })
                )}
              </div>
            </section>
          )}

          {trendingProducts.length > 0 && (
            <section className="max-w-6xl mx-auto px-4 sm:px-4 lg:px-8 py-8 sm:py-12">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gray-400">Community picks</p>
                  <h3 className="text-2xl font-semibold text-gray-900">Trending now</h3>
                </div>
                <button onClick={goToTrending} className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--accent)] hover:underline">
                  View all trending
                </button>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {trendingProducts.slice(0, homeCols).map((product, idx) =>
                  renderHomeProductCard(product, { badge: 'Trending', badgeTone: 'bg-rose-500 text-white', delay: idx * 0.05 })
                )}
              </div>
            </section>
          )}

          {bestSellers.length > 0 && (
            <section className="max-w-6xl mx-auto px-4 sm:px-4 lg:px-8 py-8 sm:py-12">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gray-400">Top picks</p>
                  <h3 className="text-2xl font-semibold text-gray-900">Best sellers</h3>
                </div>
                <button onClick={() => goto('catalog')} className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--accent)] hover:underline">
                  Shop best sellers
                </button>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {bestSellers.slice(0, homeCols).map((product, idx) =>
                  renderHomeProductCard(product, { badge: 'Best seller', badgeTone: 'bg-amber-500 text-white', delay: idx * 0.05 })
                )}
              </div>
            </section>
          )}

          {/* Featured removed per request */}

          <section className="max-w-6xl mx-auto px-4 sm:px-4 lg:px-8">
            <div className="rounded-2xl sm:rounded-none border border-gray-200 bg-white p-6 shadow-sm flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Need a hand?</h3>
                <p className="text-sm text-gray-600">Visit the Help Center for delivery updates, returns guidance, and direct contact options.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => goto('help')}
                  className="inline-flex items-center rounded-full sm:rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:shadow-lg"
                >
                  Go to Help Center
                </button>
                <button
                  type="button"
                  onClick={() => goto('track')}
                  className="inline-flex items-center rounded-full sm:rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:border-[var(--accent)]"
                >
                  Track an order
                </button>
              </div>

              </div>
          </section>

        </>
      )}
      {page === 'home' && false && !(currentBanner || heroBgSrc || bannerImgSrc) && (
        <section className="relative overflow-hidden min-h-[50vh] md:min-h-[60vh] flex items-center bg-gradient-to-r from-gray-100 via-white to-gray-50">
          <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-white/40 via-white/60 to-white/40" />
          <div className="relative max-w-6xl mx-auto h-full flex items-center px-4 sm:px-6 lg:px-8">
            <div className="max-w-xl space-y-4">
              <div className="h-8 sm:h-10 w-2/3 bg-gray-200 rounded" />
              <div className="h-4 w-5/6 bg-gray-100 rounded" />
              <div className="h-4 w-3/5 bg-gray-100 rounded" />
            </div>
          </div>
        </section>
      )}

{page === 'offers' && (
      <section className="max-w-6xl mx-auto px-4 sm:px-0 bg-white rounded-2xl sm:rounded-none shadow-sm p-6 mt-6">
        <h2 className="text-2xl font-semibold mb-2 text-center">Offers</h2>
        <p className="text-sm text-gray-600 text-center mb-6">Tap any offer image to view details.</p>
        {offersPromotions.length === 0 ? (
          <div className="text-center text-gray-600 py-8">No active offers right now. Please check back soon.</div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {offersPromotions.map((promo) => (
              <button
                key={promo.id}
                type="button"
                onClick={() => {
                  setSelectedPromotion(promo);
                  setPromoModalOpen(true);
                }}
                className="group overflow-hidden rounded-2xl sm:rounded-none border border-gray-200 bg-white shadow-sm hover:shadow-lg transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              >
                <PVImage
                  src={resolveMediaUrl(promo.image_url || promo.imageUrl)}
                  alt={promo.title || 'Promotion'}
                  className="w-full aspect-[16/3] object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <span className="sr-only">{promo.title || 'Open promotion details'}</span>
              </button>
            ))}
          </div>
        )}
      </section>
    )}
    {/* Track your order page */}
    {page === 'track' && (
      <section className="max-w-4xl mx-auto w-full bg-white rounded-2xl sm:rounded-none shadow-md border border-gray-100 p-6 sm:p-8 mt-8">
        <h2 className="text-2xl font-semibold mb-2 text-center">Track your order</h2>
        <form onSubmit={trackOrder} className="flex flex-col gap-4 mt-4">
          <label className="font-medium text-sm" htmlFor="track-order-input">Order ID</label>
          <input
            id="track-order-input"
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            value={trackingOrderId}
            onChange={e => handleTrackingInputChange(e.target.value)}
            placeholder={`Example: ${trackingExampleId || '83425190'} or 1A2B3C`}
            maxLength={36}
            className="w-full rounded-lg border border-gray-200 px-4 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent tracking-wider"
            autoFocus
          />
          <p className="-mt-2 text-xs text-gray-500">Only letters, numbers, and dashes are allowed. We&apos;ll match either your display ID or the full UUID from email/SMS receipts.</p>
          {trackingOrderId.length > 0 && !isTrackingIdValid && (
            <p className="-mt-3 text-xs text-red-500">Order IDs must be at least 4 characters.</p>
          )}
          <button
            type="submit"
            className="w-full py-2 rounded-full sm:rounded-lg bg-[var(--accent)] text-white font-semibold text-lg hover:bg-yellow-500 transition"
            disabled={isTracking || !isTrackingIdValid}
          >
            {isTracking ? 'Tracking...' : 'Track Order'}
          </button>
        </form>
        {trackingError && (
          <div className="mt-4 rounded-2xl sm:rounded-none border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3 text-sm text-red-700">
              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 font-semibold">!</div>
              <div className="flex-1">
                <div className="font-semibold">We couldn't find that order.</div>
                <p className="mt-1 leading-relaxed">{trackingError}</p>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-red-600">
                  <button
                    type="button"
                    onClick={resetTrackingSearch}
                    className="rounded-full border border-red-200 bg-white px-3 py-1 font-semibold text-red-600 hover:bg-red-100"
                  >
                    Clear &amp; try again
                  </button>
                  {supportEmailHref ? (
                    <a href={supportEmailHref} className="underline">Contact support</a>
                  ) : (
                    <button type="button" onClick={() => goto('help')} className="underline">Contact support</button>
                  )}
                </div>
              </div>

              </div>
          </div>
        )}
        {!trackingResult && !trackingError && (
          <div className="mt-6 border-t pt-4 text-sm text-gray-600">
            <p>Track using the order ID from your SMS or email receipt. Use the exact display ID format your store is configured to issue.</p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-gray-500">
              <li>Example display ID: {trackingExampleId || '83425190'}</li>
              <li>Format: {trackingFormatDescription || '8 digits'}</li>
              {supportEmailHref ? (
                <li>Need help? Email <a href={supportEmailHref} className="underline">{supportEmail}</a> with your name and contact.</li>
              ) : (
                <li>Need help? Use the Help page to contact support.</li>
              )}
            </ul>
          </div>
        )}
        {trackingResult && (
          <div className="mt-6 border-t pt-4 space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold">Order Details</h3>
                <p className="text-xs text-gray-500">
                  Placed on {trackingResult.created_at ? new Date(trackingResult.created_at).toLocaleString() : '—'}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Status: {formatOrderStatus(trackingResult.status)}
                </span>
                <button
                  type="button"
                  onClick={() => copyOrderId(trackingResult.display_order_id)}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:border-[var(--accent)] hover:text-gray-900"
                >
                  Copy ID
                </button>
              </div>

              </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><span className="font-medium">Order ID:</span> {trackingResult.display_order_id}</div>
              <div><span className="font-medium">Total crafted:</span> {currency(trackingResult.total)}</div>
              <div>
                <span className="font-medium">Contact:</span>{' '}
                {trackingResult.customer?.phone || trackingResult.customer?.email || '—'}
              </div>
              <div><span className="font-medium">Payment:</span> {trackingResult.payment?.method === 'cod' ? 'Cash on Delivery' : trackingResult.payment?.method?.toUpperCase() || '—'}</div>

              </div>

            <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
              <div className="font-medium text-gray-900">Shipping</div>
              <p className="mt-1">
                {trackingResult.shipping
                  ? [trackingResult.shipping.house, trackingResult.shipping.city].filter(Boolean).join(', ')
                  : 'Address not available'}
              </p>
              {trackingResult.shipping?.option && (
                <p className="mt-1 text-xs text-gray-500">Method: {trackingResult.shipping.option === 'outside' ? 'Outside Dhaka delivery' : 'Inside Dhaka delivery'}</p>
              )}
              {trackingResult.pathao_url && (
                <p className="mt-2 text-xs">
                  <span className="font-medium">Delivery Tracking:</span>{' '}
                  <a href={trackingResult.pathao_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Open Pathao tracking</a>
                </p>
              )}

              </div>

            {trackingResult.items?.length ? (
              <div>
                <div className="font-medium text-sm text-gray-900">Items</div>
                <ul className="mt-2 space-y-1 text-sm text-gray-700">
                  {trackingResult.items.map((item, idx) => (
                    <li key={`${item.id || item.title}-${idx}`} className="flex justify-between gap-3">
                      <span>
                        <span>{item.title}</span>
                        {item.variantLabel && <span className="block text-xs text-gray-500">{formatSelectedOptionLabel(item.variantLabel)}</span>}
                        {item.variantSku && <span className="block text-xs text-gray-500">SKU: {item.variantSku}</span>}
                      </span>
                      <span className="text-right">
                        <span className="block text-gray-500">×{item.qty}</span>
                        <PriceDisplay
                          price={(Number(item.price) || 0) * Math.max(1, Number(item.qty) || 1)}
                          originalPrice={Number(item.originalPrice) > Number(item.price) ? Number(item.originalPrice) * Math.max(1, Number(item.qty) || 1) : null}
                          align="right"
                          originalClassName="text-[11px] text-gray-400 line-through"
                          currentClassName="text-xs font-semibold text-gray-700"
                        />
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-lg bg-gray-50 p-4 space-y-2 text-sm text-gray-700">
                  <div className="flex items-center justify-between">
                    <span>Subtotal</span>
                    <span className="font-medium">{currency(trackingOriginalSubtotal)}</span>
                  </div>
                  {trackingProductDiscount > 0 && (
                    <div className="flex items-center justify-between text-green-700">
                      <span>Product discount</span>
                      <span className="font-medium">-{currency(trackingProductDiscount)}</span>
                    </div>
                  )}
                  {Number(trackingResult.discount_amount || 0) > 0 && (
                    <div className="flex items-center justify-between text-green-700">
                      <span>Promo code discount{trackingResult.promo_code ? ` (${trackingResult.promo_code})` : ''}</span>
                      <span className="font-medium">-{currency(trackingResult.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span>Shipping</span>
                    <span className="font-medium">{currency(trackingResult.shipping_cost || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-2 font-semibold text-gray-900">
                    <span>Total</span>
                    <span>{currency(trackingResult.total)}</span>
                  </div>
                </div>
              </div>
            ) : null}

            {trackingTimeline && (
              <div>
                <div className="font-medium text-sm text-gray-900">Delivery progress</div>
                <ol className="mt-3 space-y-3">
                  {trackingTimeline.steps.map((step, idx) => {
                    const reached = idx <= trackingTimeline.currentIndex;
                    const isCurrent = idx === trackingTimeline.currentIndex;
                    return (
                      <li key={step.id} className="relative pl-6 text-sm text-gray-700">
                        <span
                          className={`absolute left-0 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border ${reached ? 'border-[var(--accent)] bg-[var(--accent)]' : 'border-gray-300 bg-white'}`}
                        />
                        {idx < trackingTimeline.steps.length - 1 && (
                          <span
                            className={`absolute left-1.5 top-4 h-[calc(100%-16px)] w-px ${idx < trackingTimeline.currentIndex ? 'bg-[rgba(var(--accent-rgb),0.35)]' : 'bg-gray-200'}`}
                          />
                        )}
                        <div className={`font-semibold ${reached ? 'text-gray-900' : 'text-gray-500'}`}>
                          {step.label}
                          {isCurrent && !trackingTimeline.isCancelled && <span className="ml-2 text-xs font-normal text-[var(--accent)]">(current)</span>}
                        </div>
                        <p className={`mt-1 text-xs ${reached ? 'text-gray-600' : 'text-gray-400'}`}>{step.description}</p>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </div>
        )}
      </section>
        )}
        {page === 'help' && (
          <section className="max-w-5xl mx-auto px-4 pt-6 sm:pt-8 pb-10">
            <div className="text-center">
              <h2 className="text-3xl font-semibold text-gray-900">Contact the Smithy</h2>
              <p className="mt-2 text-sm text-gray-600">Questions about an order or a product? Reach the ItemSmith team for delivery, returns, and custom requests.</p>

              </div>
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-3">
                {helpFaqs.map(faq => {
                  const isOpen = openHelpFaqs.includes(faq.id);
                  return (
                    <div key={faq.id} className="rounded-2xl sm:rounded-none border border-gray-200 bg-white shadow-sm">
                      <button
                        type="button"
                        onClick={() => toggleHelpFaq(faq.id)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left"
                      >
                        <span className="text-sm font-semibold text-gray-900">{faq.question}</span>
                        <svg className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
                        </svg>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-5 text-sm leading-relaxed text-gray-600">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <form onSubmit={handleHelpFormSubmit} className="rounded-2xl sm:rounded-none border border-gray-200 bg-white shadow-sm p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Send a message to the forge</h3>
                  <p className="mt-1 text-sm text-gray-500">Share as much detail as you can about your order or request and we’ll get back to you as soon as possible.</p>
                </div>

                <>
                  {/*
                   * Removed mobile product actions from help form.
                   * When viewing the help page, `selected` is null and product-specific controls are not needed.
                   */}
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Name</label>
                    <input
                      type="text"
                      value={helpForm.name}
                      onChange={(e) => handleHelpFormChange('name', e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Best contact (Instagram @, Facebook URL, or phone)</label>
                    <input
                      type="text"
                      value={helpForm.contact || ''}
                      onChange={(e) => handleHelpFormChange('contact', e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                      placeholder="@yourhandle or https://facebook.com/you or +880..."
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Order ID</label>
                      <input
                        type="text"
                        value={helpForm.orderId}
                        onChange={(e) => handleHelpFormChange('orderId', e.target.value.toUpperCase())}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                        placeholder={`e.g. ${trackingExampleId || '83425190'}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Topic</label>
                      <select
                        value={helpForm.topic}
                        onChange={(e) => handleHelpFormChange('topic', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                      >
                        <option value="">Select</option>
                        <option value="delivery">Delivery &amp; tracking</option>
                        <option value="returns">Exchange or return</option>
                        <option value="payments">Payment question</option>
                        <option value="product">Product inquiry</option>
                        <option value="other">Something else</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Message</label>
                    <textarea
                      rows={4}
                      value={helpForm.message}
                      onChange={(e) => handleHelpFormChange('message', e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                      placeholder="Share how we can help"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmittingHelp}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full sm:rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {isSubmittingHelp && <Icon.Loading className="w-4 h-4" />}
                    {isSubmittingHelp ? 'Sending...' : 'Send message'}
                  </button>
                  <p className="text-xs text-gray-400">We respond Monday-Saturday, 10:00 AM - 7:00 PM. For urgent questions call or DM us on Instagram.</p>
                </>
              </form>

              </div>
          </section>
        )}
        {page === 'returns' && (
          <section className="max-w-5xl mx-auto px-4 sm:px-4 lg:px-8 pt-6 sm:pt-8 pb-12">
            <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl space-y-6">
              <h1 className="text-3xl font-semibold text-gray-900">Returns & Exchanges</h1>
              <p className="text-gray-700">Request an exchange or return within 3 days of delivery. Provide your Order ID and we will follow up by email with the next steps.</p>
              <form onSubmit={async (e)=>{e.preventDefault(); const fd=new FormData(e.currentTarget); const payload={ name: (fd.get('name')||'').toString(), contact:(fd.get('contact')||'').toString(), orderId:(fd.get('orderId')||'').toString(), topic:'returns', message:(fd.get('reason')||'').toString(), hp:(fd.get('hp')||'').toString() }; try{ const res=await fetch(`${API_BASE}/help`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)}); if(res.ok){ push('Request submitted'); e.currentTarget.reset(); } else { push('Could not submit request','error'); } }catch{ push('Could not submit request','error'); } }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-600">Your name *</label>
                  <input name="name" required className="mt-1 w-full rounded-md border-gray-200 px-3 py-2" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Best contact (Instagram @, Facebook URL, or phone) *</label>
                  <input name="contact" required className="mt-1 w-full rounded-md border-gray-200 px-3 py-2" placeholder="@yourhandle or https://facebook.com/you or +880..." />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-600">Order ID *</label>
                  <input name="orderId" required className="mt-1 w-full rounded-md border-gray-200 px-3 py-2" placeholder={`e.g. ${trackingExampleId || '83425190'}`} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-600">Reason</label>
                  <select name="reason" className="mt-1 w-full rounded-md border-gray-200 px-3 py-2">
                    <option>Exchange for another color</option>
                    <option>Exchange for another product</option>
                    <option>Return for store credit</option>
                    <option>Other (write details)</option>
                  </select>
                </div>
                <input name="hp" className="hidden" autoComplete="off" />
                <div className="sm:col-span-2">
                  <button type="submit" className="px-4 py-2 rounded-full bg-[var(--accent)] text-gray-900 font-semibold">Submit request</button>
                </div>
              </form>
            </div>
          </section>
        )}
        {isMaintenance && (
          <div className="fixed inset-0 z-[1000] bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center">
            <h1 className="text-3xl font-semibold text-gray-900">We’ll be right back</h1>
            <p className="mt-2 max-w-xl text-gray-600">Our site is currently under maintenance. Please check back shortly. If you need urgent assistance, contact us on Instagram or Facebook.</p>
          </div>
        )}

        {page === 'catalog' && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-10 sm:pb-12">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-semibold text-gray-900">Collections</h2>
              <div className="text-right">
                <div className="text-sm text-gray-600">Showing {catalog.length} of {products.length}</div>
                {(products.length - catalog.length) > 0 && (
                  <div className="text-xs text-gray-400">{products.length - catalog.length} filtered by current filters</div>
                )}
              </div>
            </div>
            
            {isLoadingProducts ? (
              <LoadingSpinner />
            ) : (
              <>
                <div ref={catalogGridRef} className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,250px)_minmax(0,1fr)] gap-8 relative">
                  <aside ref={filtersAsideRef} className="hidden lg:block self-start relative">
                    {filtersFixed && (
                      <div style={{ height: filtersPanelHeight }} aria-hidden="true" />
                    )}
                    <div
                      ref={filtersPanelRef}
                      className="rounded-[32px] border border-gray-100 bg-white/95 shadow-2xl will-change-transform"
                      style={filtersFixed ? {
                        position: 'fixed',
                        top: stickyPriceOffset,
                        left: filtersBox.left,
                        width: filtersBox.width,
                        zIndex: 30,
                        maxHeight: `calc(100vh - ${stickyPriceOffset}px)`,
                        overflow: 'auto',
                        padding: '1.25rem',
                        transform: filtersTranslate ? `translateY(${-filtersTranslate}px)` : 'translateY(0)',
                        transition: 'transform 160ms ease-out'
                      } : { padding: '1.25rem' }}
                    >
                      <div className="space-y-5">
                        {renderCatalogFilters('desktop')}
                      </div>
                    </div>
                  </aside>

                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {catalog.map(p => {
                      const cardImg = p.img || (p.images ? (Array.isArray(p.images) ? p.images[0] : (JSON.parse(p.images)[0] || '')) : '');
                      return (
                        <article key={p.id} className="rounded-2xl sm:rounded-none border border-gray-100 bg-white/95 overflow-hidden shadow-sm flex flex-col transition-transform sm:hover:-translate-y-1">
                          <div className="relative cursor-pointer group" onClick={() => goto('product', p, false, cardImg)}>
                            <PVImage onError={handleImgError} src={cardImg} alt={p.title} className="w-full aspect-square object-cover" imgProps={{ variant: 'thumb' }} />
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelected(p); setQuickLook(p); setQuickLookOpen(true); }}
                              className="absolute -top-2 -left-1 hidden sm:inline-flex items-center justify-center h-8 w-8 text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)] group-hover:flex"
                              aria-label="Quick view"
                              title="Quick view"
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1.5 12s3.8-6.5 10.5-6.5S22.5 12 22.5 12 18.7 18.5 12 18.5 1.5 12 1.5 12Z" />
                                <circle cx="12" cy="12" r="3.5" />
                              </svg>
                            </button>
                          {(p.displayStock ?? p.stock ?? 0) <= 5 && (p.displayStock ?? p.stock ?? 0) > 0 && (
                            <div className="absolute top-3 right-3">
                              <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                                Only {p.displayStock ?? p.stock} left
                              </span>
                            </div>
                          )}
                          {(p.displayStock ?? p.stock ?? 0) === 0 && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                Out of Stock
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-3 sm:p-4 flex flex-col flex-1">
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0">
                              <h3 className="text-lg sm:text-base font-semibold text-gray-900 truncate">{p.title}</h3>
                              <div className="text-sm sm:text-xs text-gray-500">
                                  {p.category}
                                  {p.color ? ` / ${p.color}` : ''}
                                </div>
                              </div>
                            <PriceDisplay
                              price={p.displayPrice ?? p.price}
                              originalPrice={p.displayOriginalPrice ?? p.originalPrice}
                              showFrom={!!p.hasVariants}
                              align="right"
                              originalClassName="text-xs text-gray-400 line-through"
                              currentClassName="text-2xl sm:text-lg font-bold text-[var(--accent)]"
                            />
                          </div>
                          <div className="mt-2 flex items-stretch gap-2">
                            <button 
                              onClick={() => buyNow(p, 1)} 
                              disabled={(p.displayStock ?? p.stock ?? 0) === 0}
                              className="flex-1 px-2 py-2 sm:py-1.5 bg-[var(--accent)] text-gray-900 text-base sm:text-xs font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {(p.displayStock ?? p.stock ?? 0) === 0 ? 'Unavailable' : 'Buy'}
                            </button>
                            <button 
                              onClick={() => addToCart(p)} 
                              disabled={(p.displayStock ?? p.stock ?? 0) === 0}
                              aria-label="Add to cart"
                              className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 h-10 sm:h-9 sm:w-9 border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Icon.Cart width={14} height={14} />
                              <span className="sm:hidden text-base font-semibold">Cart</span>
                            </button>
                          </div>
                        </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              </div>

              </>
            )}
            {mobileFiltersOpen && (
              <>
                <div
                  className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm sm:hidden"
                  onClick={() => setMobileFiltersOpen(false)}
                />
                <div className="fixed inset-x-4 bottom-24 z-50 sm:hidden" id="mobile-catalog-filters">
                  <div
                    className="rounded-[28px] border border-gray-100 bg-white/95 p-4 shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold text-gray-900">Refine results</h3>
                      <button
                        type="button"
                        onClick={() => setMobileFiltersOpen(false)}
                        className="inline-flex h-8 w-8 items-center justify-center text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                        aria-label="Close filters"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto pr-1">
                      {renderCatalogFilters('mobile')}
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        )}
        {page === 'product' && selected && (
          <section className="relative max-w-6xl mx-auto px-4 sm:px-4 lg:px-8 pt-6 sm:pt-8 pb-10 sm:pb-12 overflow-visible">
            <div ref={productGridRef} className="grid items-start gap-10 overflow-visible lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)]">
              <div className="space-y-6">
                <div
                  ref={imageContainerRef}
                  className="group relative overflow-hidden rounded-[32px] border border-gray-100 bg-white shadow-xl"
                >
                  {(() => {
                    const heroSrc = productShowLayerA ? productLayerASrc : productLayerBSrc;
                    const otherSrc = productShowLayerA ? productLayerBSrc : productLayerASrc;
                    const layerAKind = getMediaKind(productLayerASrc);
                    const layerBKind = getMediaKind(productLayerBSrc);
                    const layerAIsImage = layerAKind === 'image';
                    const layerBIsImage = layerBKind === 'image';
                    const heroKind = getMediaKind(heroSrc);
                    const heroIsImage = heroKind === 'image';
                    if (heroSrc && !heroIsImage) {
                      const heroIsModel = heroKind === 'model';
                      return (
                        <div className="relative w-full aspect-square">
                          <PVImage
                            onError={handleImgError}
                            src={heroSrc}
                            alt={selected.title}
                            className="relative z-10 h-full w-full"
                            imgProps={{
                              ...(heroIsModel ? { interactive3d: true } : {}),
                              frameClassName: 'h-full'
                            }}
                          />
                        </div>
                      );
                    }
                    return (heroSrc || otherSrc) ? (
                      <div className="relative w-full aspect-square">
                        {productLayerASrc && (
                          <div
                            className="absolute inset-0 transition-opacity duration-200"
                            style={{ opacity: productShowLayerA ? 1 : 0 }}
                          >
                            {layerAIsImage && !productLayerASquare && (
                              <div
                                className="absolute inset-0"
                                style={{
                                  backgroundImage: `url(${resolveMediaUrl(productLayerASrc)})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  filter: 'blur(24px)',
                                  transform: 'scale(1.08)'
                                }}
                                aria-hidden="true"
                              />
                            )}
                            <PVImage
                              onError={handleImgError}
                              src={productLayerASrc}
                              alt={productShowLayerA ? selected.title : ''}
                              className={`relative z-10 h-full w-full ${layerAIsImage ? 'object-contain cursor-zoom-in' : ''}`}
                              imgProps={{
                                ...(layerAIsImage ? {
                                  onClick: (e) => { e.stopPropagation(); if (productLayerASrc) openMediaPreview(productLayerASrc); }
                                } : {}),
                                frameClassName: 'h-full',
                                ...(layerAKind === 'model' ? { interactive3d: true } : {}),
                                ...(layerAIsImage ? {
                                  onLoad: (e) => {
                                    const img = e.currentTarget;
                                    const square = img.naturalWidth > 0 && img.naturalWidth === img.naturalHeight;
                                    setProductLayerASquare(square);
                                  }
                                } : {})
                              }}
                            />
                          </div>
                        )}
                        {productLayerBSrc && (
                          <div
                            className="absolute inset-0 transition-opacity duration-200"
                            style={{ opacity: productShowLayerA ? 0 : 1 }}
                          >
                            {layerBIsImage && !productLayerBSquare && (
                              <div
                                className="absolute inset-0"
                                style={{
                                  backgroundImage: `url(${resolveMediaUrl(productLayerBSrc)})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  filter: 'blur(24px)',
                                  transform: 'scale(1.08)'
                                }}
                                aria-hidden="true"
                              />
                            )}
                            <PVImage
                              onError={handleImgError}
                              src={productLayerBSrc}
                              alt={!productShowLayerA ? selected.title : ''}
                              className={`relative z-10 h-full w-full ${layerBIsImage ? 'object-contain cursor-zoom-in' : ''}`}
                              imgProps={{
                                ...(layerBIsImage ? {
                                  onClick: (e) => { e.stopPropagation(); if (productLayerBSrc) openMediaPreview(productLayerBSrc); }
                                } : {}),
                                frameClassName: 'h-full',
                                ...(layerBKind === 'model' ? { interactive3d: true } : {}),
                                ...(layerBIsImage ? {
                                  onLoad: (e) => {
                                    const img = e.currentTarget;
                                    const square = img.naturalWidth > 0 && img.naturalWidth === img.naturalHeight;
                                    setProductLayerBSquare(square);
                                  }
                                } : {})
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}
                </div>
                <div>
                  {(() => {
                    try {
                      const fullGallery = detailGallery;
                      return fullGallery && fullGallery.length > 0 ? (
                        <div className="flex gap-3 overflow-x-auto pb-2">
                          {fullGallery.map((entry, index) => {
                            const url = entry.url;
                            const isActive = (currentImage || selectedPurchaseProduct?.img) === url;
                            const isSelectedOption = entry.selectionId
                              ? (entry.selectionId === BASE_PRODUCT_OPTION_ID ? selectedVariantId === BASE_PRODUCT_OPTION_ID : selectedVariantId === entry.selectionId)
                              : false;
                            return (
                              <button
                                key={`${entry.selectionId || 'gallery'}-${index}-${url}`}
                                type="button"
                                onClick={() => {
                                  setProductImage(url, entry.forceSelection ? entry.selectionId : null);
                                }}
                                className={`relative overflow-hidden rounded-lg sm:rounded-none border transition ${(isActive || isSelectedOption) ? 'border-[var(--accent)] ring-2 ring-[var(--accent)]/40 shadow-sm' : 'border-gray-100 hover:border-[var(--accent)]/60'}`}
                                title={entry.label || `Gallery ${index + 1}`}
                              >
                                <PVImage
                                  onError={handleImgError}
                                  src={url}
                                  alt={entry.label || `Gallery ${index + 1}`}
                                  className="h-20 w-20 object-cover"
                                  imgProps={{ variant: 'thumb' }}
                                />
                                {entry.label && (
                                  <span className="absolute inset-x-1 bottom-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white truncate">
                                    {entry.label}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
                {/* Mobile price & actions: shown only on small screens */}
                <div
                  className="lg:hidden sticky z-30 space-y-6 rounded-[32px] border border-gray-100 bg-white/90 p-6 shadow-2xl"
                  style={{ top: stickyPriceTop }}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-gray-500">
                        {selected.category || 'Signature'}
                      </span>
                      <span className={`text-sm font-semibold ${(selectedPurchaseProduct?.stock || 0) === 0 ? 'text-red-500' : (selectedPurchaseProduct?.stock || 0) <= 5 ? 'text-orange-500' : 'text-emerald-600'}`}>
                        {(selectedPurchaseProduct?.stock || 0) === 0 ? 'Sold out' : `${selectedPurchaseProduct?.stock || 0} in studio`}
                      </span>
                    </div>
                    <h2 className="text-3xl font-semibold text-gray-900">{selected.title}</h2>
                    {productOptionChoices.length > 0 && (
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold uppercase tracking-[0.25em] text-gray-500">Option</label>
                        <div className="grid grid-cols-1 gap-2">
                          {productOptionChoices.map((option) => {
                            const isSelected = selectedVariantId === option.id;
                            return (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => setSelectedVariantId((current) => current === option.id ? '' : option.id)}
                                className="w-full rounded-2xl border bg-white px-4 py-3 text-left transition"
                                style={isSelected
                                  ? {
                                      borderColor: 'var(--accent)',
                                      boxShadow: '0 0 0 2px var(--accent)'
                                    }
                                  : undefined
                                }
                                aria-pressed={isSelected}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span
                                        className="inline-flex h-4 w-4 items-center justify-center rounded-full border shrink-0"
                                        style={isSelected
                                          ? {
                                              borderColor: 'var(--accent)'
                                            }
                                          : {
                                              borderColor: '#d1d5db'
                                            }
                                        }
                                      >
                                        <span
                                          className="block h-2 w-2 rounded-full"
                                          style={isSelected
                                            ? {
                                                backgroundColor: 'var(--accent)'
                                              }
                                            : {
                                                backgroundColor: 'transparent'
                                              }
                                          }
                                        />
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">{option.label}</span>
                                    </div>
                                    {option.sku ? (
                                      <div className="mt-1 text-xs text-gray-500">SKU: {option.sku}</div>
                                    ) : null}
                                  </div>
                                  <div className="text-right">
                                    <PriceDisplay
                                      price={option.price}
                                      originalPrice={option.originalPrice}
                                      align="right"
                                      originalClassName="text-[10px] text-gray-400 line-through"
                                      currentClassName="text-sm font-semibold"
                                      currentStyle={{ color: 'var(--accent)' }}
                                    />
                                    <div className={`mt-0.5 text-[11px] font-medium ${option.stock === 0 ? 'text-red-500' : option.stock <= 5 ? 'text-orange-500' : 'text-gray-500'}`}>
                                      {option.stock === 0 ? 'Out of stock' : `${option.stock} in stock`}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {selectedVariant?.sku ? (
                          <div className="text-xs text-gray-500">SKU: {selectedVariant.sku}</div>
                        ) : null}
                      </div>
                    )}
                    <PriceDisplay
                      price={selectedPurchaseProduct?.price ?? selected.displayPrice ?? selected.price}
                      originalPrice={selectedPurchaseProduct?.originalPrice ?? null}
                      currentClassName="text-3xl font-extrabold text-[var(--accent)]"
                      originalClassName="text-sm text-gray-400 line-through"
                    />
                    <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                      {selectedPurchaseProduct?.description || `Structured ${selected.category ? selected.category.toLowerCase() : 'carryall'} finished in ${selected.color ? selected.color.toLowerCase() : 'signature hues'} with room for phones, wallets, and a small notebook.`}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm">
                      <span className="font-semibold">Quantity</span>
                      <div className="flex items-center divide-x divide-gray-200 rounded-full border border-gray-200">
                        <button type="button" onClick={() => setSelectedQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-base font-semibold hover:bg-gray-50">-</button>
                        <span className="px-4 text-sm font-semibold">{selectedQty}</span>
                        <button type="button" onClick={() => setSelectedQty((q) => Math.min(99, Math.min(selectedPurchaseProduct?.stock || 99, q + 1)))} className="px-3 py-2 text-base font-semibold hover:bg-gray-50">+</button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => addToCart(selectedPurchaseProduct, selectedQty)}
                        disabled={(selectedPurchaseProduct?.stock || 0) === 0}
                        className="inline-flex flex-1 min-w-[160px] items-center justify-center gap-2 rounded-full bg-gray-200 px-5 py-3 text-sm font-semibold text-gray-900 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {(selectedPurchaseProduct?.stock || 0) === 0 ? 'Out of stock' : 'Add to cart'}
                      </button>
                      <button
                        onClick={() => buyNow(selectedPurchaseProduct, selectedQty)}
                        disabled={(selectedPurchaseProduct?.stock || 0) === 0}
                        className="inline-flex flex-1 min-w-[160px] items-center justify-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3 text-sm font-semibold text-gray-900 shadow-lg transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {(selectedPurchaseProduct?.stock || 0) === 0 ? 'Unavailable' : 'Buy'}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Customer reviews section */}
                <div className="space-y-6 rounded-2xl sm:rounded-none border border-gray-200 bg-white/90 p-6 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">Customer reviews</h3>
                    <span className="text-xs font-medium text-gray-500">
                      {reviews.length} total
                    </span>
                  </div>

                  {/* Add review form */}
                  <div className="rounded-xl bg-gradient-to-b from-white to-gray-50 p-3 sm:p-4 shadow-sm">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <label className="block text-sm font-semibold text-gray-900">Give your review</label>
                      <button
                        type="button"
                        onClick={handleAddReview}
                        disabled={!reviewForm.text.trim()}
                        className="inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-4 py-1.5 text-xs font-semibold text-gray-900 shadow-md transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Add review
                      </button>
                    </div>

                    <textarea
                      value={reviewForm.text}
                      onChange={(e) => setReviewForm((f) => ({ ...f, text: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                      rows={3}
                      placeholder="Share your experience with this product..."
                    />

                    <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-3">
                      <div className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1 shadow-sm">
                        <span className="text-[11px] font-semibold text-gray-600">Rating</span>
                        <div className="flex items-center gap-0.5" role="radiogroup" aria-label="Rating">
                          {[1, 2, 3, 4, 5].map((n) => {
                            const active = reviewForm.rating >= n;
                            return (
                              <button
                                key={n}
                                type="button"
                                role="radio"
                                aria-checked={reviewForm.rating === n}
                                aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
                                onClick={() => setReviewForm((f) => ({ ...f, rating: n }))}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                              >
                                <svg className={`h-4 w-4 ${active ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              </button>
                            );
                          })}
                        </div>
                        <span className="text-xs font-semibold text-gray-700">{reviewForm.rating}/5</span>
                      </div>

                      <input
                        id="review-media-upload"
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleReviewMediaChange}
                        className="sr-only"
                      />
                      <label
                        htmlFor="review-media-upload"
                        className="inline-flex items-center rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 cursor-pointer"
                      >
                        Choose file
                      </label>
                      <span className="text-xs text-gray-500 truncate max-w-[220px]">
                        {reviewForm.mediaFile?.name || 'No file selected'}
                      </span>
                      {reviewForm.mediaUrl && (
                        <button
                          type="button"
                          onClick={() => setReviewForm((f) => ({ ...f, mediaFile: null, mediaUrl: null, mediaType: '' }))}
                          className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-600 hover:bg-gray-100"
                        >
                          Remove
                        </button>
                      )}

                    </div>

                    {reviewForm.mediaUrl && (
                      <div className="mt-2">
                        {reviewForm.mediaType && reviewForm.mediaType.startsWith('video') ? (
                          <video src={reviewForm.mediaUrl} controls className="h-16 w-24 object-cover rounded-md border border-gray-200 bg-black" />
                        ) : (
                          <img src={reviewForm.mediaUrl} alt="preview" className="h-16 w-24 object-cover rounded-md border border-gray-200" />
                        )}
                      </div>
                    )}
                  </div>

                  {reviewPhotos.length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                      {reviewPhotos.slice(0, 16).map((rev) => {
                        const src = resolveMediaUrl(rev.media_url);
                        return (
                          <button key={rev.id} type="button" className="relative group aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-50" onClick={() => openMediaPreview(rev.media_url)}>
                            <img src={src} alt="Customer photo" loading="lazy" decoding="async" className="h-full w-full object-cover transition group-hover:scale-105" onError={(e)=>{ e.currentTarget.style.display='none'; }} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {reviews.length === 0 ? (
                    <p className="text-sm text-gray-600">No reviews yet. Be the first to review this product!</p>
                  ) : (
                    <>
                    <ul className="space-y-4">
                      {pagedReviews.map((rev) => (
                        <li key={rev.id} className="border rounded-lg p-4">
                          <div className="flex flex-col sm:flex-row gap-4">
                            {rev.media_url && (
                              <div className="h-32 w-full sm:w-40 flex-shrink-0">
                                {(() => {
                                  const mediaSrc = resolveMediaUrl(rev.media_url);
                                  const mediaKind = getMediaKind(rev.media_url);
                                  if (mediaKind === 'image') {
                                    return (
                                      <img
                                        src={mediaSrc}
                                        alt="Customer review photo"
                                        loading="lazy"
                                        decoding="async"
                                        className="h-full w-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition"
                                        onClick={() => openMediaPreview(rev.media_url)}
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                        }}
                                      />
                                    );
                                  }
                                  const label = mediaKind === 'video' ? 'Video review' : (mediaKind === 'model' ? '3D review' : 'Media review');
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => openMediaPreview(rev.media_url)}
                                      className="h-full w-full rounded-lg border border-gray-200 bg-gradient-to-br from-gray-900 to-gray-700 text-white flex items-center justify-center p-2 text-xs font-semibold uppercase tracking-wide"
                                    >
                                      {label}
                                    </button>
                                  );
                                })()}
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-1 mb-2">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <svg 
                                    key={i} 
                                    className={`h-4 w-4 ${i < rev.rating ? 'text-yellow-400' : 'text-gray-300'}`} 
                                    fill="currentColor" 
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                              </div>
                              <p className="text-sm text-gray-800 leading-relaxed">{rev.text}</p>
                              <p className="text-xs text-gray-400 mt-2">
                                {new Date(rev.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {totalReviewPages > 1 && (
                      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setReviewsPage((p) => Math.max(1, p - 1))}
                          disabled={reviewsPage <= 1}
                          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <div className="flex flex-wrap items-center gap-1">
                          {Array.from({ length: totalReviewPages }, (_, idx) => idx + 1).map((pageNo) => (
                            <button
                              key={pageNo}
                              type="button"
                              onClick={() => setReviewsPage(pageNo)}
                              className={`h-8 min-w-8 rounded-md border px-2 text-xs font-semibold ${
                                pageNo === reviewsPage
                                  ? 'border-gray-900 bg-gray-900 text-white'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                              }`}
                              aria-label={`Go to reviews page ${pageNo}`}
                            >
                              {pageNo}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setReviewsPage((p) => Math.min(totalReviewPages, p + 1))}
                          disabled={reviewsPage >= totalReviewPages}
                          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    )}
                    </>
                  )}
                </div>

              </div>
              
              <div
                ref={priceAsideContainerRef}
                className="hidden lg:block self-start"
                style={{
                  position: 'relative',
                  minHeight: livePriceRailHeight || undefined,
                  height: livePriceRailHeight || undefined
                }}
              >
                <div style={priceStickyStyle}>
                <aside ref={priceAsideRef} className="space-y-6 rounded-[32px] border border-gray-100 bg-white/90 p-6 shadow-2xl" style={priceAsideInnerStyle}>
                  <div className="space-y-3">
                    <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-gray-500">
                      {selected.category || 'Signature'}
                    </span>
                    <h2 className="text-3xl font-semibold text-gray-900">{selected.title}</h2>
                    {productOptionChoices.length > 0 && (
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold uppercase tracking-[0.25em] text-gray-500">Option</label>
                        <div className="grid grid-cols-1 gap-2">
                          {productOptionChoices.map((option) => {
                            const isSelected = selectedVariantId === option.id;
                            return (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => setSelectedVariantId((current) => current === option.id ? '' : option.id)}
                                className="w-full rounded-2xl border bg-white px-4 py-3 text-left transition"
                                style={isSelected
                                  ? {
                                      borderColor: 'var(--accent)',
                                      boxShadow: '0 0 0 2px var(--accent)'
                                    }
                                  : undefined
                                }
                                aria-pressed={isSelected}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span
                                        className="inline-flex h-4 w-4 items-center justify-center rounded-full border shrink-0"
                                        style={isSelected
                                          ? {
                                              borderColor: 'var(--accent)'
                                            }
                                          : {
                                              borderColor: '#d1d5db'
                                            }
                                        }
                                      >
                                        <span
                                          className="block h-2 w-2 rounded-full"
                                          style={isSelected
                                            ? {
                                                backgroundColor: 'var(--accent)'
                                              }
                                            : {
                                                backgroundColor: 'transparent'
                                              }
                                          }
                                        />
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">{option.label}</span>
                                    </div>
                                    {option.sku ? (
                                      <div className="mt-0.5 text-xs text-gray-500">SKU: {option.sku}</div>
                                    ) : null}
                                  </div>
                                  <div className="text-right">
                                    <PriceDisplay
                                      price={option.price}
                                      originalPrice={option.originalPrice}
                                      align="right"
                                      originalClassName="text-[10px] text-gray-400 line-through"
                                      currentClassName="text-sm font-semibold"
                                      currentStyle={{ color: 'var(--accent)' }}
                                    />
                                    <div className={`mt-0.5 text-[11px] font-medium ${option.stock === 0 ? 'text-red-500' : option.stock <= 5 ? 'text-orange-500' : 'text-gray-500'}`}>
                                      {option.stock === 0 ? 'Out of stock' : `${option.stock} in stock`}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {selectedVariant?.sku ? (
                          <div className="text-xs text-gray-500">SKU: {selectedVariant.sku}</div>
                        ) : null}
                      </div>
                    )}
                    <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                      {selectedPurchaseProduct?.description || `Structured ${selected.category ? selected.category.toLowerCase() : 'carryall'} finished in ${selected.color ? selected.color.toLowerCase() : 'signature hues'} with room for phones, wallets, and a small notebook.`}
                    </p>
                    <div className="flex items-center justify-between">
                      <PriceDisplay
                        price={selectedPurchaseProduct?.price ?? selected.displayPrice ?? selected.price}
                        originalPrice={selectedPurchaseProduct?.originalPrice ?? null}
                        currentClassName="text-3xl font-extrabold text-[var(--accent)]"
                        originalClassName="text-sm text-gray-400 line-through"
                      />
                      <span className={`text-sm font-semibold ${(selectedPurchaseProduct?.stock || 0) === 0 ? 'text-red-500' : (selectedPurchaseProduct?.stock || 0) <= 5 ? 'text-orange-500' : 'text-emerald-600'}`}>
                        {(selectedPurchaseProduct?.stock || 0) === 0 ? 'Sold out' : `${selectedPurchaseProduct?.stock || 0} in studio`}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm">
                      <span className="font-semibold">Quantity</span>
                      <div className="flex items-center divide-x divide-gray-200 rounded-full border border-gray-200">
                        <button type="button" onClick={() => setSelectedQty(q => Math.max(1, q - 1))} className="px-3 py-2 text-base font-semibold hover:bg-gray-50">-</button>
                        <span className="px-4 text-sm font-semibold">{selectedQty}</span>
                        <button type="button" onClick={() => setSelectedQty(q => Math.min(99, Math.min(selectedPurchaseProduct?.stock || 99, q + 1)))} className="px-3 py-2 text-base font-semibold hover:bg-gray-50">+</button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => addToCart(selectedPurchaseProduct, selectedQty)}
                        disabled={(selectedPurchaseProduct?.stock || 0) === 0}
                        className="inline-flex flex-1 min-w-[160px] items-center justify-center gap-2 rounded-full bg-gray-200 px-5 py-3 text-sm font-semibold text-gray-900 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {(selectedPurchaseProduct?.stock || 0) === 0 ? 'Out of stock' : 'Add to cart'}
                      </button>
                      <button
                        onClick={() => buyNow(selectedPurchaseProduct, selectedQty)}
                        disabled={(selectedPurchaseProduct?.stock || 0) === 0}
                        className="inline-flex flex-1 min-w-[160px] items-center justify-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3 text-sm font-semibold text-gray-900 shadow-lg transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {(selectedPurchaseProduct?.stock || 0) === 0 ? 'Unavailable' : 'Buy'}
                      </button>
                    </div>
                  </div>
                </aside>
                </div>
              </div>
            </div>
          </section>
        )}
        {page === 'product' && selected && (
          <section className="max-w-6xl mx-auto px-4 sm:px-4 lg:px-8 py-8">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xl font-semibold text-gray-900">You may also like</h3>
              <button className="text-sm text-[var(--accent)] hover:underline" onClick={()=>goto('catalog')}>Browse all</button>
            </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {relatedProducts
                  .slice(0, 8)
                  .map((p, idx) => {
                    const cardImg = p.img || (p.images ? (Array.isArray(p.images) ? p.images[0] : (JSON.parse(p.images)[0]||'')) : '');
                    return (
                      <article
                        key={p.id}
                        className="rounded-2xl sm:rounded-none border border-gray-100 bg-white/95 overflow-hidden shadow-sm cursor-pointer"
                        onClick={()=>goto('product', p, false, cardImg)}
                        style={isLiteMode ? undefined : { opacity: 0, transform: 'translateY(8px)', animation: 'shopstarter-fade-up 0.4s ease forwards', animationDelay: `${idx*0.04}s` }}
                      >
                        <PVImage onError={handleImgError} src={cardImg} alt={p.title} className="w-full aspect-square object-cover" imgProps={{ variant: 'thumb' }} />
                        <div className="p-3">
                          <div className="text-sm font-semibold text-gray-900 truncate">{p.title}</div>
                          <div className="text-xs text-gray-500 truncate">{p.category}{p.color? ` / ${p.color}`: ''}</div>
                          <PriceDisplay
                            price={p.displayPrice ?? p.price}
                            originalPrice={p.displayOriginalPrice ?? p.originalPrice}
                            showFrom={!!p.hasVariants}
                            wrapperClassName="mt-1"
                            originalClassName="text-[10px] text-gray-400 line-through"
                            currentClassName="text-sm font-bold text-[var(--accent)]"
                          />
                        </div>
                      </article>
                    );
                  })}
            </div>
          </section>
        )}


{page === 'cart' && (
          <section className="max-w-6xl mx-auto px-4 sm:px-4 lg:px-8 pt-6 sm:pt-8 pb-10 sm:pb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Your cart</h2>
            <div className="mt-2">
              <button type="button" onClick={() => goto('catalog')} className="text-sm text-[var(--accent)] hover:underline">← Continue shopping</button>
            </div>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)] gap-8">
              <div className="space-y-4">
                {cart.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 bg-white/70 rounded-[32px] border border-gray-100">Your cart is empty. Add something you like to continue.</div>
                ) : (
                  <ul className="space-y-4">
                    {cart.map(item => (
                      <li key={item.id} className="flex items-center gap-4 rounded-2xl sm:rounded-none border border-gray-100 bg-white/90 p-4 shadow-sm">
                        <PVImage
                          onError={handleImgError}
                          src={item.img}
                          alt={item.displayTitle || item.title}
                          className="w-24 h-24 object-cover rounded-xl sm:rounded-none cursor-pointer"
                          imgProps={{
                            variant: 'thumb',
                            onClick: () => {
                              const prod = (products || []).find(p => p && p.id === item.productId) || item;
                              setSelectedVariantId(item.variantId || (prod?.hasVariants ? BASE_PRODUCT_OPTION_ID : ''));
                              goto('product', prod, false, item.img);
                            }
                          }}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 truncate">{item.displayTitle || item.title}</div>
                              {item.variantLabel && <div className="text-[11px] text-gray-500 mt-0.5">{formatSelectedOptionLabel(item.variantLabel)}</div>}
                              {item.variantSku && <div className="text-[11px] text-gray-500 mt-0.5">SKU: {item.variantSku}</div>}
                            </div>
                            <PriceDisplay
                              price={item.price * item.qty}
                              originalPrice={Number(item.originalPrice) > Number(item.price) ? Number(item.originalPrice) * Number(item.qty) : null}
                              align="right"
                              originalClassName="text-[11px] text-gray-400 line-through"
                              currentClassName="text-sm font-semibold text-gray-700 whitespace-nowrap"
                            />
                          </div>
                          <div className="mt-3 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Qty</span>
                              <div className="flex items-center divide-x divide-gray-200 rounded-full border border-gray-200 bg-white">
                                <button
                                  type="button"
                                  onClick={() => updateQty(item.id, Math.max(1, item.qty - 1))}
                                  className="px-3 py-1 text-base font-semibold hover:bg-gray-50"
                                >
                                  -
                                </button>
                                <span className="px-4 text-sm font-semibold min-w-[24px] text-center">{item.qty}</span>
                                <button
                                  type="button"
                                  onClick={() => updateQty(item.id, Math.min(99, item.qty + 1))}
                                  className="px-3 py-1 text-base font-semibold hover:bg-gray-50"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                            <button 
                              onClick={() => removeFromCart(item.id)} 
                              className="p-2 rounded-md text-red-500 hover:bg-red-50"
                              aria-label="Remove item"
                            >
                              <Icon.Trash width={18} height={18} />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <aside className="rounded-[32px] border border-gray-100 bg-white/90 p-6 shadow-2xl sticky top-24 h-max space-y-4">
                {typeof freeShipThreshold === 'number' && !cartFreeDelivery && (
                  <div className="mb-1 rounded-2xl sm:rounded-none border border-gray-200 bg-white p-3">
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>Free delivery at {currency(freeShipThreshold)}</span>
                      <span>{toFreeShip === 0 ? 'Eligible' : `${currency(toFreeShip)} to go`}</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-[var(--accent)]" style={{ width: `${Math.min(100, Math.round((subtotal / freeShipThreshold) * 100))}%` }} />
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">Subtotal</div>
                  <div className="font-semibold">{currency(subtotal)}</div>
                </div>
                <div className="text-sm text-gray-500">
                  Shipping calculated at checkout.
                  {cartFreeDelivery ? (
                    <span className="text-green-600 font-semibold ml-2">Free delivery applied!</span>
                  ) : cartDeliveryDiscount > 0 ? (
                    <span className="text-blue-600 font-semibold ml-2">Delivery discount: -{currency(cartDeliveryDiscount)}</span>
                  ) : null}
                </div>
                <div className="border-t pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Total</div>
                    <div className="text-lg font-bold text-[var(--accent)]">
                      {currency(subtotal)}
                    </div>
                  </div>
                  <button 
                    onClick={() => goto('checkout')} 
                    disabled={cart.length === 0} 
                    className="w-full px-4 py-3 rounded-full bg-[var(--accent)] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md transition"
                  >
                    Checkout
                  </button>
                </div>
              </aside>
            </div>
          </section>
        )}

        {page === 'checkout' && checkoutItems.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 sm:px-4 lg:px-8 pt-6 sm:pt-8 pb-10 sm:pb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Checkout</h2>
            <form
              ref={(node) => {
                checkoutFormRef.current = node;
                checkoutGridRef.current = node;
              }}
              onSubmit={placeOrder}
              onKeyDown={handleCheckoutFormKeyDown}
              className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,0.8fr)] gap-8"
            >
              <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-600">First name *</label>
                    <input 
                      required 
                      value={checkoutState.firstName} 
                      onChange={e => setCheckoutState(s => ({ ...s, firstName: e.target.value }))} 
                      className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                      aria-invalid={checkoutState.firstName.trim().length === 0}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Last name (optional)</label>
                    <input 
                      value={checkoutState.lastName} 
                      onChange={e => setCheckoutState(s => ({ ...s, lastName: e.target.value }))} 
                      className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-xs text-gray-600">Country / Region</label>
                  <input 
                    value={checkoutState.country} 
                    readOnly 
                    className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 bg-gray-50" 
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-600">House number and street name *</label>
                    <input 
                      required 
                      value={checkoutState.house} 
                      onChange={e => setCheckoutState(s => ({ ...s, house: e.target.value }))} 
                      className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                      placeholder="House/Flat, Street" 
                      aria-invalid={checkoutState.house.trim().length === 0}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Town / City *</label>
                    <input 
                      required 
                      list="bd-cities"
                      value={checkoutState.city} 
                      onChange={e => setCheckoutState(s => ({ ...s, city: e.target.value }))} 
                      className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                      placeholder="Start typing e.g. Dhaka, Sylhet, Cox's Bazar"
                      aria-invalid={checkoutState.city.trim().length === 0}
                    />
                    <datalist id="bd-cities">
                      {BD_CITIES.map(c => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                  </div>
                </div>

                {(checkoutState.house && checkoutState.city) ? (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-1">Verify address on map</div>
                    <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl sm:rounded-none border border-gray-200 bg-gray-50">
                      <iframe
                        title="Address preview"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps?q=${encodeURIComponent(`${checkoutState.house}, ${checkoutState.city}, Bangladesh`)}&output=embed`}
                        className="h-full w-full"
                      />
                    </div>
                  </div>
                ) : null}

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-600">Phone *</label>
                    <input 
                      required 
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={checkoutState.phone} 
                      onChange={e => setCheckoutState(s => ({ ...s, phone: e.target.value }))} 
                      className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                      aria-invalid={!/^\+?\d{7,15}$/.test(checkoutState.phone.replace(/\s+/g, ''))}
                      placeholder="e.g. +8801XXXXXXXXX" 
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Email address (optional)</label>
                    <input 
                      type="email"
                      value={checkoutState.email} 
                      onChange={e => setCheckoutState(s => ({ ...s, email: e.target.value }))} 
                      className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                      aria-invalid={checkoutState.email.trim().length > 0 && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(checkoutState.email)}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={checkoutState.shipDifferent} 
                      onChange={(e) => setCheckoutState(s => ({ ...s, shipDifferent: e.target.checked }))} 
                    />
                    Ship to a different address?
                  </label>
                </div>

                <div className="mt-4">
                  <label className="text-xs text-gray-600">Order notes (optional)</label>
                  <textarea 
                    value={checkoutState.notes} 
                    onChange={e => setCheckoutState(s => ({ ...s, notes: e.target.value }))} 
                    className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                    placeholder="Notes about your order, e.g. special notes for delivery."
                    rows={3}
                  />
                </div>

                <div className="mt-6">
                  <h3 className="font-semibold">Shipping</h3>
                  <div className="mt-3 text-sm text-gray-600">Choose your delivery area – shipping fee is applied here.</div>
                  <div className="mt-3 flex gap-4">
                    <label className="flex items-center gap-2">
                      <input 
                        type="radio" 
                        name="shipping" 
                        value="inside" 
                        checked={checkoutState.shippingOption === 'inside'} 
                        onChange={(e) => setCheckoutState(s => ({ ...s, shippingOption: e.target.value }))} 
                      />
                      <span className="text-sm">Inside Dhaka – {currency((shippingRates && shippingRates.inside) || 0)}</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input 
                        type="radio" 
                        name="shipping" 
                        value="outside" 
                        checked={checkoutState.shippingOption === 'outside'} 
                        onChange={(e) => setCheckoutState(s => ({ ...s, shippingOption: e.target.value }))} 
                      />
                      <span className="text-sm">Outside Dhaka – {currency(shippingRates.outside)}</span>
                    </label>
                  </div>
                </div>

                <div className="mt-6 lg:hidden rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-semibold text-gray-900">Promo code</h3>
                    {appliedCoupon && (
                      <span className="inline-flex items-center gap-2 text-xs font-semibold text-green-700">
                        <span>{appliedCoupon.code} applied</span>
                        <button
                          type="button"
                          onClick={clearAppliedCoupon}
                          className={promoRemoveButtonClass}
                          aria-label="Remove applied promo code"
                        >
                          <MiniCloseIcon className="block h-3.5 w-3.5 shrink-0" />
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value)}
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      placeholder="e.g. SAVE10"
                    />
                    <button
                      type="button"
                      onClick={applyCouponCode}
                      disabled={couponApplying || !coupon.trim()}
                      className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {couponApplying ? '...' : 'Apply'}
                    </button>
                  </div>
                  {couponError && <div className="mt-2 text-xs text-red-600">{couponError}</div>}
                  {appliedCoupon && (
                    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-green-700">
                      <span>Discount: -{currency(appliedCouponDiscount)}</span>
                      <button
                        type="button"
                        onClick={clearAppliedCoupon}
                        className={promoRemoveButtonClass}
                        aria-label="Remove applied promo code"
                      >
                        <MiniCloseIcon className="block h-3.5 w-3.5 shrink-0" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <h3 className="font-semibold">Payment</h3>
                  <div className="mt-3 flex flex-col gap-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cod"
                        checked={checkoutState.paymentMethod === 'cod'}
                        onChange={(e) => setCheckoutState(s => ({ ...s, paymentMethod: e.target.value }))}
                      />
                      Cash on Delivery
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="bkash"
                        checked={checkoutState.paymentMethod === 'bkash'}
                        onChange={(e) => setCheckoutState(s => ({ ...s, paymentMethod: e.target.value }))}
                      />
                      bKash
                    </label>
                  </div>

                  {checkoutState.paymentMethod === 'bkash' ? (
                    <>
                      <div className="mt-3 rounded-2xl border border-gray-200 bg-white/70 p-4">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                          {settings.bkashQrUrl ? (
                            <img src={resolveMediaUrl(settings.bkashQrUrl)} alt="bKash QR code" className="h-28 w-28 rounded-xl object-contain bg-white border border-gray-200" />
                          ) : null}
                          <div className="min-w-0 flex-1 space-y-2 text-sm text-gray-700">
                            <div className="font-semibold text-gray-900">Pay with bKash</div>
                            <div>Send the exact amount below, then enter your bKash details.</div>
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-1">
                              <div>bKash Personal Number: <strong>{settings.bkashNumber || 'Not configured'}</strong></div>
                              <div>Amount to send: <strong>{currency(checkoutGrandTotal)}</strong></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-600">Your bKash number *</label>
                          <input 
                            required 
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            value={checkoutState.bkashFrom} 
                            onChange={e => setCheckoutState(s => ({ ...s, bkashFrom: e.target.value }))} 
                            className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                            placeholder="01XXXXXXXXX" 
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">bKash Transaction ID *</label>
                          <input 
                            required 
                            value={checkoutState.bkashTxn} 
                            onChange={e => setCheckoutState(s => ({ ...s, bkashTxn: e.target.value }))} 
                            className="mt-2 w-full rounded-lg border border-gray-300/40 ring-1 ring-gray-200/30 p-3 focus:border-gray-400/60 focus:ring-2 focus:ring-gray-300/50" 
                            placeholder="Transaction ID"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="text-xs text-gray-600">Upload payment screenshot (optional)</label>
                        <input type="file" accept="image/*" className="mt-2 block w-full text-sm" onChange={async (e)=>{ try { const file=e.target.files?.[0]; if(!file) return; const fd=new FormData(); fd.append('file', file); const res=await fetch(`${API_BASE}/payment/upload`,{ method:'POST', body: fd }); const data=await res.json(); if(data?.url){ setCheckoutState(s => ({ ...s, bkashScreenshotUrl: data.url })); push('Screenshot uploaded'); } else { push('Upload failed','error'); } } catch { push('Upload failed','error'); } }} />
                        {checkoutState.bkashScreenshotUrl && (
                          <div className="mt-2 flex items-center gap-3">
                            <img src={resolveMediaUrl(checkoutState.bkashScreenshotUrl)} alt="bKash receipt" className="h-20 w-20 object-cover rounded border" />
                            <button type="button" className="text-sm text-red-600" onClick={()=> setCheckoutState(s => ({ ...s, bkashScreenshotUrl: '' }))}>Remove</button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="mt-3 text-sm text-gray-600">
                      Pay in cash upon delivery. Our courier will collect the total amount on arrival.
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={rememberDetails} onChange={(e)=>setRememberDetails(e.target.checked)} />
                    Remember my details on this device
                  </label>
                </div>

                {checkoutError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="text-sm text-red-600">{checkoutError}</div>
                  </div>
                )}

                <div className="mt-6 flex gap-3">
                  <button 
                    type="submit" 
                    disabled={isPlacing || apiLoading} 
                    className="px-6 py-3 rounded-full bg-[var(--accent)] text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isPlacing && <Icon.Loading className="w-4 h-4" />}
                    {isPlacing ? 'Placing order...' : 'Place order'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => goto('cart')} 
                    className="px-6 py-3 rounded-full border border-gray-200 hover:bg-gray-50"
                  >
                    Back to cart
                  </button>
                </div>
              </div>

              <div
                ref={checkoutAsideContainerRef}
                className="hidden lg:block self-start"
                style={{
                  position: 'relative',
                  minHeight: checkoutRailMode === 'static' ? undefined : (liveCheckoutRailHeight || undefined),
                  height: checkoutRailMode === 'static' ? undefined : (liveCheckoutRailHeight || undefined)
                }}
              >
                <div style={checkoutStickyStyle}>
                <aside ref={checkoutAsideRef} className="rounded-[32px] border border-gray-100 bg-white/90 p-6 shadow-2xl space-y-4" style={checkoutAsideInnerStyle}>
                    <h4 className="font-semibold">Your order</h4>
                    {(isPlacing || apiLoading) && (
                      <div className="animate-pulse space-y-3">
                        {Array.from({ length: Math.max(1, Math.min(4, cart.length || 3)) }).map((_, i) => (
                          <div key={i} className="py-3 flex items-center justify-between">
                            <div className="space-y-1">
                              <div className="h-4 w-40 bg-gray-200 rounded" />
                              <div className="h-3 w-20 bg-gray-200 rounded" />
                            </div>
                            <div className="h-4 w-16 bg-gray-200 rounded" />
                          </div>
                        ))}
                        <div className="h-px bg-gray-200 my-2" />
                        <div className="flex items-center justify-between text-sm">
                          <div className="h-3 w-16 bg-gray-200 rounded" />
                          <div className="h-3 w-20 bg-gray-200 rounded" />
                        </div>
                        <div className="flex items-center justify-between text-sm mt-2">
                          <div className="h-3 w-16 bg-gray-200 rounded" />
                          <div className="h-3 w-28 bg-gray-200 rounded" />
                        </div>
                        <div className="mt-4 flex items-center justify-between text-lg font-bold text-[var(--accent)]">
                          <div className="h-4 w-12 bg-gray-200 rounded" />
                          <div className="h-5 w-24 bg-gray-300 rounded" />
                        </div>
                      </div>
                    )}
                    <div className="mt-3 divide-y">
                      {checkoutItems.map(i => (
                        <div key={i.id} className="py-3 flex items-center justify-between">
                          <div>
                          <div className="text-sm">{i.displayTitle || i.title} <span className="text-xs text-gray-500">×{i.qty}</span></div>
                          {i.variantLabel && <div className="text-[11px] text-gray-400">{formatSelectedOptionLabel(i.variantLabel)}</div>}
                          {i.variantSku && <div className="text-[11px] text-gray-400">SKU: {i.variantSku}</div>}
                        </div>
                        <PriceDisplay
                          price={i.price * i.qty}
                          originalPrice={Number(i.originalPrice) > Number(i.price) ? Number(i.originalPrice) * Number(i.qty) : null}
                          align="right"
                          originalClassName="text-[11px] text-gray-400 line-through"
                          currentClassName="font-semibold"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 border-t pt-4">
                    <div className="flex items-center justify-between text-sm">
                      <div>Subtotal</div>
                      <div className="font-semibold">{currency(checkoutSubtotal)}</div>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <div>Promo code</div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <input value={coupon} onChange={(e)=>setCoupon(e.target.value)} className="w-28 border rounded px-2 py-1 text-sm" placeholder="e.g. SAVE10" />
                          <button type="button" onClick={applyCouponCode} disabled={couponApplying||!coupon.trim()} className="px-2 py-1 text-sm rounded bg-gray-100 disabled:opacity-50">{couponApplying?'...':'Apply'}</button>
                        </div>
                        {couponError && <div className="text-xs text-red-600 mt-1">{couponError}</div>}
                      </div>
                    </div>
                    {appliedCoupon && (
                      <div className="flex items-center justify-between text-sm mt-2 text-green-700">
                        <div className="inline-flex items-center gap-2">
                          <span>Discount ({appliedCoupon.code})</span>
                          <button
                            type="button"
                            onClick={clearAppliedCoupon}
                            className={promoRemoveButtonClass}
                            aria-label="Remove applied promo code"
                          >
                            <MiniCloseIcon className="block h-3.5 w-3.5 shrink-0" />
                          </button>
                        </div>
                        <div>-{currency(appliedCouponDiscount)}</div>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm mt-2">
                      <div>Shipping</div>
                      <div>
                        {checkoutState.shippingOption === 'outside' 
                          ? `Outside Dhaka: ${currency(checkoutShippingCost)}` 
                          : `Inside Dhaka: ${currency(checkoutShippingCost)}`
                        }
                      </div>
                    </div>
                    {checkoutBkashCashoutCharge > 0 && (
                      <div className="flex items-center justify-between text-sm mt-2">
                        <div>bKash cashout charge</div>
                        <div>{currency(checkoutBkashCashoutCharge)}</div>
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-between text-lg font-bold text-[var(--accent)]">
                      <div>Total</div>
                      <div>{currency(checkoutGrandTotal)}</div>
                    </div>
                  </div>
                </aside>
                </div>
              </div>
            </form>
          </section>
        )}

        {page === 'thanks' && (
          <section className="text-center py-20 px-4 sm:px-0">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold mt-4">Thank you for your order!</h2>
              <div className="mt-3 p-3 rounded-2xl sm:rounded-lg bg-gray-50 border border-gray-200 text-sm">
                <div className="text-gray-600">Your order ID</div>
                <div className="mt-1 text-lg font-mono font-semibold">
                  {lastOrderDisplayId || (typeof window !== 'undefined' && localStorage.getItem('shopstarter_last_order_id')) || '—'}
                </div>
              </div>
              <p className="mt-4 text-gray-600">
                We received your order and will contact you with confirmation and tracking details once we verify the bKash payment.
              </p>
              <div className="mt-6">
                <button 
                  onClick={() => { try { localStorage.removeItem('shopstarter_last_order_id'); } catch {}; window.location.assign('/'); }} 
                  className="px-6 py-3 rounded-full sm:rounded-lg bg-[var(--accent)] text-white"
                >
                  Continue shopping
                </button>
                <button
                  onClick={() => {
                    try {
                      const last = typeof window !== 'undefined' ? localStorage.getItem('shopstarter_last_order_id') : '';
                      const normalized = sanitizeTrackingInput(last || '');
                      goto('track', null, true, null, {
                        trackingOrderId: normalized,
                        autoTrack: Boolean(normalized)
                      });
                      return;
                    } catch {}
                    goto('track');
                  }}
                  className="ml-3 px-6 py-3 rounded-full sm:rounded-lg border border-gray-200 hover:bg-gray-50"
                >
                  Track your order
                </button>
              </div>

              </div>
          </section>
        )}
      </main>

      {showDiscardBuyNowDialog && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-4 py-6 sm:py-12" role="dialog" aria-modal="true" aria-labelledby="discard-buy-now-title">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDiscardBuyNowDialog(false)} />
          <div className="relative w-full max-w-md rounded-3xl border border-gray-100 bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <h3 id="discard-buy-now-title" className="text-xl font-semibold text-gray-900">Discard this Buy Now order?</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-600">
              This will remove the Buy Now item from checkout and take you back to your cart. Your existing cart items will stay exactly as they are.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={discardBuyNowCheckout}
                className="inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-5 py-3 text-sm font-semibold text-gray-900 shadow-lg transition hover:shadow-xl"
              >
                Discard and go to cart
              </button>
              <button
                type="button"
                onClick={() => setShowDiscardBuyNowDialog(false)}
                className="inline-flex items-center justify-center rounded-full border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Keep this checkout
              </button>
            </div>
          </div>
        </div>
      )}

      {showConsent && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white/95 shadow-2xl">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="text-sm text-gray-700 pr-2">
                We use cookies to improve your experience and show relevant content. See our <button className="underline" onClick={() => goto('cookies')}>Cookies Policy</button>.
              </div>
              <div className="ml-auto flex gap-2">
                <button className="px-3 py-2 rounded-md border border-gray-200" onClick={() => { try { localStorage.setItem('shopstarter_cookie_consent', JSON.stringify({ analytics: false })); } catch {}; setShowConsent(false); }}>Decline</button>
                <button className="px-3 py-2 rounded-md bg-[var(--accent)] text-white" onClick={() => { try { localStorage.setItem('shopstarter_cookie_consent', JSON.stringify({ analytics: true })); } catch {}; setShowConsent(false); }}>Accept</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <div className="fixed right-4 top-20 z-50 flex flex-col gap-2" role="status" aria-live="polite" aria-atomic="true">
        {toasts.map(t => (
          <button
            type="button"
            key={t.id} 
            onClick={() => dismiss(t.id)}
            className={`p-3 rounded-lg shadow-lg text-sm max-w-sm animate-slide-in ${
              t.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-white border border-gray-200'
            } text-left cursor-pointer transition hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]`}
          >
            {t.msg}
          </button>
        ))}
      </div>

      {/* Legal Pages */}
      {/* Legal and informational pages */}
      {(page === 'terms' || page === 'privacy' || page === 'cookies') && (
        <section className="max-w-5xl mx-auto px-4 sm:px-4 lg:px-8 pt-6 sm:pt-8 pb-12">
          {legalPageLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Icon.Loading className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : legalPage ? (
            <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl space-y-6">
              <h1 className="text-3xl font-semibold text-gray-900">{legalPage.title}</h1>
              <div 
                className="prose prose-gray max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: legalPage.content }}
              />
            </div>
          ) : (
            <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl text-center">
              <h1 className="text-3xl font-semibold text-gray-900 mb-4">Content Not Available</h1>
              <p className="text-gray-600">This page content is currently unavailable. Please contact support if this persists.</p>
            </div>
          )}
        </section>
      )}

      {/* Order & Cancellation page (legal content) */}
      {page === 'order' && (
        <section className="max-w-5xl mx-auto px-4 sm:px-4 lg:px-8 pt-6 sm:pt-8 pb-12">
          {legalPageLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Icon.Loading className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : legalPage ? (
            <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl space-y-6">
              <h1 className="text-3xl font-semibold text-gray-900">{legalPage.title}</h1>
              <div className="prose prose-gray max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: legalPage.content }} />
            </div>
          ) : (
            <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl text-center">
              <h1 className="text-3xl font-semibold text-gray-900 mb-4">Content Not Available</h1>
              <p className="text-gray-600">This page content is currently unavailable. Please contact support if this persists.</p>
            </div>
          )}
        </section>
      )}

      {/* Shipping & Delivery page (legal content) */}
      {page === 'shipping' && (
        <section className="max-w-5xl mx-auto px-4 sm:px-4 lg:px-8 pt-6 sm:pt-8 pb-12">
          {legalPageLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Icon.Loading className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : legalPage ? (
            <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl space-y-6">
              <h1 className="text-3xl font-semibold text-gray-900">{legalPage.title}</h1>
              <div className="prose prose-gray max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: legalPage.content }} />
            </div>
          ) : (
            <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl text-center">
              <h1 className="text-3xl font-semibold text-gray-900 mb-4">Content Not Available</h1>
              <p className="text-gray-600">This page content is currently unavailable. Please contact support if this persists.</p>
            </div>
          )}
        </section>
      )}

      {/* Site Map page */}
      {page === 'sitemap' && (
        <section className="max-w-5xl mx-auto px-4 sm:px-4 lg:px-8 pt-6 sm:pt-8 pb-12">
          <div className="rounded-[32px] border border-gray-100 bg-white/90 p-8 shadow-2xl space-y-6">
            <h1 className="text-3xl font-semibold text-gray-900">Site Map</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500">Primary</h2>
                <ul className="mt-3 space-y-2 text-gray-700">
                  <li><button onClick={() => goto('home')} className="hover:underline">Home</button></li>
                  <li><button onClick={() => goto('catalog')} className="hover:underline">Catalog</button></li>
                  <li><button onClick={() => goto('offers')} className="hover:underline">Offers</button></li>
                  <li><button onClick={() => goto('help')} className="hover:underline">Help</button></li>
                  <li><button onClick={() => goto('track')} className="hover:underline">Track Order</button></li>
                </ul>
              </div>
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500">Policies</h2>
                <ul className="mt-3 space-y-2 text-gray-700">
                  <li><button onClick={() => goto('terms')} className="hover:underline">Terms & Conditions</button></li>
                  <li><button onClick={() => goto('privacy')} className="hover:underline">Privacy Policy</button></li>
                  <li><button onClick={() => goto('cookies')} className="hover:underline">Cookies</button></li>
                  <li><button onClick={() => goto('order')} className="hover:underline">Order & Cancellation</button></li>
                  <li><button onClick={() => goto('shipping')} className="hover:underline">Shipping & Delivery</button></li>
                </ul>
              </div>
              <div className="md:col-span-2">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500">Categories</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Array.from(new Set(products.map(p => p.category).filter(Boolean))).map(cat => (
                    <button key={cat} onClick={() => { setFilters(f => ({ ...f, category: cat })); goto('catalog'); }} className="rounded-full border border-gray-200 px-3 py-1 text-xs hover:underline">{cat}</button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500">Products</h2>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-700">
                  {products.slice(0, 50).map(p => (
                    <li key={p.id}>
                      <button onClick={() => { setSelected(p); goto('product', p); }} className="hover:underline">
                        {p.title}
                      </button>
                    </li>
                  ))}
                </ul>
                {products.length > 50 && (
                  <div className="mt-2 text-xs text-gray-500">Showing 50 of {products.length} products</div>
                )}
              </div>
              <div className="md:col-span-2 text-sm text-gray-500">
                XML: <a className="text-[var(--accent)] hover:underline" href={sitemapXmlHref} target="_blank" rel="noopener noreferrer">/sitemap.xml</a>
              </div>
            </div>
          </div>
        </section>
      )}
      {page === 'legal' && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-12">
          {legalPageLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Icon.Loading className="w-8 h-8 text-gray-400 mb-4" />
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : legalPage ? (
            <div className="bg-white rounded-2xl sm:rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-white p-6 sm:p-8 border-b border-gray-200">
                <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900">{legalPage.title}</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Last updated: {new Date(legalPage.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <div 
                className="prose prose-sm sm:prose max-w-none p-6 sm:p-8"
                dangerouslySetInnerHTML={{ __html: legalPage.content }}
              />
            </div>
          ) : (
            <div className="text-center py-16">
              <h1 className="text-3xl font-semibold text-gray-900 mb-2">Page Not Found</h1>
              <p className="text-gray-600 mb-6">The legal page you're looking for doesn't exist.</p>
              <button 
                onClick={() => goto('home')}
                className="px-6 py-3 rounded-full sm:rounded-lg bg-[var(--accent)] text-gray-900 hover:shadow-md transition"
              >
                Go Home
              </button>
            </div>
          )}
        </section>
      )}
      {page === 'notfound' && (
        <main className="max-w-2xl mx-auto px-4 py-8 sm:py-16 text-center">
          <h1 className="text-3xl font-semibold">Page not found</h1>
          <p className="mt-2 text-gray-600">We couldn't find the page you were looking for.</p>
          <button onClick={() => goto('home')} className="mt-6 px-6 py-3 rounded-full sm:rounded-lg bg-[var(--accent)] text-white">Go Home</button>
        </main>
      )}

      {showScrollTop && typeof document !== 'undefined' && createPortal(
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed right-4 sm:right-6 z-[120] inline-flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full border border-black/10 bg-[var(--accent)] text-gray-900 transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--accent)]"
          style={{
            bottom: `calc(${backToTopBottom} + env(safe-area-inset-bottom, 0px))`,
            boxShadow: '0 16px 32px rgba(230,198,93,0.28)'
          }}
          aria-label="Back to top"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.9" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l7-7 7 7" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14" />
          </svg>
        </button>,
        document.body
      )}

      {isPromoModalOpen && selectedPromotion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:py-12" role="dialog" aria-modal="true" aria-labelledby="promo-modal-title">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closePromoModal} />
          <div
            className="relative w-full max-w-md sm:max-w-xl lg:max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden border border-gray-100"
            onClick={(e) => e.stopPropagation()}
            ref={promoModalRef}
          >
            <div className="absolute right-4 top-4">
              <button
                type="button"
                onClick={closePromoModal}
                className="inline-flex h-10 w-10 items-center justify-center text-white mix-blend-difference hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                aria-label="Close promotion details"
                ref={promoCloseRef}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.2" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>

              </div>
            {Boolean(String(selectedPromotion.image_url || selectedPromotion.imageUrl || '').trim()) && (
              <PVImage
                src={resolveMediaUrl(selectedPromotion.image_url || selectedPromotion.imageUrl)}
                alt={selectedPromotion.title || 'Promotion'}
                className="w-full h-auto object-cover"
                imgProps={{ loading: 'eager' }}
              />
            )}
            <div className="px-5 sm:px-8 py-6 sm:py-8 space-y-4">
              <h2 id="promo-modal-title" className="text-2xl sm:text-3xl font-semibold text-gray-900">
                {selectedPromotion.title || 'Offer details'}
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-gray-700 whitespace-pre-wrap">
                {selectedPromotion.offer_details || selectedPromotion.description || 'Offer details will appear here once configured in the admin panel.'}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Hotbar-style Footer */}
      <footer className="bg-white/95 border-t border-gray-200 mt-6 sm:mt-8">
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-3 sm:py-5 flex flex-col gap-4 text-sm text-gray-700">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Brand */}
            <div className="flex items-center gap-3">
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] border border-gray-200 bg-gradient-to-br from-white via-white to-[rgba(var(--accent-rgb),0.10)] shadow-sm relative overflow-hidden">
                <img
                  src={(settingsReady && resolveMediaUrl(settings.logoUrl)) || (settingsReady ? '/logo.png' : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==')}
                  alt="ItemSmith logo"
                  className="h-12 w-12 object-contain"
                />
              </span>
              <div className="flex flex-col">
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">ItemSmith</span>
                <span className="text-sm font-semibold text-gray-900">{settings.siteName || 'ItemSmith'}</span>
              </div>
            </div>
            {/* Socials */}
            <div className="flex items-center gap-3 text-gray-500">
              <a
                href={(settings.facebookUrl && settings.facebookUrl.trim()) ? settings.facebookUrl : '#'}
                target={(settings.facebookUrl && settings.facebookUrl.trim() && settings.facebookUrl.startsWith('http')) ? '_blank' : undefined}
                rel={(settings.facebookUrl && settings.facebookUrl.trim() && settings.facebookUrl.startsWith('http')) ? 'noopener noreferrer' : undefined}
                aria-label="Facebook"
                className="hover:text-sky-600"
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0"/></svg>
              </a>
              <a
                href={(settings.instagramUrl && settings.instagramUrl.trim()) ? settings.instagramUrl : '#'}
                target={(settings.instagramUrl && settings.instagramUrl.trim() && settings.instagramUrl.startsWith('http')) ? '_blank' : undefined}
                rel={(settings.instagramUrl && settings.instagramUrl.trim() && settings.instagramUrl.startsWith('http')) ? 'noopener noreferrer' : undefined}
                aria-label="Instagram"
                className="hover:text-pink-600"
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm10 2a3 3 0 013 3v10a3 3 0 01-3 3H7a3 3 0 01-3-3V7a3 3 0 013-3h10zm-5 3a5 5 0 100 10 5 5 0 000-10zm0 2.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5zm5.75-2.25a1 1 0 100 2 1 1 0 000-2z"/></svg>
              </a>
              {supportEmailHref && (
                <a href={supportEmailHref} aria-label="Email" className="hover:text-emerald-600">
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M12 13.065L.002 6.5V19.5A2.5 2.5 0 0 0 2.5 22h19a2.5 2.5 0 0 0 2.5-2.5V6.5l-12 6.565zm11.998-8.565A2.5 2.5 0 0 0 21.5 2h-19A2.5 2.5 0 0 0 .002 4.5v.637l12 6.565 12-6.565V4.5z"/></svg>
                </a>
              )}
            </div>
          </div>
          {/* Hotbar navigation */}
          <div className="flex flex-col sm:flex-row items-center justify-between text-[11px] text-gray-500 gap-2 mt-2">
            <div className="flex flex-wrap items-center gap-4">
              <span>&copy; {new Date().getFullYear()} {settings.siteName || 'ItemSmith'}</span>
              <button type="button" className="hover:underline" onClick={() => goto('terms')}>Terms</button>
              <button type="button" className="hover:underline" onClick={() => goto('privacy')}>Privacy</button>
              <button type="button" className="hover:underline" onClick={() => goto('cookies')}>Cookies</button>
            </div>
            <div className="text-center sm:text-right">
              <div>Curated by the ItemSmith team.</div>
              <div className="mt-1 text-[10px] text-gray-400">Made by Tj</div>
            </div>
          </div>
        </div>
      </footer>
      {hasMobileBottomBar && (
        <div className="md:hidden h-16" aria-hidden="true" />
      )}
      {/* Sticky mobile CTA: Checkout */}
      {page === 'checkout' && checkoutItems.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-gray-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
            <div className="text-lg">
              <div className="text-gray-600">Total</div>
              <div className="font-bold text-[var(--accent)]">{currency(checkoutGrandTotal)}</div>
            </div>
            <button
              type="button"
              onClick={() => { try { checkoutFormRef.current?.requestSubmit(); } catch {} }}
              disabled={isPlacing || apiLoading}
              className="px-8 py-2 rounded-full bg-[var(--accent)] text-gray-900 font-semibold disabled:opacity-50"
            >
              {isPlacing ? 'Placing order…' : 'Place order'}
            </button>
          </div>
        </div>
      )}

      {/* Floating cart widget for mobile screens */}

      {/* Sticky mobile CTA: Cart */}
      {page === 'cart' && cart.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-gray-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="text-lg">
              <div className="text-gray-600">Total</div>
              <div className="font-bold text-[var(--accent)]">{currency(subtotal)}</div>
            </div>
            <button
              type="button"
              onClick={() => goto('checkout')}
              className="px-8 py-2 rounded-full bg-[var(--accent)] text-gray-900 font-semibold"
            >
              Checkout
            </button>
          </div>
        </div>
      )}

      {/* Floating compare button */}
      {compare.length > 0 && (
        <button
          type="button"
          onClick={() => setCompareOpen(true)}
          className="fixed bottom-36 left-4 z-40 inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 shadow-lg"
          title="Compare items"
        >
          Compare ({compare.length}/2)
        </button>
      )}

      {page === 'catalog' && (
        <button
          type="button"
          onClick={() => setMobileFiltersOpen(v => !v)}
          className={`sm:hidden fixed ${filtersButtonBottomClass} right-4 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-lg`}
          aria-label={mobileFiltersOpen ? 'Close filters and sorting' : 'Open filters and sorting'}
          aria-expanded={mobileFiltersOpen}
          aria-controls="mobile-catalog-filters"
        >
          <svg className="h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M6 12h12M10 18h8" />
          </svg>
          {activeCatalogFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-semibold text-gray-900">
              {activeCatalogFilterCount}
            </span>
          )}
        </button>
      )}

      {page !== 'checkout' && (
        <button
          type="button"
          onClick={() => goto('cart')}
          className={`fixed ${cartButtonBottomClass} right-4 z-40 sm:hidden inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--accent)] text-gray-900 shadow-lg`}
        >
          <Icon.Cart width={24} height={24} />
          {cart.length > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-white px-1 text-[10px] font-semibold text-[var(--accent)]">
              {cart.reduce((s, i) => s + i.qty, 0)}
            </span>
          )}
        </button>
      )}

      <SearchFocusHelper onFocus={() => { const el = document.querySelector('input[placeholder*="Search"]'); el?.focus(); }} />
      {showConfetti && !isLiteMode && (
        <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
          <style>
            {`@keyframes pv-confetti-fall { 0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; } 100% { transform: translateY(110vh) rotate(360deg); opacity: 0; } }`}
          </style>
          {Array.from({ length: 60 }).map((_, i) => {
            const left = Math.random() * 100;
            const size = 6 + Math.random() * 6;
            const hue = Math.floor(Math.random() * 360);
            const delay = Math.random() * 0.6;
            const time = 0.9 + Math.random() * 0.9;
            return (
              <span
                key={i}
                style={{ position: 'absolute', top: '-5vh', left: `${left}%`, width: size, height: size, borderRadius: 1, background: `hsl(${hue} 80% 60%)`, animation: `pv-confetti-fall ${time}s linear ${delay}s forwards` }}
              />
            );
          })}
        </div>
      )}

      {/* Compare modal */}
      {compareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setCompareOpen(false)}
        >
          <div
            className="bg-white rounded-2xl sm:rounded-none shadow-xl w-full max-w-3xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Compare products</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={()=>setCompareOpen(false)}>
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left">Attribute</th>
                    {compare.map(id => {
                      const p = products.find(x => x && x.id === id);
                      return <th key={id} className="px-3 py-2 text-left">{p?.title || '—'}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {['price','color','category','stock'].map(attr => (
                    <tr key={attr} className="border-t">
                      <td className="px-3 py-2 font-medium capitalize">{attr}</td>
                      {compare.map(id => {
                        const p = products.find(x => x && x.id === id);
                        let val = '';
                        if (attr === 'price') val = p ? (
                          <PriceDisplay
                            price={p.displayPrice ?? p.price}
                            originalPrice={p.displayOriginalPrice ?? p.originalPrice}
                            showFrom={!!p.hasVariants}
                            originalClassName="text-[10px] text-gray-400 line-through"
                            currentClassName="text-sm font-semibold text-[var(--accent)]"
                          />
                        ) : '—';
                        else if (attr === 'stock') val = p ? (((p.displayStock ?? p.stock) || 0) > 0 ? `${p.displayStock ?? p.stock} in stock` : 'Sold out') : '—';
                        else val = p?.[attr] || '—';
                        return <td key={id} className="px-3 py-2">{val}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <button className="text-sm text-red-600" onClick={()=> setCompare([])}>Clear compare</button>
              <div className="text-xs text-gray-500">Select up to two items</div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile PDP sticky bar */}
      {page === 'product' && selected && (
        <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-gray-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}>
            <div className="min-w-0">
              <div className="text-base text-gray-500 truncate">{selected.title}</div>
              <PriceDisplay
                price={selectedPurchaseProduct?.price ?? selected.displayPrice ?? selected.price}
                originalPrice={selectedPurchaseProduct?.originalPrice ?? null}
                currentClassName="text-xl font-bold text-[var(--accent)]"
                originalClassName="text-xs text-gray-400 line-through"
              />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => buyNow(selectedPurchaseProduct, 1)} className="px-7 py-2 rounded-full bg-[var(--accent)] text-gray-900 font-semibold">Buy</button>
              <button onClick={() => addToCart(selectedPurchaseProduct)} className="px-7 py-2 rounded-full border border-gray-200 bg-white font-semibold">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick view modal */}
      {quickLookOpen && quickLook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={()=>{ setQuickLookOpen(false); setQuickLook(null); }}>
          <div className="bg-white rounded-2xl sm:rounded-none shadow-xl w-full max-w-2xl p-4 sm:p-6" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">{quickLook.title}</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={()=>{ setQuickLookOpen(false); setQuickLook(null); }}>
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <PVImage
                  src={quickLook.img || (Array.isArray(quickLook.images)? quickLook.images[0]: (quickLook.images? JSON.parse(quickLook.images||'[]')[0]: ''))}
                  alt={quickLook.title}
                  className="w-full h-64 object-cover rounded-xl sm:rounded-none cursor-pointer"
                  onError={handleImgError}
                  imgProps={{
                    variant: 'thumb',
                    onClick: () => {
                      const img = quickLook.img || (Array.isArray(quickLook.images)? quickLook.images[0]: (quickLook.images? JSON.parse(quickLook.images||'[]')[0]: ''));
                      setQuickLookOpen(false);
                      setQuickLook(null);
                      goto('product', quickLook, false, img);
                    }
                  }}
                />
              </div>
              <div className="space-y-3">
                <PriceDisplay
                  price={quickLook.displayPrice ?? quickLook.price}
                  originalPrice={quickLook.displayOriginalPrice ?? quickLook.originalPrice}
                  showFrom={!!quickLook.hasVariants}
                  currentClassName="text-2xl font-bold text-[var(--accent)]"
                  originalClassName="text-sm text-gray-400 line-through"
                />
                <div className="text-sm text-gray-600">{quickLook.category}{quickLook.color? ` / ${quickLook.color}`: ''}</div>
                <div className="text-sm text-gray-500 line-clamp-5">{quickLook.description || 'Handcrafted quality from our studio.'}</div>
                <div className="flex items-center gap-2 pt-2">
                  <button onClick={()=>{ buyNow(quickLook,1); setQuickLookOpen(false); }} className="flex-1 px-3 py-2 rounded-full sm:rounded-lg bg-[var(--accent)] text-gray-900 font-semibold shadow">Buy</button>
                  <button onClick={()=>{ addToCart(quickLook); setQuickLookOpen(false); }} className="px-3 py-2 rounded-full sm:rounded-lg border border-gray-200">Add to cart</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {mediaPreview && (
        <div className="fixed inset-0 z-[60] bg-black/90" onClick={() => setMediaPreview(null)}>
          <button
            type="button"
            className="absolute top-3 right-3 z-20 inline-flex h-10 w-10 items-center justify-center text-white hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            onClick={() => setMediaPreview(null)}
            aria-label="Close preview"
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="3.2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="w-full h-full flex items-center justify-center p-2 sm:p-6 pointer-events-none">
            {getMediaKind(mediaPreview) === 'image' && (
              <img
                src={mediaPreview}
                alt="Preview"
                className="max-w-full max-h-[92vh] object-contain pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {getMediaKind(mediaPreview) === 'video' && (
              <div className="w-full max-w-[1400px] max-h-[92vh] pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                <VideoPlayer
                  src={mediaPreview}
                  className="w-full h-full"
                  frameClassName="w-full h-[80vh] sm:h-[86vh] max-h-[92vh]"
                  controlsMode="minimal"
                  immersive
                />
              </div>
            )}
            {getMediaKind(mediaPreview) === 'model' && (
              String(mediaPreview || '').toLowerCase().match(/\.(stl)(\?.*)?$/) ? (
                <div className="w-full max-w-[1400px] h-[80vh] sm:h-[86vh] max-h-[92vh] pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                  <STLViewer src={mediaPreview} className="h-full" immersive autoLoad />
                </div>
              ) : isModelPreviewable(mediaPreview) ? (
                <div className="w-full max-w-[1400px] h-[80vh] sm:h-[86vh] max-h-[92vh] bg-black overflow-hidden pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                  <ModelViewer src={mediaPreview} interactive autoLoad />
                </div>
              ) : (
                <div className="text-sm text-white/90 pointer-events-auto" onClick={(e) => e.stopPropagation()}>Preview is available for .glb, .gltf, .usdz, or .stl models. For textured models, use .glb (embedded textures).</div>
              )
            )}
            {getMediaKind(mediaPreview) === 'unknown' && (
              <div className="text-sm text-white pointer-events-auto" onClick={(e) => e.stopPropagation()}>Unsupported media type.</div>
            )}
          </div>
        </div>
      )}
  </div>
  </ErrorBoundary>
  );
}

function SearchFocusHelper({ onFocus }) {
  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onFocus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFocus]);
  return null;
}












